/**
 * Per-entity mesh recipes. The live roster is compiled from Blender; the
 * original code recipes remain available for local before/after studies.
 *
 * Every entity in both registries has one, and `roster.test.ts` enumerates the
 * registry and fails on any gap — a unit with no recipe would silently render
 * as nothing.
 *
 * Silhouette remains the first constraint, with closed surfaces and additional
 * equipment detail in the Blender exports. Runtime recipes retain palette roles
 * and rigid parts rather than importing scene materials or lighting.
 *
 * The two factions are separated by shape language as well as by owner colour:
 * Helios is boxy, symmetrical, and horizontal; the Ascendancy is angular,
 * asymmetric, and vertical.
 */

import {
  PART,
  SHADE,
  ORDNANCE_PARTS,
  MeshBuilder,
  RecipeBuilder,
  type MeshRecipe,
} from './meshes.js'

/** The three ordnance part kinds, as the recipes use them. */
const ORDNANCE = ORDNANCE_PARTS
import { ID } from '../sim/data/entities.js'
import { blenderRecipe } from './blender.js'

type Recipe = () => MeshRecipe

/*
 * A note on `topShade`, learned the hard way.
 *
 * The camera is fixed and looks down: for anything wider than it is tall, the
 * top face is most of what is ever seen. Passing the trim colour as `topShade`
 * on a large surface therefore does not add a trim detail — it repaints the
 * whole object. A Wraith Skimmer built that way came out as a flat brass cross
 * with no owner colour anywhere on it.
 *
 * Trim belongs on small parts and on vertical faces. Big horizontal surfaces
 * take owner colour, which is also what keeps ownership readable from above.
 *
 * The same argument applies one step further in, and it took a second round to
 * see it: `OwnerLight` on a big top face is not a highlight either. The light
 * step of an owner ramp is a pale tint — a near-white blue for Helios — so a
 * hull deck painted with it is a white unit with a coloured stripe down each
 * flank, which is exactly what a jury counted on the roster shots. The lighting
 * already gives a top face about 1.4:1 over a flank on its own, so **the largest
 * surfaces of a unit take `Owner` itself**, and `OwnerLight`, `Metal` and
 * `MetalDark` are for turret caps, tracks, barrels and sub-cell trim. Structures
 * are left as they are; the complaint was about units, and it is on units that
 * a lost team read costs a fight.
 *
 * That rule stands, and it is a rule about *top* faces only. Applied to a whole
 * hull it produced the opposite failure: a Helios vehicle was one hue on every
 * face it had, so no part boundary anywhere on the model had a value change
 * across it and the unit flattened into a coloured blob.
 *
 * The split that was tried next — owner colour on horizontal faces, `MetalMid`
 * on vertical ones — solved the flattening by giving the whole vehicle away. A
 * unit is seen at forty-five degrees, so its flanks are as many pixels as its
 * deck, and painting every flank in the mid neutral put a tank at about a third
 * team colour: measured off `sheet-helios`, a Guardian Tank was a grey box with
 * a blue lid and a blue stripe down each side. The reference's tanks are 50–60%
 * team colour across hull *and* turret, and they do not flatten, because what
 * carries the volume there is not a neutral flank — it is the neutral
 * **sub-parts** standing on the coloured body: near-black running gear, a grey
 * engine deck, a grey mantlet and barrel, a dark turret shoulder.
 *
 * So the rule is by part, and it is the one the reference uses: **the largest
 * contiguous body masses — hull, turret, shoulder block — take the owner colour
 * on every face**, and the neutral three-value chassis ramp is demoted to the
 * machinery bolted to them. Lighting alone gives a top face about 1.4:1 over a
 * flank, which is all the value change a single painted mass needs; the value
 * changes that matter are the ones across a part boundary, and those are now
 * carried by grey against colour rather than by colour against colour.
 */

/**
 * The pad a structure stands on.
 *
 * Every building opened with the same three lines — one flat quad in the
 * darkest neutral, seams, studs — and twenty of those on a map read as a decal
 * printed under each building rather than as a foundation. A flat black
 * rectangle has no section for the light to find: no edge, no step, and nothing
 * on it at the scale of the thing standing there. Worse, most structures put
 * their own base course in the same darkest neutral directly on top, so pad and
 * plinth fused into one black slab wider than the building.
 *
 * The replacement keeps the footprint and gives it a profile: a near-black
 * chamfer where it meets the ground — the one part of a pad that belongs in its
 * own shadow — a lit plate one step in from it, and a team-coloured block at
 * each corner. The corners are deliberate: a building covers all but a couple of
 * pixels of its own pad, so the corners are the only part reliably visible, and
 * on a small emplacement they are the only owner colour the model can afford.
 *
 * The other half of that fix is not here: every neutral base course now lights
 * its *top* face, so a building's own podium is a lit deck over dark walls
 * rather than a second black quad stacked on the first. Neither change alone was
 * enough — the pad and the course above it were the same value, and it is the
 * pair of them that a jury measured as one slab.
 */
function foundation(hull: MeshBuilder, w: number, d: number, cols: number, rows: number): void {
  hull.wedge(0, -0.06, 0, w, 0.06, d, 0.05, SHADE.MetalDark)
  hull.box(0, 0, 0, w - 0.1, 0.08, d - 0.1, SHADE.MetalMid, SHADE.Metal)
  hull.seams(0, 0.08, 0, w - 0.1, d - 0.1, cols, rows)
  hull.studs(0, 0.08, 0, w - 0.26, d - 0.26, SHADE.Metal)
  const mx = w / 2 - 0.15
  const mz = d / 2 - 0.15
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      hull.box(sx * mx, 0.08, sz * mz, 0.18, 0.1, 0.18, SHADE.Owner, SHADE.OwnerLight)
    }
  }
}

// ---------------------------------------------------------------------------
// Helios Directorate — units
// ---------------------------------------------------------------------------

/** Box-section armour and exposed running gear, authored at final recipe scale. */
const guardianTank: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  hull.box(0, 0.11, 0, 0.59, 0.13, 0.82, SHADE.OwnerDark, SHADE.Owner)
  hull.box(0, 0.24, -0.015, 0.55, 0.09, 0.72, SHADE.Owner)
  hull.box(0, 0.16, 0.42, 0.52, 0.08, 0.1, SHADE.MetalMid)
  hull.box(0, 0.335, -0.27, 0.32, 0.012, 0.22, SHADE.MetalDark)
  for (let i = 0; i < 4; i++) {
    hull.box(0, 0.345, -0.35 + i * 0.05, 0.3, 0.012, 0.023, SHADE.MetalMid)
  }
  for (const side of [-1, 1]) {
    hull.box(side * 0.32, 0.24, 0, 0.16, 0.06, 0.92, SHADE.Owner)
    hull.box(side * 0.29, 0.305, 0.34, 0.09, 0.04, 0.06, SHADE.Light)
    hull.box(side * 0.3, 0.29, -0.39, 0.1, 0.06, 0.12, SHADE.MetalDark)
    hull.box(side * 0.285, 0.19, 0.12, 0.035, 0.12, 0.11, SHADE.OwnerLight)
  }

  const tracks = r.part(PART.Tread)
  for (const side of [-1, 1]) {
    tracks.box(side * 0.31, 0.025, 0, 0.18, 0.19, 0.88, SHADE.MetalDark)
    for (let i = 0; i < 4; i++) {
      const z = -0.3 + i * 0.2
      tracks.box(side * 0.405, 0.065, z, 0.025, 0.11, 0.13, SHADE.MetalMid)
      tracks.box(side * 0.42, 0.095, z, 0.015, 0.05, 0.05, SHADE.Metal)
    }
    for (let i = 0; i < 7; i++) {
      tracks.box(side * 0.31, 0.205, -0.39 + i * 0.13, 0.185, 0.024, 0.035, SHADE.MetalMid)
    }
    for (const end of [-1, 1]) {
      tracks.box(side * 0.31, 0.055, end * 0.448, 0.18, 0.035, 0.025, SHADE.MetalMid)
      tracks.box(side * 0.31, 0.135, end * 0.448, 0.18, 0.035, 0.025, SHADE.MetalMid)
    }
  }

  const turret = r.part(PART.Turret)
  turret.box(0, 0.33, -0.045, 0.4, 0.035, 0.36, SHADE.MetalDark)
  turret.box(0, 0.365, -0.065, 0.47, 0.14, 0.4, SHADE.Owner)
  turret.box(0, 0.505, -0.095, 0.38, 0.04, 0.31, SHADE.Owner)
  turret.box(-0.07, 0.545, -0.13, 0.16, 0.025, 0.14, SHADE.MetalDark, SHADE.Metal)
  turret.box(0.115, 0.55, -0.12, 0.09, 0.05, 0.07, SHADE.MetalDark, SHADE.Glass)
  turret.box(0, 0.405, 0.18, 0.2, 0.115, 0.16, SHADE.MetalMid)
  turret.box(0, 0.43, 0.38, 0.09, 0.065, 0.35, SHADE.Owner)
  turret.box(0, 0.425, 0.58, 0.115, 0.08, 0.09, SHADE.MetalMid)
  turret.box(0, 0.443, 0.628, 0.06, 0.042, 0.005, SHADE.MetalDark)
  turret.box(0.16, 0.505, -0.23, 0.018, 0.2, 0.018, SHADE.MetalDark)
  for (const side of [-1, 1]) {
    turret.box(side * 0.24, 0.39, -0.16, 0.065, 0.1, 0.16, SHADE.MetalMid, SHADE.Metal)
    turret.box(side * 0.239, 0.4, 0.02, 0.015, 0.075, 0.09, SHADE.OwnerLight)
  }
  return r.build()
}

/**
 * The one silhouette on the map with nothing under it. Read from above — the
 * only angle there is — an aircraft has to be legible as *not a vehicle*: no
 * treads, no wheels, a narrow fuselage and wings wider than the body.
 */
const hornetGunship: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]
  const big = new MeshBuilder()

  /** Mirror a `+x` face to the other side, reversing the winding with it. */
  const face = (sx: number, q: number[][], shade: Role): void => {
    if (sx > 0) big.quad(q[0], q[1], q[2], q[3], shade)
    else
      big.quad(
        [-q[3][0], q[3][1], q[3][2]],
        [-q[2][0], q[2][1], q[2][2]],
        [-q[1][0], q[1][1], q[1][2]],
        [-q[0][0], q[0][1], q[0][2]],
        shade,
      )
  }

  // --- fuselage -------------------------------------------------------------
  // [z, keelHalf, chineHalf, deckHalf, yKeel, yChine, yDeck]. The chine is the
  // widest line and sits low, so the flanks flare out of the keel and fall back
  // in to a narrow deck; both ends pull in on all three widths at once.
  const stations = [
    [-1.45, 0.1, 0.17, 0.11, 0.3, 0.4, 0.5],
    [-1.1, 0.2, 0.3, 0.22, 0.22, 0.38, 0.56],
    [-0.55, 0.26, 0.4, 0.3, 0.16, 0.36, 0.64],
    [0.1, 0.24, 0.38, 0.28, 0.14, 0.34, 0.62],
    [0.75, 0.16, 0.26, 0.16, 0.16, 0.32, 0.5],
    [1.35, 0.05, 0.09, 0.05, 0.24, 0.3, 0.36],
  ]
  /** One side of a station, bottom to top: keel, chine bottom, chine top, deck. */
  const profile = (s: number[], sx: number): number[][] => [
    [sx * s[1], s[4]],
    [sx * s[2], s[5] - 0.035],
    [sx * s[2], s[5] + 0.035],
    [sx * s[3], s[6]],
  ]
  const BANDS: Role[] = [SHADE.OwnerDark, SHADE.MetalMid, SHADE.Owner]

  for (let i = 0; i < stations.length - 1; i++) {
    const a = stations[i]
    const b = stations[i + 1]
    const za = a[0]
    const zb = b[0]
    for (const sx of [1, -1]) {
      const pa = profile(a, sx)
      const pb = profile(b, sx)
      for (let k = 0; k < 3; k++) {
        const q0 = [pa[k][0], pa[k][1], za]
        const q1 = [pa[k + 1][0], pa[k + 1][1], za]
        const q2 = [pb[k + 1][0], pb[k + 1][1], zb]
        const q3 = [pb[k][0], pb[k][1], zb]
        if (sx > 0) big.quad(q0, q1, q2, q3, BANDS[k])
        else big.quad(q3, q2, q1, q0, BANDS[k])
      }
    }
    // The deck is one lane wide and the keel is left open: the spine and the two
    // intake troughs cover the whole of the first, and a downward face on an
    // aircraft under a fixed overhead camera is geometry nobody ever gets.
    big.quad(
      [-b[3], b[6], zb],
      [b[3], b[6], zb],
      [a[3], a[6], za],
      [-a[3], a[6], za],
      SHADE.Owner,
    )
  }
  for (const [s, front, shade] of [
    [stations[0], false, SHADE.MetalDark],
    [stations[stations.length - 1], true, SHADE.Owner],
  ] as Array<[number[], boolean, Role]>) {
    const p = profile(s, 1)
    for (let k = 0; k < 3; k++) {
      const q = [
        [p[k][0], p[k][1], s[0]],
        [-p[k][0], p[k][1], s[0]],
        [-p[k + 1][0], p[k + 1][1], s[0]],
        [p[k + 1][0], p[k + 1][1], s[0]],
      ]
      if (front) big.quad(q[1], q[0], q[3], q[2], shade)
      else big.quad(q[0], q[1], q[2], q[3], shade)
    }
  }

  // --- wing and tailplane ---------------------------------------------------
  // [x, leadingZ, trailingZ, yCentre, halfThickness]. The root station is inside
  // the fuselage, so the wing has no root rib to hide.
  const wing = [
    [0.34, 0.62, -1.0, 0.36, 0.11],
    [0.8, 0.42, -0.92, 0.35, 0.085],
    [1.16, 0.16, -0.78, 0.33, 0.06],
    [1.45, -0.05, -0.62, 0.28, 0.035],
  ]
  const tail = [
    [0.12, -0.92, -1.42, 0.42, 0.055],
    [0.44, -1.02, -1.4, 0.42, 0.042],
    [0.72, -1.16, -1.38, 0.45, 0.028],
  ]
  /** A point on a wing station at chord fraction `f`, on the upper or lower skin. */
  const pt = (s: number[], f: number, top: boolean): number[] => [
    s[0],
    s[3] + (top ? s[4] : -s[4]),
    s[1] + (s[2] - s[1]) * f,
  ]
  /**
   * The upper skin goes down in chordwise lanes, so the panel break follows the
   * sweep instead of crossing it, and the spar line between them is a lane of its
   * own — the wing's answer to the fuselage's chine facet.
   */
  const loft = (table: number[][], lanes: Array<[number, number, Role]>): void => {
    for (const sx of [1, -1]) {
      for (let i = 0; i < table.length - 1; i++) {
        const a = table[i]
        const b = table[i + 1]
        for (const [f0, f1, shade] of lanes) {
          face(sx, [pt(a, f0, true), pt(b, f0, true), pt(b, f1, true), pt(a, f1, true)], shade)
        }
        face(sx, [pt(a, 0, false), pt(b, 0, false), pt(b, 0, true), pt(a, 0, true)], SHADE.MetalMid)
        face(sx, [pt(b, 1, false), pt(a, 1, false), pt(a, 1, true), pt(b, 1, true)], SHADE.MetalDark)
      }
      const t = table[table.length - 1]
      face(sx, [pt(t, 0, false), pt(t, 1, false), pt(t, 1, true), pt(t, 0, true)], SHADE.MetalMid)
    }
  }
  loft(wing, [
    [0, 0.48, SHADE.Owner],
    [0.48, 0.58, SHADE.MetalMid],
    [0.58, 1, SHADE.OwnerDark],
  ])
  loft(tail, [
    [0, 0.5, SHADE.Owner],
    [0.5, 1, SHADE.OwnerDark],
  ])

  // --- deck ---------------------------------------------------------------
  // Two intake lanes let into the deck either side of the spine, each with a
  // splitter lip standing up at its mouth where the light can find it.
  for (const sx of [-1, 1]) {
    big.box(sx * 0.25, 0.645, -0.06, 0.17, 0.03, 0.36, SHADE.MetalDark)
    big.box(sx * 0.25, 0.645, 0.15, 0.21, 0.09, 0.07, SHADE.MetalMid, SHADE.Metal)
  }
  // The spine is body, not fitting: an owner-coloured ridge running the deck
  // from the mouths back to the tail, dividing the two dark lanes.
  big.wedge(0, 0.63, -0.42, 0.2, 0.1, 1.16, 0.06, SHADE.Owner, SHADE.OwnerLight)
  // The canopy: one faceted block sunk into the deck, glass on the top plane.
  big.wedge(0, 0.5, 0.68, 0.32, 0.09, 0.5, 0.08, SHADE.MetalDark, SHADE.Accent)
  big.box(0, 0.435, 1.06, 0.13, 0.035, 0.26, SHADE.Accent)
  // Louvres stepping down the falling rear deck, and twin nozzles opening
  // upward at the tail — an exhaust facing aft is a face this camera never sees.
  for (let i = 0; i < 2; i++) {
    big.box(0, 0.582 - i * 0.016, -0.95 - i * 0.11, 0.42 - i * 0.08, 0.03, 0.08, SHADE.MetalDark)
  }
  for (const sx of [-1, 1]) {
    big.box(sx * 0.19, 0.47, -1.3, 0.24, 0.13, 0.42, SHADE.MetalDark, SHADE.Accent)
    // Fins toed out either side of the nozzles. A blade canted in plan is the one
    // way a tail changes the outline of something seen from overhead.
    const fin = new MeshBuilder()
    fin.wedge(0, 0, 0, 0.11, 0.34, 0.54, 0.045, SHADE.Owner, SHADE.OwnerLight)
    big.merge(fin, sx * 0.36, 0.54, -1.04, 1, sx * 0.2)
    // Actuator fairings straddling the trailing edge, tapering out past it, and a
    // lit tip. They are the only things on the wing that leave its outline, which
    // is what keeps a plan-view triangle from reading as one flat panel.
    big.wedge(sx * 0.66, 0.29, -0.92, 0.12, 0.11, 0.46, 0.035, SHADE.MetalMid, SHADE.Metal)
    big.box(sx * 1.38, 0.3, -0.12, 0.09, 0.045, 0.16, SHADE.Accent)
  }
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // --- ordnance -------------------------------------------------------------
  // Two darts tucked into the leading edge with their noses out in front of it,
  // and a heavier one along the keel that reaches past the chisel.
  const rounds: Array<[number, number, number, number, number]> = [
    [-0.68, 0.26, 0.62, 0.15, 0.78],
    [0.68, 0.26, 0.62, 0.15, 0.78],
    [0, 0.07, 1.1, 0.19, 0.92],
  ]
  for (let i = 0; i < ORDNANCE.length; i++) {
    const [x, y, z, w, len] = rounds[i]
    const shot = new MeshBuilder()
    // Dark flanks under a lit spine, because the top is the face that has to
    // answer "is it still armed" from overhead.
    shot.box(x, y, z, w, w, len - 0.2, SHADE.MetalDark, SHADE.Accent)
    shot.wedge(x, y, z + len / 2 - 0.1, w, w, 0.2, 0.055, SHADE.MetalMid)
    r.part(ORDNANCE[i]).merge(shot, 0, 0, 0, 0.5)
  }
  return r.build()
}

const regolithHarvester: Recipe = () => {
  const r = new RecipeBuilder()
  const big = new MeshBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]

  /** A painted panel on a flank, laid a hair proud of the plate it sits on. */
  const onX = (x: number, y0: number, y1: number, z0: number, z1: number, s: Role): void => {
    if (x > 0) big.quad([x, y0, z1], [x, y0, z0], [x, y1, z0], [x, y1, z1], s)
    else big.quad([x, y0, z0], [x, y0, z1], [x, y1, z1], [x, y1, z0], s)
  }
  /** The same on an end face. `out` is the direction the plate faces. */
  const onZ = (z: number, out: number, y0: number, y1: number, x0: number, x1: number, s: Role): void => {
    if (out > 0) big.quad([x0, y0, z], [x1, y0, z], [x1, y1, z], [x0, y1, z], s)
    else big.quad([x1, y0, z], [x0, y0, z], [x0, y1, z], [x1, y1, z], s)
  }

  // The frame: one blunt sill, and its top face is the bin floor. Narrower than
  // the bin standing on it, so the load-carrying mass overhangs its own chassis
  // and throws a line of shadow down each side.
  big.box(0, 0.1, -0.16, 1.02, 0.28, 1.56, SHADE.MetalMid, SHADE.MetalDark)
  for (const sx of [-1, 1]) {
    onX(sx * 0.511, 0.14, 0.26, -0.9, 0.5, SHADE.MetalDark)
  }

  // The bin. Four separate plates, not a box with a lid: the tail plate is the
  // tallest thing on the vehicle, the sides step down from it, and the front is
  // cut low enough that the heap shows over it. Every rim takes the light metal,
  // so each wall reads as a slab with a thickness rather than as a coloured
  // plane.
  for (const sx of [-1, 1]) {
    big.box(sx * 0.5, 0.38, -0.26, 0.16, 0.6, 1.22, SHADE.Owner, SHADE.Metal)
  }
  big.box(0, 0.38, -0.79, 1.16, 0.7, 0.16, SHADE.Owner, SHADE.Metal)
  big.box(0, 0.38, 0.29, 1.16, 0.34, 0.16, SHADE.Owner, SHADE.Metal)

  // Plate courses and a wear strip, painted on. A raised girth band was built
  // first and it crossed the straps into a dark lattice that read as
  // scaffolding; two flat lines do the same work of splitting one tall sheet
  // into courses for four triangles a side.
  for (const sx of [-1, 1]) {
    onX(sx * 0.581, 0.66, 0.74, -0.86, 0.34, SHADE.MetalDark)
    onX(sx * 0.581, 0.42, 0.52, -0.86, 0.34, SHADE.MetalDark)
  }
  onZ(-0.871, -1, 0.66, 0.74, -0.56, 0.56, SHADE.MetalDark)
  onZ(0.371, 1, 0.5, 0.58, -0.56, 0.56, SHADE.MetalDark)
  // A dark liner just inside the four plates. The wall the camera looks across
  // at is the *far* one, whose inner face is the only interior surface culling
  // leaves standing, so lining it is what puts the gold against something dark
  // instead of against more team colour.
  for (const sx of [-1, 1]) {
    onX(sx * 0.41, 0.38, 0.96, -0.7, 0.2, SHADE.MetalDark)
  }
  onZ(-0.7, 1, 0.38, 1.06, -0.42, 0.42, SHADE.MetalDark)
  onZ(0.2, -1, 0.38, 0.7, -0.42, 0.42, SHADE.MetalDark)

  // The load, heaped against the tail plate and cresting the front lip.
  big.box(-0.16, 0.38, -0.44, 0.46, 0.46, 0.46, SHADE.Accent)
  big.box(0.2, 0.38, -0.08, 0.4, 0.4, 0.52, SHADE.Accent)
  big.box(0.22, 0.38, -0.52, 0.36, 0.34, 0.36, SHADE.Accent)
  big.box(-0.22, 0.38, 0.04, 0.34, 0.26, 0.3, SHADE.MetalMid)

  // The intake: low, blunt, and the widest thing on the vehicle — the one mass
  // that leaves the chassis' outline, and the reason this reads as a machine
  // that eats ground rather than as a truck. Three fat teeth, because three that
  // survive the halving beat eight that do not.
  big.wedge(0, 0.02, 0.84, 1.38, 0.3, 0.36, 0.07, SHADE.MetalDark, SHADE.Metal)
  for (const x of [-0.44, 0, 0.44]) {
    big.box(x, 0, 1.04, 0.26, 0.22, 0.18, SHADE.Metal)
  }
  big.quad([-0.62, 0.325, 1.0], [0.62, 0.325, 1.0], [0.62, 0.325, 0.9], [-0.62, 0.325, 0.9], SHADE.Accent)
  big.quad([-0.6, 0.325, 0.86], [0.6, 0.325, 0.86], [0.6, 0.325, 0.74], [-0.6, 0.325, 0.74], SHADE.MetalDark)

  // The elevator: two blunt blocks in a stair, climbing from the maw to the
  // bin's front lip and finishing just over it. It gives the nose a rising back
  // rather than a flat one, which is the half of the silhouette a bin cannot
  // supply on its own.
  big.box(-0.1, 0.3, 0.52, 0.62, 0.24, 0.34, SHADE.MetalMid, SHADE.Metal)
  big.box(-0.1, 0.54, 0.4, 0.56, 0.22, 0.3, SHADE.MetalDark, SHADE.Metal)

  // A cab welded onto the front corner, off the centreline — the one place a
  // crew could sit on a vehicle that is otherwise all container, and what stops
  // the front from mirroring. Both its lit faces are painted on.
  big.box(0.42, 0.38, 0.5, 0.42, 0.52, 0.44, SHADE.Owner, SHADE.Metal)
  onZ(0.721, 1, 0.62, 0.78, 0.24, 0.6, SHADE.Accent)
  onX(0.631, 0.62, 0.78, 0.34, 0.66, SHADE.Accent)

  // Tail: a counterweight across the back and one stack standing off the corner
  // opposite the cab. The stack is a single blunt pipe rather than a matched
  // pair — the vehicle is asymmetric at both ends, and one thing breaking the
  // roofline is worth more than two that balance.
  big.box(0, 0.12, -0.96, 1.06, 0.26, 0.18, SHADE.MetalDark, SHADE.Metal)
  big.box(0.6, 0.38, -0.7, 0.2, 0.62, 0.2, SHADE.MetalDark, SHADE.Accent)
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  const track = new MeshBuilder()
  // Authored long and halved, so the band comes back with nine cleats where one
  // drawn at final size would have had four. It runs proud of the frame on both
  // sides — the running gear is the widest part of the chassis — and sits back
  // under the bin, which is where the weight it is carrying actually is.
  for (const sx of [-1, 1]) {
    track.treadBand(sx * 0.46, -0.2, 1.08, 0.34, SHADE.MetalDark)
  }
  r.part(PART.Tread).merge(track, 0, 0, 0, 0.5)
  return r.build()
}

/**
 * A low ring of sandbags, drawn only while the squad is dug in.
 *
 * Deliberately squat and open at the front. It has to read as cover from
 * directly above without hiding the figures behind it — a wall tall enough to
 * look protective at this camera angle swallows the unit it is protecting, and
 * then the player cannot tell which of their squads is which.
 */
function fieldworks(r: RecipeBuilder): void {
  const works = r.part(PART.Works)
  // Only the front parapet is stacked two courses high. The flanks were too,
  // and the triangle budget an infantry recipe has is better spent on the
  // figures behind the wall than on the wall — a squad that reads as three men
  // is the whole defect, and a single course still closes the ring.
  const bag = (x: number, z: number, w: number, d: number, stacked: boolean): void => {
    works.box(x, 0, z, w, 0.075, d, SHADE.Metal, SHADE.MetalDark)
    if (stacked) works.box(x, 0.075, z, w * 0.82, 0.06, d * 0.82, SHADE.MetalDark)
  }
  for (const x of [-0.2, 0, 0.2]) bag(x, -0.24, 0.19, 0.13, true)
  for (const z of [-0.08, 0.12]) {
    bag(-0.27, z, 0.13, 0.19, false)
    bag(0.27, z, 0.13, 0.19, false)
  }
}

const rifleSquad: Recipe = () => {
  const r = new RecipeBuilder()

  type Shade = (typeof SHADE)[keyof typeof SHADE]

  /**
   * A value band laid a hair proud of a vertical face, and its horizontal twin
   * for a top one.
   *
   * Two triangles apiece where a box costs ten, and three men plus their
   * fieldworks have four hundred between them — weld seams, a belt line, a
   * visor slit and a helmet crest exist at all only because they are bought at
   * this price. Under a fixed light a strip of a different value on a flat
   * plate reads the way a raised one does; what it cannot do is change the
   * outline, so every mass that carries the silhouette is still solid.
   */
  const weld = (
    b: MeshBuilder,
    cx: number,
    y0: number,
    cz: number,
    w: number,
    h: number,
    shade: Shade,
  ): void => {
    b.quad(
      [cx - w / 2, y0, cz],
      [cx + w / 2, y0, cz],
      [cx + w / 2, y0 + h, cz],
      [cx - w / 2, y0 + h, cz],
      shade,
    )
  }
  const seam = (
    b: MeshBuilder,
    cx: number,
    y: number,
    cz: number,
    w: number,
    d: number,
    shade: Shade,
  ): void => {
    b.quad(
      [cx - w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz - d / 2],
      [cx - w / 2, y, cz - d / 2],
      shade,
    )
  }

  /**
   * One trooper, modelled at twice final size and welded out of plate.
   *
   * The band stack the rest of the infantry hold to survives — dark at the
   * ground, team colour through the middle, a skin band at the jaw, the ramp's
   * light step closing the top — but every mass in it is a slab with a rolled
   * edge rather than a limb.
   *
   * There are no arms. The shoulder break, which is what stops a figure being
   * a rectangle, is a pair of pauldrons hung off the chest deep enough and low
   * enough to be the arms as well: bare alloy on the flanks so the man reads
   * metal from the side, team colour on their tops so he reads coloured from
   * above, where most of him is. Two slabs that overhang the torso do more for
   * the outline at ten pixels than two columns that hang beside it, and they
   * are the reason this squad is recognisable as the same army's heavy end
   * rather than as the marksman standing up.
   *
   * Separate shins and knee plates keep the stance legible below the armour.
   */
  const trooper = (H: number, side: number, kit: number): MeshBuilder => {
    const b = new MeshBuilder()
    const y = (v: number): number => v * H

    // Boots: two blunt blocks, staggered against each other so the squad is
    // standing rather than parked. Light tops — under a camera that looks down
    // they are the only ground-level surface of him that is ever seen.
    for (const sx of [-1, 1]) {
      b.box(
        sx * 0.105,
        0,
        0.02 + sx * side * 0.045,
        0.17,
        y(0.19),
        0.24,
        SHADE.MetalDark,
        SHADE.MetalMid,
      )
    }

    for (const sx of [-1, 1]) {
      b.box(sx * 0.105, y(0.15), sx * side * 0.03, 0.13, y(0.24), 0.17, SHADE.OwnerDark, SHADE.Owner)
      b.box(sx * 0.105, y(0.23), 0.12 + sx * side * 0.03, 0.13, y(0.09), 0.04, SHADE.MetalMid)
    }
    b.box(0, y(0.35), 0, 0.3, y(0.06), 0.22, SHADE.MetalDark)
    weld(b, 0, y(0.33), 0.152, 0.30, y(0.055), SHADE.MetalDark)

    // Chest: the team surface, and a slab — square-shouldered, square-hipped,
    // nothing tapering anywhere. It is deeper and taller than the pauldrons
    // beside it on purpose. Cut level with them the man came out as one pale
    // lid seen from above with the team colour buried underneath, and a rifle
    // squad that carries no owner colour on the surface the camera actually
    // gets is the most numerous unit on the field being the least legible one.
    // The weld seam across the front sits above the gun and below the jaw,
    // which is the only strip of him the weapon and the shoulders both clear.
    b.box(0, y(0.41), 0, 0.34, y(0.27), 0.26, SHADE.Owner)
    weld(b, 0, y(0.6), 0.133, 0.3, y(0.05), SHADE.MetalDark)

    // Pauldrons, and their tops kept a step below the chest. From overhead that
    // orders him into rings — bare alloy at the shoulders, team colour around
    // them, the light step closing the middle — where flush plates gave one
    // silhouette-wide blank.
    for (const sx of [-1, 1]) {
      b.box(sx * 0.215, y(0.38), -0.005, 0.13, y(0.24), 0.2, SHADE.Metal, SHADE.MetalMid)
    }

    // Jaw under the brow of the helmet, with a slit cut across it. The skin
    // band is the one place on the model the armour opens, and it has to be a
    // full band rather than a hairline or the head fuses to the chest.
    b.box(0, y(0.68), 0.02, 0.2, y(0.1), 0.18, SHADE.Accent)
    weld(b, 0, y(0.715), 0.113, 0.15, y(0.035), SHADE.Glass)

    // Helmet: the brightest thing on the man and wider than the jaw under it,
    // overhanging at the front so there is a brow. The crest rib along its
    // spine is there because the helmet's top face is the largest single
    // surface a standing figure has, and an unbroken one is what makes infantry
    // read as coloured tiles from this camera.
    b.wedge(0, y(0.78), 0, 0.29, y(0.19), 0.26, 0.03, SHADE.OwnerLight, SHADE.OwnerLight)
    seam(b, 0, y(0.97) + 0.008, 0, 0.06, 0.18, SHADE.MetalDark)

    // Kit on the back: the third mass, and the only thing separating one man in
    // the squad from another now that all three wear the same plate. Each gets
    // a lid seam, because a pack is the second-largest top face on him.
    if (kit === 0) {
      b.box(0, y(0.42), -0.195, 0.3, y(0.26), 0.12, SHADE.MetalDark, SHADE.MetalMid)
      seam(b, 0, y(0.68) + 0.008, -0.195, 0.16, 0.08, SHADE.Metal)
    } else if (kit === 1) {
      // A canister standing above the shoulder line: one man in three breaks
      // his outline upward, and it is the cheapest way to stop a squad looking
      // like one figure stamped three times.
      b.box(-0.04, y(0.4), -0.2, 0.22, y(0.42), 0.13, SHADE.OwnerDark, SHADE.MetalMid)
      seam(b, -0.04, y(0.82) + 0.008, -0.2, 0.12, 0.09, SHADE.Metal)
    } else {
      b.box(0.03, y(0.42), -0.21, 0.26, y(0.32), 0.15, SHADE.MetalDark, SHADE.Metal)
      seam(b, 0.03, y(0.74) + 0.008, -0.21, 0.14, 0.1, SHADE.MetalMid)
    }

    // The weapon: a slab receiver carried level at the chest and a heavy barrel
    // past it. Its reach is a contract — the muzzle stands clear of the man's
    // own outline by most of a body depth, because a figure with nothing
    // projecting from it is a crate whatever is painted on it — and it is thick
    // enough to survive a pixel per voxel, in the mid chassis value so it holds
    // against shadowed ground as well as against him.
    b.box(0.1 * side, y(0.455), 0.2, 0.11, y(0.12), 0.4, SHADE.MetalMid)
    b.box(0.1 * side, y(0.47), 0.52, 0.085, y(0.09), 0.28, SHADE.Metal, SHADE.MetalMid)
    return b
  }

  // Modelled at 2x and merged down. At that size a 0.06 weld seam is a part you
  // would think to put in; at final scale it is a 0.03 sliver you would talk
  // yourself out of, and the difference between those two decisions is most of
  // what separates this squad from three painted blocks.
  const big = new MeshBuilder()
  big.merge(trooper(0.8, 1, 0), -0.46, 0, 0.2, 1, 0.14)
  big.merge(trooper(0.76, -1, 1), 0.44, 0, -0.04, 1, -0.17)
  big.merge(trooper(0.86, 1, 2), -0.02, 0, -0.5, 1, 0.06)
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // Fieldworks: a bolted steel revetment rather than sandbags, because this
  // squad does not fill bags — it drops plate off a truck and pins it with two
  // driven posts. Kept low and open at the rear for the reason the sandbag line
  // was: a parapet tall enough to look protective from directly overhead hides
  // the unit behind it, and then a player cannot tell their squads apart.
  const worksBig = new MeshBuilder()
  worksBig.box(0, 0, 0.58, 1.3, 0.3, 0.16, SHADE.Metal, SHADE.MetalMid)
  weld(worksBig, 0, 0.185, 0.663, 1.24, 0.06, SHADE.MetalDark)
  weld(worksBig, 0, 0.055, 0.663, 1.24, 0.045, SHADE.MetalMid)
  for (const sx of [-1, 1]) {
    // The posts stand a head above the wall they hold up. They are the only
    // part of the works that breaks its own top edge, and a flat-topped wall at
    // this camera angle is a stripe on the ground.
    worksBig.box(sx * 0.62, 0, 0.545, 0.18, 0.4, 0.28, SHADE.MetalDark, SHADE.MetalMid)
    // The returns are set wide enough that the men stand inside them rather
    // than through them.
    worksBig.box(sx * 0.66, 0, 0.1, 0.14, 0.24, 0.6, SHADE.Metal, SHADE.MetalMid)
  }
  r.part(PART.Works).merge(worksBig, 0, 0, 0, 0.5)
  return r.build()
}

const engineer: Recipe = () => {
  const r = new RecipeBuilder()

  type Shade = (typeof SHADE)[keyof typeof SHADE]

  /** A value band laid a hair proud of a front face. Two triangles a seam. */
  const weld = (
    b: MeshBuilder,
    cx: number,
    y0: number,
    cz: number,
    w: number,
    h: number,
    shade: Shade,
  ): void => {
    b.quad(
      [cx - w / 2, y0, cz],
      [cx + w / 2, y0, cz],
      [cx + w / 2, y0 + h, cz],
      [cx - w / 2, y0 + h, cz],
      shade,
    )
  }

  /** Its horizontal twin, for the top faces the camera actually gets. */
  const seam = (
    b: MeshBuilder,
    cx: number,
    y: number,
    cz: number,
    w: number,
    d: number,
    shade: Shade,
  ): void => {
    b.quad(
      [cx - w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz - d / 2],
      [cx - w / 2, y, cz - d / 2],
      shade,
    )
  }

  /** Bolt heads in a row. Proud, because a countersunk rivet has no shadow. */
  const rivets = (b: MeshBuilder, y: number, cz: number, xs: number[], shade: Shade): void => {
    for (const x of xs) b.box(x, y, cz, 0.09, 0.035, 0.09, shade)
  }

  // Modelled at twice size and merged down. Everything on him under a tenth of
  // a unit — the visor band, the knee plates, the bolt row along the yoke's lip,
  // the ribs on the hose — exists only because at this size each was a part
  // worth drawing rather than a sliver worth arguing out of.
  const big = new MeshBuilder()

  // Boots, staggered: the left one planted forward, the right one back and
  // turned out. He is braced over the work, not standing to attention. Lit
  // tops, since from this camera the top of a boot is all of a boot there is.
  big.box(-0.18, 0, 0.10, 0.26, 0.15, 0.34, SHADE.MetalDark, SHADE.MetalMid)
  big.box(0.19, 0, -0.05, 0.26, 0.15, 0.34, SHADE.MetalDark, SHADE.MetalMid)

  // Shins in the dark team step, with a bare knee plate riveted over the front
  // of each. The plate is the only value change between boot and apron, and
  // without it the whole lower half is one column.
  big.box(-0.17, 0.13, 0.06, 0.24, 0.19, 0.26, SHADE.OwnerDark)
  big.box(0.18, 0.13, -0.03, 0.24, 0.19, 0.26, SHADE.OwnerDark)
  big.box(-0.17, 0.21, 0.19, 0.20, 0.11, 0.05, SHADE.Metal)
  big.box(0.18, 0.21, 0.10, 0.20, 0.11, 0.05, SHADE.Metal)

  // The apron: one flared skirt rather than two thighs. A negative inset is how
  // plate gets a lip instead of a taper, and the lip is what throws the shadow
  // that separates his waist from his legs.
  big.wedge(0, 0.28, 0.02, 0.46, 0.17, 0.32, -0.04, SHADE.OwnerDark, SHADE.MetalDark)
  weld(big, 0, 0.375, 0.202, 0.44, 0.055, SHADE.MetalDark)
  // Kit on the belt, hung off both hips so he is wide where a soldier is thin.
  big.box(-0.29, 0.29, 0.06, 0.11, 0.15, 0.15, SHADE.MetalDark, SHADE.Metal)
  // A pry bar clipped up the left flank, balancing the cutter on the right. Two
  // things hung off a man at different angles is what stops him reading as one
  // solid block seen from any side.
  big.box(-0.31, 0.34, -0.10, 0.08, 0.30, 0.09, SHADE.MetalMid)
  big.box(-0.31, 0.62, -0.10, 0.10, 0.06, 0.13, SHADE.Metal, SHADE.MetalMid)

  // Chest: two slabs, each stepped forward of the one below. Three courses of
  // plate walking towards the work is how a stoop is built out of axis-aligned
  // boxes, and it costs nothing but the offset.
  big.box(0, 0.42, 0.05, 0.50, 0.14, 0.32, SHADE.Owner)
  weld(big, 0, 0.465, 0.212, 0.44, 0.05, SHADE.MetalDark)
  big.box(0, 0.55, 0.09, 0.48, 0.13, 0.30, SHADE.Owner)
  weld(big, 0, 0.595, 0.242, 0.42, 0.05, SHADE.MetalDark)

  // The yoke: a single wide plate laid across the shoulders, overhanging every
  // mass beneath it. This is the silhouette. A shape that is widest at the top
  // and has its head buried in it does not look like anything else the faction
  // walks onto a map.
  //
  // It takes the owner colour and the chest keeps it too, because from directly
  // above this plate *is* the man: laid in bare alloy it hid every blue surface
  // under it and the model came back a grey lump with a blue sliver down one
  // side. The neutral ramp is left to the things bolted on top of him.
  big.box(0, 0.67, 0.10, 0.42, 0.09, 0.26, SHADE.OwnerDark, SHADE.MetalDark)
  big.box(0, 0.74, 0.05, 0.68, 0.08, 0.44, SHADE.Owner)
  seam(big, 0, 0.821, 0.05, 0.10, 0.42, SHADE.MetalDark)
  // Bolt heads along the front lip in hazard tan. Four bright points in a row
  // across the widest edge of him is the marking the fighting infantry does not
  // wear, and it survives the quantizer where a painted stripe would not.
  rivets(big, 0.821, 0.23, [-0.29, -0.11, 0.11, 0.29], SHADE.Accent)

  // Head: a welding hood, set low and forward so the yoke overhangs it. No jaw
  // and no skin — the one opening in the armour is the visor, and it is the hot
  // slit rather than a face.
  big.box(0, 0.81, 0.15, 0.30, 0.19, 0.24, SHADE.OwnerLight, SHADE.OwnerLight)
  weld(big, 0, 0.885, 0.272, 0.26, 0.085, SHADE.Accent)
  weld(big, 0, 0.822, 0.272, 0.28, 0.055, SHADE.MetalDark)
  // The brow is a lip, not a lid. Run back over the crown it buried the pale
  // step the hood is painted in and the head came back as a grey hatch.
  big.box(0, 0.955, 0.255, 0.34, 0.05, 0.09, SHADE.MetalDark, SHADE.MetalMid)

  // Pack and bottles. The bottles stand clear of the hood on purpose: they are
  // the part of him that shows over cover, and the only round thing in a roster
  // of squares. Bare alloy, never team colour — a six-sided drum takes its
  // light on the sides, and the owner ramp goes to mud there.
  big.box(0, 0.40, -0.23, 0.46, 0.32, 0.26, SHADE.MetalMid, SHADE.Metal)
  for (const sx of [-1, 1]) {
    big.cylinder(sx * 0.17, 0.58, -0.20, 0.10, 0.32, 6, SHADE.Metal, SHADE.MetalMid)
    big.box(sx * 0.17, 0.90, -0.20, 0.09, 0.07, 0.09, SHADE.Accent)
  }
  big.box(0, 0.84, -0.20, 0.36, 0.05, 0.09, SHADE.MetalDark)

  // The feed line, laid across the back of the pack behind the bottles. A tread
  // band is the one primitive whose detail gets *finer* the longer it is built
  // and the harder it is scaled down, so a corrugated hose modelled at this
  // length comes back ribbed closer than any hand-placed row of blocks would be
  // worth writing — and it goes on a top face, which is the only place this
  // camera could ever count ribs.
  const hose = new MeshBuilder()
  hose.treadBand(0, 0, 0.42, 0.11, SHADE.MetalDark)
  big.merge(hose, 0, 0.71, -0.32, 1, Math.PI / 2)

  // Arms: two gauntlets, both committed to the tool. There is no free hand.
  big.box(0.25, 0.44, 0.18, 0.15, 0.15, 0.26, SHADE.OwnerDark, SHADE.MetalMid)
  big.box(-0.15, 0.40, 0.26, 0.14, 0.14, 0.22, SHADE.OwnerDark, SHADE.MetalMid)
  big.box(0.25, 0.42, 0.30, 0.17, 0.11, 0.08, SHADE.MetalDark, SHADE.Metal)

  // The cutter, stepping down and forward from the belt to a nozzle at boot
  // height. Every other figure in the army points its line straight ahead; this
  // one points at the ground it is standing on, and the steps alternate value so
  // the run reads as four parts rather than as one grey lump on his hands.
  big.box(0.07, 0.36, 0.36, 0.24, 0.17, 0.22, SHADE.MetalMid, SHADE.Metal)
  seam(big, 0.07, 0.531, 0.36, 0.12, 0.16, SHADE.Metal)
  big.box(0.07, 0.26, 0.50, 0.19, 0.12, 0.20, SHADE.MetalDark, SHADE.MetalMid)
  big.box(0.07, 0.14, 0.62, 0.15, 0.13, 0.16, SHADE.Metal, SHADE.MetalMid)
  big.box(0.07, 0.10, 0.70, 0.13, 0.07, 0.10, SHADE.MetalDark)
  big.box(0.07, 0.01, 0.71, 0.12, 0.10, 0.13, SHADE.Accent)

  r.part(PART.Hull).merge(big, 0, 0, -0.06, 0.5)
  return r.build()
}

const scoutBuggy: Recipe = () => {
  const r = new RecipeBuilder()

  // A cast body rather than an assembled one. Six-point cross-sections are
  // lofted nose to tail, so the chine line runs unbroken the whole length of
  // the unit and every panel meets its neighbour at an angle instead of at a
  // joint. The plan is deliberately waisted between the axles: nothing else
  // this faction fields narrows in the middle, and a pinched hull with four
  // oversized wheels standing proud of it is an outline no layered box can be
  // confused with.
  type Station = {
    z: number
    /** Half-beam at the belly, at the chine, and at the deck. */
    wb: number
    w: number
    wt: number
    /** Belly, chine and deck heights. */
    y0: number
    ym: number
    y1: number
  }

  // Counter-clockwise seen from the nose: up the right flank, across the deck,
  // down the left flank, back along the belly.
  const ring = (s: Station): number[][] => [
    [s.wb, s.y0, s.z],
    [s.w, s.ym, s.z],
    [s.wt, s.y1, s.z],
    [-s.wt, s.y1, s.z],
    [-s.w, s.ym, s.z],
    [-s.wb, s.y0, s.z],
  ]

  const STATIONS: Station[] = [
    { z: 0.96, wb: 0.05, w: 0.08, wt: 0.05, y0: 0.15, ym: 0.2, y1: 0.25 },
    { z: 0.62, wb: 0.16, w: 0.24, wt: 0.15, y0: 0.1, ym: 0.22, y1: 0.36 },
    { z: 0.3, wb: 0.22, w: 0.32, wt: 0.22, y0: 0.1, ym: 0.24, y1: 0.46 },
    { z: -0.02, wb: 0.19, w: 0.27, wt: 0.21, y0: 0.1, ym: 0.24, y1: 0.52 },
    { z: -0.36, wb: 0.23, w: 0.34, wt: 0.25, y0: 0.1, ym: 0.24, y1: 0.49 },
    { z: -0.72, wb: 0.12, w: 0.18, wt: 0.13, y0: 0.16, ym: 0.22, y1: 0.31 },
  ]

  // Drawn at twice size and merged down at the end. At 2x a 0.05 vent slat is a
  // part you would think to draw; at final scale it is a 0.025 sliver you would
  // talk yourself out of, and the three of them stepping down the bonnet are
  // the best-read detail on the model.
  const body = new MeshBuilder()
  for (let i = 0; i < STATIONS.length - 1; i++) {
    const a = ring(STATIONS[i])
    const c = ring(STATIONS[i + 1])
    for (let k = 0; k < 6; k++) {
      const j = (k + 1) % 6
      // Owner colour on the whole upper body — deck and shoulders are the
      // largest contiguous mass here and the only thing that can carry a team
      // read on a vehicle this small. The mid neutral is kept for the strake
      // below the chine, so the chine itself has a value step along it.
      const shade =
        k === 5 ? SHADE.MetalDark : k === 0 || k === 4 ? SHADE.MetalMid : SHADE.Owner
      body.quad(a[k], c[k], c[j], a[j], shade)
    }
  }
  const nose = ring(STATIONS[0])
  const tail = ring(STATIONS[STATIONS.length - 1])
  for (let i = 1; i < 5; i++) {
    body.tri(nose[0], nose[i], nose[i + 1], SHADE.MetalMid)
    body.tri(tail[0], tail[i + 1], tail[i], SHADE.MetalDark)
  }

  // A splitter blade slid under the prow. It is what stops the loft reading as
  // a lozenge floating over its own shadow: the hull visibly sits on something.
  body.wedge(0, 0.05, 0.78, 0.42, 0.07, 0.3, 0.07, SHADE.MetalMid, SHADE.Metal)

  // Lamps let into the nose shoulders.
  for (const sx of [-1, 1]) body.box(sx * 0.13, 0.21, 0.72, 0.09, 0.07, 0.07, SHADE.Accent)

  // The bonnet is the largest unbroken face on the unit and the camera looks
  // straight down at it, so it gets a raised access hatch and two vent slats
  // stepping down the slope with it.
  body.wedge(0, 0.4, 0.4, 0.24, 0.055, 0.12, 0.05, SHADE.MetalMid, SHADE.Metal)
  body.box(0, 0.365, 0.55, 0.18, 0.05, 0.05, SHADE.MetalDark, SHADE.Metal)
  body.box(0, 0.335, 0.63, 0.15, 0.05, 0.05, SHADE.MetalDark, SHADE.Metal)

  // Cockpit fairing: a tapered cap over the waist, glass across its front, and
  // a low hoop behind it. The hoop is the one part of this unit with sky inside
  // its own outline, which is worth more at gameplay zoom than any panel line.
  body.wedge(0, 0.46, 0.08, 0.4, 0.18, 0.38, 0.08, SHADE.Owner, SHADE.OwnerLight)
  body.box(0, 0.5, 0.25, 0.28, 0.11, 0.05, SHADE.Accent)
  for (const sx of [-1, 1]) body.box(sx * 0.15, 0.5, -0.16, 0.05, 0.13, 0.05, SHADE.Metal)
  body.box(0, 0.63, -0.16, 0.35, 0.05, 0.05, SHADE.Metal)

  // Engine bay aft: a faired intake stepping down off the fairing, and louvres
  // over the deck behind it. Both sit off the centreline — an even spread of
  // vents reads as a placeholder the moment the eye finds the axis.
  body.wedge(0, 0.45, -0.26, 0.32, 0.09, 0.2, 0.06, SHADE.Owner, SHADE.OwnerLight)
  for (const z of [-0.42, -0.52]) {
    body.box(0.08, 0.43, z, 0.22, 0.05, 0.05, SHADE.MetalDark, SHADE.Metal)
  }

  // Exhausts out of the tail cut, glowing at the mouth.
  for (const sx of [-1, 1]) body.box(sx * 0.12, 0.24, -0.75, 0.09, 0.09, 0.14, SHADE.MetalDark, SHADE.Accent)

  // A whip off the rear quarter. It is the tallest thing on the unit and it
  // costs almost nothing — a scout is found by the thread over it before it is
  // found by its hull.
  body.box(-0.2, 0.45, -0.34, 0.05, 0.27, 0.05, SHADE.MetalDark, SHADE.Accent)

  r.part(PART.Hull).merge(body, 0, 0, 0, 0.5)

  // The wheels, turned about their own axle rather than stacked from boxes —
  // `cylinder` only spins about Y, so a road wheel has to be wound by hand.
  // Adjacent facets alternate between the two neutral chassis values, which is
  // the trick `treadBand` uses on its cleats and it works for the same reason:
  // a tyre shaded as one mass is a black hole in the outline, and a tyre with a
  // value step every eighth of a turn reads as rubber even at six pixels.
  const SEGS = 8
  const wheel = (b: MeshBuilder, cx: number, cz: number, halfW: number, out: number) => {
    const cy = 0.25
    const x0 = cx - halfW
    const x1 = cx + halfW
    const at = (k: number): number[] => {
      const a = ((k % SEGS) / SEGS) * Math.PI * 2
      return [cy + Math.sin(a) * 0.25, cz + Math.cos(a) * 0.25]
    }
    for (let k = 0; k < SEGS; k++) {
      const [ya, za] = at(k)
      const [yb, zb] = at(k + 1)
      b.quad(
        [x0, ya, za],
        [x1, ya, za],
        [x1, yb, zb],
        [x0, yb, zb],
        k % 2 === 0 ? SHADE.MetalMid : SHADE.MetalDark,
      )
    }
    // Both sidewalls are closed. The inboard one looks buried against the chine
    // and is not: the hull is narrowest exactly where the axles are, so on the
    // far side of the unit the camera looks straight into an open rim.
    const p = Array.from({ length: SEGS }, (_, k) => at(k))
    for (let i = 1; i < SEGS - 1; i++) {
      const [u, v] = [p[i], p[i + 1]]
      b.tri([x1, p[0][0], p[0][1]], [x1, v[0], v[1]], [x1, u[0], u[1]], SHADE.MetalDark)
      b.tri([x0, p[0][0], p[0][1]], [x0, u[0], u[1]], [x0, v[0], v[1]], SHADE.MetalDark)
    }
    // Hub proud of the outboard sidewall.
    b.box(cx + out * (halfW + 0.015), cy - 0.05, cz, 0.055, 0.1, 0.1, SHADE.Metal)
  }

  const gear = new MeshBuilder()
  for (const sx of [-1, 1]) {
    wheel(gear, sx * 0.4, 0.44, 0.11, sx)
    wheel(gear, sx * 0.4, -0.42, 0.11, sx)
  }
  r.part(PART.Tread).merge(gear, 0, 0, 0, 0.5)

  // The mount is a blister faired into the fairing behind it, not a drum stood
  // on the roof: this hull has no flat left to stand one on. A short stub gun
  // and a sensor can either side of it.
  const pod = new MeshBuilder()
  pod.wedge(0, 0.56, 0, 0.32, 0.12, 0.3, 0.08, SHADE.Owner, SHADE.OwnerLight)
  pod.box(0, 0.61, 0.17, 0.08, 0.08, 0.22, SHADE.MetalDark, SHADE.Metal)
  pod.box(0, 0.62, 0.3, 0.06, 0.05, 0.06, SHADE.Accent)
  r.part(PART.Turret).merge(pod, 0, 0, 0, 0.5)
  return r.build()
}

const mcv: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]

  const track = new MeshBuilder()
  for (const sx of [-1, 1]) {
    track.treadBand(sx * 0.56, -0.02, 1.1, 0.32, SHADE.MetalDark)
  }
  r.part(PART.Tread).merge(track, 0, 0, 0, 0.5)

  const big = new MeshBuilder()

  /** A patch on one flank, wound to face outward. Two triangles of panel line. */
  const flank = (
    sx: number,
    x: number,
    z0: number,
    z1: number,
    y0: number,
    y1: number,
    shade: Role,
  ): void => {
    const px = sx * x
    if (sx > 0) big.quad([px, y0, z1], [px, y0, z0], [px, y1, z0], [px, y1, z1], shade)
    else big.quad([px, y0, z0], [px, y0, z1], [px, y1, z1], [px, y1, z0], shade)
  }
  /** The same on a forward face. */
  const face = (z: number, x0: number, x1: number, y0: number, y1: number, shade: Role): void => {
    big.quad([x0, y0, z], [x1, y0, z], [x1, y1, z], [x0, y1, z], shade)
  }
  /** And lying on a deck. */
  const deck = (y: number, x0: number, x1: number, z0: number, z1: number, shade: Role): void => {
    big.quad([x0, y, z1], [x1, y, z1], [x1, y, z0], [x0, y, z0], shade)
  }

  // Chassis: one slab of frame in bare alloy, cut narrow enough that the tracks
  // stand outboard of it and keep their own ground line. Everything team
  // coloured on this vehicle stands on grey machinery, which is what keeps a
  // mass this large off the field as one blue block.
  big.box(0, 0.14, 0, 1.18, 0.32, 1.88, SHADE.MetalDark, SHADE.MetalMid)
  for (const sx of [-1, 1]) {
    for (const z of [-0.62, 0, 0.62]) {
      flank(sx, 0.591, z - 0.09, z + 0.09, 0.16, 0.44, SHADE.Metal)
    }
  }

  // The load. A blunt crate with a dark reveal cut round its foot, strapped down
  // its flanks, and corner castings under the eaves for the derrick to lift the
  // walls by.
  big.box(0, 0.46, -0.35, 1.14, 0.56, 1.06, SHADE.Owner)
  for (const sx of [-1, 1]) {
    flank(sx, 0.576, -0.88, 0.18, 0.46, 0.53, SHADE.MetalDark)
    for (const z of [-0.76, -0.35, 0.06]) {
      flank(sx, 0.576, z - 0.05, z + 0.05, 0.53, 1.0, SHADE.MetalDark)
    }
    for (const z of [-0.86, 0.02]) {
      flank(sx, 0.576, z, z + 0.16, 0.86, 1.0, SHADE.Metal)
    }
  }
  // Its forward face is the one large flat the sun reaches, so that is where the
  // bolted work goes: a sunk plate and a row of rivet heads over it.
  face(0.185, -0.45, 0.45, 0.58, 0.94, SHADE.OwnerDark)
  for (const x of [-0.28, 0.28]) {
    face(0.186, x - 0.05, x + 0.05, 0.84, 0.93, SHADE.Metal)
  }
  // A pitched roof, not a lid, on a lipped eave band. Nothing else that drives
  // has one, and it is the cue that turns a crate on a truck into a building on
  // a truck.
  big.box(0, 1.02, -0.35, 1.2, 0.06, 1.12, SHADE.MetalDark)
  big.wedge(0, 1.08, -0.36, 0.96, 0.22, 0.96, 0.15, SHADE.Owner, SHADE.Owner)
  for (const x of [-0.13, 0.13]) {
    deck(1.301, x - 0.04, x + 0.04, -0.66, -0.06, SHADE.MetalDark)
  }

  // The erection derrick: a stub tower at the back of the load with its boom
  // stowed forward over the roof and the hook block hanging past the front of
  // it. The tallest thing on the vehicle, and at the wrong end to be a gun.
  big.box(0, 1.24, -0.54, 0.36, 0.34, 0.36, SHADE.MetalMid, SHADE.Metal)
  big.box(0, 1.44, 0.08, 0.24, 0.14, 1.16, SHADE.Metal)
  big.box(0, 1.3, 0.62, 0.2, 0.14, 0.18, SHADE.MetalDark, SHADE.Accent)

  // The flatbed load: the building's wall panels folded flat and stacked, edged
  // along their long sides so the stack reads as plates rather than as a block.
  big.box(0, 0.46, 0.44, 1.02, 0.24, 0.5, SHADE.OwnerDark, SHADE.OwnerLight)
  for (const sx of [-1, 1]) {
    flank(sx, 0.511, 0.21, 0.67, 0.56, 0.6, SHADE.Metal)
  }
  for (const x of [-0.3, 0.3]) {
    deck(0.701, x - 0.045, x + 0.045, 0.21, 0.67, SHADE.MetalDark)
  }

  // Cab: mid height, wide as the chassis, with a slit rather than a canopy — a
  // driving compartment on a machine like this is a hole cut in armour plate.
  big.box(0, 0.36, 0.85, 1.14, 0.54, 0.34, SHADE.Owner)
  big.box(0, 0.72, 1.0, 1.0, 0.08, 0.12, SHADE.MetalDark)
  face(1.021, -0.42, 0.42, 0.56, 0.7, SHADE.Accent)
  for (const x of [-0.36, 0.36]) {
    face(1.021, x - 0.05, x + 0.05, 0.42, 0.51, SHADE.Metal)
  }

  // Blunt nose plate with a bolted bumper across it.
  big.wedge(0, 0.1, 1.02, 1.22, 0.3, 0.28, 0.1, SHADE.MetalMid, SHADE.Metal)
  for (const x of [-0.34, 0.34]) {
    face(1.161, x - 0.15, x + 0.15, 0.12, 0.28, SHADE.MetalDark)
  }

  // Two short stacks over the engine. They cost twenty triangles and they are
  // what stops the back end reading as a sawn-off box.
  for (const sx of [-1, 1]) {
    big.box(sx * 0.3, 0.46, -0.94, 0.2, 0.3, 0.2, SHADE.MetalDark, SHADE.Accent)
  }

  // The forward jacks, stowed vertical over the tracks. They stand a head above
  // the flatbed, which is the one place this outline steps twice in a row.
  for (const sx of [-1, 1]) {
    big.box(sx * 0.58, 0.3, 0.62, 0.22, 0.62, 0.3, SHADE.MetalMid)
    flank(sx, 0.691, 0.47, 0.77, 0.8, 0.92, SHADE.Metal)
  }

  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)
  return r.build()
}

// ---------------------------------------------------------------------------
// Helios Directorate — structures
// ---------------------------------------------------------------------------

/**
 * The Helios base root: the first thing built, the last thing killed, and the
 * object the camera sits over for most of a match.
 *
 * Three masses — a tall command block, a lower operations wing, a garage annex
 * — set so that no two edges agree and nothing on the model is mirrored. The
 * overall form is deliberately plain, because at 3x3 a base root is read by its
 * bulk; what the size buys is *surface*. This building has the largest
 * horizontal faces in the game and a fixed overhead camera spends the whole
 * match looking down at them, so every deck carries vents, a hatch, stowage, a
 * conduit run or a mast, and no feature has a twin on the far side: an even
 * spread reads as a placeholder the moment the eye finds the axis.
 *
 * The triangle ceiling is the real constraint on that, and it decides which
 * greebles are worth having. Panel seams cost exactly what a vent box costs and
 * only break a face; anything standing proud of the roofline breaks the
 * *outline* as well, which is the detail that survives to gameplay zoom. So the
 * budget goes to things with a profile — stacks, a dish, crates, a rail, a whip
 * — and the flat faces are broken by the small parts standing on them.
 */
const commandYard: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 2.95, 2.95, 2, 2)
  hull.box(0, 0, 0, 2.7, 0.3, 2.7, SHADE.MetalDark, SHADE.Metal)

  // The three masses, the tall one on a dark base course so a painted block
  // never fuses with the deck it stands on.
  hull.box(-0.5, 0.3, -0.4, 1.7, 0.12, 1.9, SHADE.MetalDark)
  hull.box(-0.5, 0.4, -0.4, 1.6, 0.92, 1.8, SHADE.Owner)
  hull.box(0.78, 0.32, 0.2, 1.05, 0.56, 2.2, SHADE.Owner)
  hull.box(-0.55, 0.3, 1.0, 1.1, 0.44, 0.7, SHADE.MetalMid, SHADE.Metal)

  // --- command block roof, at 1.32 -------------------------------------------
  // The cab: the one part with people in it, so it gets the glass and the
  // beacon, and it sits off in a corner rather than on centre.
  hull.box(-0.82, 1.32, 0.02, 0.72, 0.3, 0.62, SHADE.Owner, SHADE.OwnerLight)
  hull.box(-0.82, 1.46, 0.32, 0.58, 0.12, 0.05, SHADE.Glass)
  hull.box(-0.82, 1.62, 0.02, 0.8, 0.05, 0.7, SHADE.Metal)
  hull.box(-1.05, 1.67, 0.2, 0.09, 0.1, 0.09, SHADE.Accent)

  // Vent bank along the back edge, uneven in width and height so four boxes in
  // a row read as equipment rather than as a repeat.
  for (const [x, w, h] of [
    [-1.1, 0.22, 0.14],
    [-0.84, 0.18, 0.09],
    [-0.6, 0.16, 0.19],
    [-0.3, 0.3, 0.12],
  ]) {
    hull.box(x, 1.32, -1.1, w, h, 0.26, SHADE.MetalMid, SHADE.Metal)
  }

  // Two stacks of different height, a bolted hatch, and a dish on a short mast
  // at the corner. Everything here stands above the roofline on purpose.
  hull.box(0.02, 1.32, -0.88, 0.2, 0.36, 0.2, SHADE.MetalDark)
  hull.box(0.02, 1.68, -0.88, 0.28, 0.06, 0.28, SHADE.Metal)
  hull.box(0.04, 1.32, -0.58, 0.14, 0.24, 0.14, SHADE.MetalDark, SHADE.Accent)
  hull.box(-0.3, 1.32, -0.44, 0.34, 0.06, 0.3, SHADE.MetalDark, SHADE.Metal)
  hull.box(0.14, 1.32, -1.2, 0.1, 0.32, 0.1, SHADE.Metal)
  hull.cylinder(0.14, 1.6, -1.2, 0.09, 0.1, 6, SHADE.Metal, SHADE.Accent, 0.24)

  // A conduit run clamped along the front edge, a sensor blister, and stowage
  // lashed down where the roof still had room.
  hull.box(-0.5, 1.34, 0.4, 1.4, 0.08, 0.1, SHADE.MetalDark)
  hull.box(-1.0, 1.32, 0.4, 0.12, 0.11, 0.16, SHADE.Metal)
  hull.box(0.12, 1.32, 0.16, 0.24, 0.12, 0.24, SHADE.Metal, SHADE.Accent)
  hull.box(0.13, 1.32, -0.12, 0.24, 0.2, 0.28, SHADE.MetalMid, SHADE.Metal)
  hull.box(0.16, 1.52, -0.1, 0.18, 0.13, 0.2, SHADE.MetalDark, SHADE.Metal)

  // --- command block walls ---------------------------------------------------
  // All of it on the two faces the fixed camera can see. A rib on the back wall
  // of a building that never turns is geometry nobody will ever be shown, and at
  // this triangle ceiling that is a vent or a crate given away for nothing.
  hull.box(-1.15, 0.75, 0.52, 0.07, 0.57, 0.1, SHADE.OwnerDark)
  hull.box(0.1, 0.4, 0.52, 0.07, 0.92, 0.1, SHADE.OwnerDark)
  hull.box(-0.9, 0.95, 0.52, 0.5, 0.14, 0.06, SHADE.Glass)
  hull.box(-0.32, 1.06, 0.52, 0.22, 0.1, 0.06, SHADE.Glass)
  hull.box(-0.62, 0.78, 0.53, 0.26, 0.3, 0.08, SHADE.MetalDark, SHADE.Metal)
  // A riser up the exposed shoulder of the block, turning over onto the roof.
  hull.box(0.31, 0.9, -1.05, 0.08, 0.42, 0.08, SHADE.Metal)
  hull.box(0.2, 1.24, -1.05, 0.3, 0.08, 0.08, SHADE.Metal)

  // --- operations wing, deck at 0.88 -----------------------------------------
  for (const [z, h] of [
    [-0.6, 0.12],
    [-0.28, 0.17],
    [0.04, 0.1],
  ]) {
    hull.box(0.62, 0.88, z, 0.5, h, 0.16, SHADE.MetalMid, SHADE.Metal)
  }
  hull.box(0.95, 0.88, 0.6, 0.4, 0.26, 0.38, SHADE.MetalMid, SHADE.Metal)
  hull.box(0.98, 1.14, 0.64, 0.3, 0.18, 0.28, SHADE.MetalDark, SHADE.Metal)
  hull.box(0.95, 0.88, 1.1, 0.34, 0.18, 0.3, SHADE.MetalDark, SHADE.Metal)
  // A rail along the far end of the deck: the one greeble up here that puts a
  // gap of sky inside the building's own outline.
  for (const x of [0.34, 1.2]) {
    hull.box(x, 0.88, 1.16, 0.07, 0.26, 0.07, SHADE.Metal)
  }
  hull.box(0.78, 1.08, 1.16, 0.95, 0.05, 0.05, SHADE.Metal)
  hull.cylinder(0.5, 0.88, 0.98, 0.18, 0.3, 6, SHADE.Metal, SHADE.MetalDark)
  hull.box(0.55, 0.88, 0.68, 0.22, 0.16, 0.2, SHADE.MetalDark, SHADE.Metal)

  // Wing walls: conduit down the outboard face, a cabinet, a strip light, and
  // the vehicle door at the near end with a frame around it.
  hull.box(1.32, 0.32, -0.55, 0.06, 0.56, 0.12, SHADE.OwnerDark)
  hull.box(1.33, 0.48, 0.55, 0.06, 0.24, 0.28, SHADE.MetalDark, SHADE.Metal)
  hull.box(1.33, 0.66, -0.2, 0.05, 0.08, 0.5, SHADE.Light)
  hull.box(0.78, 0.32, 1.32, 0.62, 0.4, 0.06, SHADE.Accent)
  hull.box(0.4, 0.32, 1.33, 0.1, 0.48, 0.08, SHADE.Metal)
  hull.box(1.16, 0.32, 1.33, 0.1, 0.48, 0.08, SHADE.Metal)

  // --- garage annex and yard -------------------------------------------------
  hull.box(-0.85, 0.74, 0.86, 0.3, 0.16, 0.3, SHADE.MetalDark, SHADE.Metal)
  hull.box(-1.02, 0.74, 1.14, 0.26, 0.2, 0.24, SHADE.MetalMid, SHADE.Metal)
  hull.box(-0.55, 0.3, 1.36, 0.7, 0.4, 0.06, SHADE.Accent)

  // The gap between annex and wing is the only open ground on the pad, so it
  // takes the plant that has to stand outside.
  hull.box(0.03, 0.3, 1.0, 0.38, 0.44, 0.5, SHADE.MetalMid, SHADE.Metal)
  hull.box(0.03, 0.5, 1.26, 0.3, 0.16, 0.04, SHADE.Accent)

  // Masts, on the part that never turns. Two whips of different height off one
  // corner: the tallest thing on the building is a thread of metal, which is
  // what keeps a wide structure from reading as a slab.
  const mast = r.part(PART.Accent)
  mast.box(0.22, 1.32, -0.62, 0.05, 0.8, 0.05, SHADE.Metal)
  mast.box(0.22, 2.12, -0.62, 0.08, 0.07, 0.08, SHADE.Accent)
  mast.box(0.3, 0.88, -0.8, 0.04, 0.62, 0.04, SHADE.MetalDark)
  return r.build()
}

const solarArray: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 1.95, 1.5, 3, 2)

  // Authored at twice size and merged down: at final scale the pilasters, the
  // window row, the handrail and the walkway grating would every one of them
  // have been rounded away as not worth the arithmetic.
  const big = new MeshBuilder()

  // The machine hall is a long low wall along the back edge and nothing else.
  // Everything in front of it is deck, so in plan the building is a bar of owner
  // colour with a wider field of glass hung off it — front to back it is not
  // symmetrical, which no other Helios structure can say.
  big.box(0, 0.16, -1.06, 4.52, 0.2, 0.94, SHADE.MetalDark, SHADE.Metal)
  big.box(0, 0.36, -1.06, 4.32, 1.48, 0.82, SHADE.Owner)
  big.box(0, 1.84, -0.72, 4.32, 0.08, 0.14, SHADE.Metal)
  // Pilasters, a recessed fascia and a lit run of windows, all in the one band
  // of wall the deck does not hide. Detail below that line is geometry nobody
  // is ever shown, and the dark fascia is what keeps the blue coping from
  // fusing with the glass in front of it.
  for (const x of [-1.98, 0, 1.98]) {
    big.box(x, 1.54, -0.6, 0.16, 0.3, 0.16, SHADE.MetalDark)
  }
  big.box(0, 1.54, -0.62, 4.32, 0.3, 0.08, SHADE.MetalDark)
  big.box(0.98, 1.6, -0.6, 1.4, 0.16, 0.08, SHADE.Accent)
  // Transformer drums on the hall roof: the one round shape on the building,
  // and three verticals where a wide flat thing most needs them.
  for (const x of [-0.62, -0.02, 0.58]) {
    big.cylinder(x, 1.84, -1.06, 0.19, 0.46, 6, SHADE.MetalMid, SHADE.Metal)
  }
  // Two stacks of unequal height on the hall roof, capped and bolted.
  for (const [x, h] of [
    [-1.44, 0.66],
    [1.26, 0.42],
  ]) {
    big.box(x, 1.84, -1.06, 0.42, h, 0.44, SHADE.MetalMid, SHADE.Metal)
    big.box(x, 1.84 + h, -1.06, 0.52, 0.1, 0.54, SHADE.MetalDark)
  }
  big.box(-1.44, 2.6, -1.06, 0.16, 0.12, 0.16, SHADE.Accent)

  // Two blunt legs at the front corners hold the deck up, braced across. The
  // span between them is open, so the deck sits above the pad, not on it.
  for (const sx of [-1, 1]) {
    big.wedge(sx * 1.9, 0.16, 1.24, 0.64, 0.14, 0.64, 0.07, SHADE.MetalDark, SHADE.Metal)
    big.box(sx * 1.9, 0.3, 1.24, 0.48, 0.74, 0.48, SHADE.Owner)
  }
  big.box(0, 0.72, 1.24, 3.4, 0.14, 0.22, SHADE.MetalDark)
  // A ladder up to the deck, thin enough to show sky either side of it.
  big.box(-0.72, 0.16, 1.54, 0.24, 0.9, 0.1, SHADE.Metal)

  // Deck: one slab of plate cantilevered off the hall, fasciaed in owner colour
  // on the three edges that are not wall, with a handrail along the front. The
  // gaps under that rail are the only part of the outline with sky in them.
  big.box(0, 1.04, 0.45, 4.4, 0.16, 2.2, SHADE.MetalDark, SHADE.MetalMid)
  big.box(0, 1.04, 1.52, 4.46, 0.2, 0.14, SHADE.Owner, SHADE.OwnerLight)
  for (const sx of [-1, 1]) {
    big.box(sx * 2.16, 1.04, 0.4, 0.14, 0.2, 2.1, SHADE.Owner, SHADE.OwnerLight)
  }
  for (const x of [-2.02, -0.68, 0.68, 2.02]) {
    big.box(x, 1.2, 1.5, 0.14, 0.36, 0.14, SHADE.Metal)
  }
  big.box(0, 1.48, 1.5, 4.2, 0.08, 0.1, SHADE.Metal)

  // The collector: one broad plate over most of the deck with a narrow tier a
  // step higher behind it, so the field tilts into the hall. Both are scribed
  // into panels rather than cut into separate blocks — a field chopped into six
  // pieces stops reading as one surface.
  big.box(0, 1.2, 0.62, 4.16, 0.16, 1.3, SHADE.MetalDark, SHADE.Glass)
  big.seams(0, 1.36, 0.62, 4.16, 1.3, 4, 2)
  big.box(0, 1.2, -0.32, 4.16, 0.34, 0.56, SHADE.MetalDark, SHADE.Glass)
  big.seams(0, 1.54, -0.32, 4.16, 0.56, 4, 1)
  // Deck furniture, all of it up where the light lands.
  big.box(-1.86, 1.36, 1.0, 0.32, 0.24, 0.32, SHADE.MetalMid, SHADE.Metal)
  big.box(-1.86, 1.6, 1.0, 0.14, 0.1, 0.14, SHADE.Accent)

  // The service catwalk crossing the array is grating, run long so its ribs
  // come back finer than they could be drawn directly. It is the one place on
  // the building where a dark ribbed strip lies straight across lit glass.
  const grate = new MeshBuilder()
  grate.treadBand(0, 0, 1.3, 0.22, SHADE.MetalDark)
  big.merge(grate, -0.02, 1.36, 0.62, 1)

  hull.merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const regolithRefinery: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 2.95, 1.95, 2, 2)

  const big = new MeshBuilder()

  // --- plinth ----------------------------------------------------------------
  // Two courses, so the deck lands at 0.53 and the ground clutter cannot poke up
  // through the yard, and so a base this deep never reads as one slab.
  big.box(0, 0, 0.01, 5.56, 0.62, 3.62, SHADE.MetalDark)
  big.box(0, 0.62, 0.01, 5.46, 0.44, 3.52, SHADE.MetalMid, SHADE.Metal)
  for (const x of [-2.15, -0.95, 0.35, 1.85]) {
    big.box(x, 0.14, 1.87, 0.62, 0.92, 0.26, SHADE.MetalMid, SHADE.Metal)
  }

  // --- hardstand -------------------------------------------------------------
  // The parking spot is painted on: strips of accent for two triangles each. At
  // this palette resolution a colour step on a deck needs no thickness.
  for (const z of [0.42, 1.06]) {
    big.quad(
      [-1.25, 1.07, z + 0.18],
      [0.3, 1.07, z + 0.18],
      [0.3, 1.07, z],
      [-1.25, 1.07, z],
      SHADE.Accent,
    )
  }
  big.quad(
    [-2.62, 1.07, 0.34],
    [-1.5, 1.07, 0.34],
    [-1.5, 1.07, 0.12],
    [-2.62, 1.07, 0.12],
    SHADE.Accent,
  )

  // The portal: box-section legs on spread base flanges, a header across them,
  // and the tipper hood slung under it at clearance for a loaded hopper.
  for (const z of [0.25, 1.55]) {
    big.box(-0.45, 1.06, z, 0.52, 0.16, 0.52, SHADE.MetalDark)
    big.box(-0.45, 1.06, z, 0.34, 1.5, 0.34, SHADE.MetalMid, SHADE.Metal)
  }
  big.box(-0.45, 2.56, 0.9, 0.38, 0.34, 1.72, SHADE.MetalDark, SHADE.Metal)
  big.box(-0.45, 2.04, 0.9, 0.92, 0.1, 1.2, SHADE.Metal)
  big.box(-0.45, 2.1, 0.9, 0.72, 0.46, 1.0, SHADE.MetalDark, SHADE.Metal)
  big.box(-0.45, 2.9, 1.55, 0.2, 0.2, 0.2, SHADE.Accent)
  // Bollards either side of the mouth: the near edge of the yard is the last
  // place on the model that still reads at gameplay zoom.
  for (const x of [-1.28, 0.24]) {
    big.box(x, 1.06, 1.58, 0.26, 0.42, 0.26, SHADE.Owner, SHADE.OwnerLight)
  }
  // The yardmaster's cabin, in the far corner of the apron. It is the only
  // small occupied thing on a building otherwise made of tankage.
  big.box(-2.2, 1.06, 1.2, 0.95, 0.7, 0.95, SHADE.Owner)
  big.box(-2.2, 1.42, 1.7, 0.62, 0.2, 0.1, SHADE.Accent)
  big.box(-2.2, 1.76, 1.2, 1.07, 0.12, 1.07, SHADE.MetalDark, SHADE.Metal)

  // --- process shed ----------------------------------------------------------
  // Full length of the plinth and barely taller than the gantry: this is what
  // makes the footprint read long rather than square.
  big.box(-1.065, 1.06, -0.925, 3.33, 0.9, 1.65, SHADE.Owner)
  big.box(-1.065, 1.96, -0.925, 3.43, 0.16, 1.75, SHADE.MetalDark, SHADE.Owner)
  // The intake the harvester noses into, on the wall the camera can see and
  // framed in heavy section so it reads as an opening rather than as paint.
  big.box(-0.55, 1.2, -0.05, 1.2, 0.72, 0.16, SHADE.Accent)
  for (const x of [-1.22, 0.12]) {
    big.box(x, 1.06, -0.03, 0.24, 0.9, 0.22, SHADE.Metal)
  }
  big.box(-0.55, 1.9, -0.03, 1.74, 0.2, 0.22, SHADE.Metal)
  for (const x of [-2.45, -1.85, -1.25]) {
    big.box(x, 1.06, -0.03, 0.26, 0.9, 0.2, SHADE.OwnerDark)
  }
  big.box(-2.2, 2.12, -1.3, 0.36, 0.64, 0.36, SHADE.MetalDark)
  big.box(-2.2, 2.76, -1.3, 0.46, 0.12, 0.46, SHADE.Metal)
  big.box(-1.45, 2.12, -0.6, 0.44, 0.22, 0.6, SHADE.MetalMid, SHADE.Metal)
  big.box(-0.5, 2.12, -0.55, 0.5, 0.3, 0.5, SHADE.MetalMid, SHADE.Metal)

  // --- storage bins ----------------------------------------------------------
  // The mass of the building. Two of them, deliberately unequal, because a pair
  // of matched towers reads as one wide block and a big one beside a small one
  // reads as tankage.
  big.box(1.32, 1.06, -0.9, 1.28, 0.28, 1.78, SHADE.MetalDark, SHADE.Metal)
  big.box(1.32, 1.34, -0.9, 1.2, 1.38, 1.7, SHADE.Owner)
  big.wedge(1.32, 2.72, -0.9, 1.2, 0.34, 1.7, 0.3, SHADE.MetalMid, SHADE.Metal)
  big.box(1.32, 3.06, -0.9, 0.7, 0.14, 1.15, SHADE.MetalDark, SHADE.Metal)
  for (const x of [0.9, 1.32, 1.74]) {
    big.box(x, 1.34, -0.02, 0.2, 1.38, 0.14, SHADE.OwnerDark)
  }

  big.box(2.37, 1.06, -0.9, 0.78, 0.28, 1.78, SHADE.MetalDark, SHADE.Metal)
  big.box(2.37, 1.34, -0.9, 0.7, 1.3, 1.7, SHADE.Owner)
  big.wedge(2.37, 2.64, -0.9, 0.7, 0.28, 1.7, 0.22, SHADE.MetalMid, SHADE.Metal)
  big.box(2.37, 2.92, -0.9, 0.4, 0.14, 1.0, SHADE.MetalDark, SHADE.Metal)
  big.box(2.37, 1.34, -0.02, 0.22, 1.3, 0.14, SHADE.OwnerDark)

  // A riser standing in the gap between the two, and its relief valve: the gap
  // is the whole reason there are two bins, so something has to live in it.
  big.box(1.99, 1.06, -0.35, 0.22, 1.9, 0.22, SHADE.Metal)
  big.box(1.99, 2.44, -0.2, 0.3, 0.24, 0.16, SHADE.Accent)

  // --- transfer bridge -------------------------------------------------------
  // Shed roof to the big bin, over open deck. It is what makes four masses one
  // machine instead of four buildings sharing a pad.
  big.box(0.32, 2.32, -0.9, 1.4, 0.26, 0.62, SHADE.MetalMid, SHADE.Metal)
  big.box(1.02, 2.22, -0.9, 0.46, 0.56, 0.72, SHADE.MetalDark, SHADE.Metal)

  // --- process tanks ---------------------------------------------------------
  // Squat, dark-sided, lit on the cap. Round tops against everything else being
  // a slab, and low enough that they never stand in front of the bins.
  big.box(1.72, 1.06, 0.66, 1.9, 0.28, 1.26, SHADE.Owner)
  for (const [x, h] of [
    [0.98, 0.72],
    [1.72, 0.56],
    [2.46, 0.84],
  ]) {
    big.cylinder(x, 1.34, 0.62, 0.38, h, 6, SHADE.MetalDark, SHADE.Metal)
  }
  big.quad([0.8, 1.07, 1.72], [2.64, 1.07, 1.72], [2.64, 1.07, 1.5], [0.8, 1.07, 1.5], SHADE.Accent)

  hull.merge(big, 0, 0, 0, 0.500)
  return r.build()
}

const habitatBlock: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 1.95, 1.95, 3, 3)

  const big = new MeshBuilder()

  // The podium: one thick slab with a wider footing course under it and welded
  // buttresses standing off the two walls the camera is ever shown.
  big.box(0, 0, 0, 3.5, 0.16, 3.5, SHADE.MetalDark)
  big.box(0, 0.16, 0, 3.4, 0.56, 3.4, SHADE.MetalMid, SHADE.Metal)
  for (const x of [-1.0, 0.5]) {
    big.box(x, 0, 1.76, 0.34, 0.72, 0.16, SHADE.MetalDark, SHADE.Metal)
  }
  for (const z of [-1.0, 0.5]) {
    big.box(1.76, 0, z, 0.16, 0.72, 0.34, SHADE.MetalDark, SHADE.Metal)
  }
  big.box(0, 0.5, 1.74, 3.4, 0.1, 0.12, SHADE.Metal)
  big.box(1.74, 0.5, 0, 0.12, 0.1, 3.4, SHADE.Metal)

  // Each can is a four-step barrel section, hooped with ribs that stand proud of
  // the ridge, and closed at the near end by a heavy leaf in a welded frame with
  // a lit transom over it. The doors are the domestic note: one per module, at a
  // person's scale, on a building whose neighbours have vehicle bays.
  const canister = (cx: number, z0: number, z1: number, hoops: number): void => {
    const cz = (z0 + z1) / 2
    const len = z1 - z0
    big.box(cx, 0.72, cz, 1.0, 0.4, len, SHADE.Owner)
    big.box(cx, 1.12, cz, 0.88, 0.3, len, SHADE.Owner)
    big.box(cx, 1.42, cz, 0.68, 0.24, len, SHADE.Owner)
    big.box(cx, 1.66, cz, 0.42, 0.2, len, SHADE.Owner, SHADE.OwnerLight)
    for (let i = 0; i < hoops; i++) {
      const z = z0 + 0.4 + ((len - 0.8) * i) / Math.max(1, hoops - 1)
      big.box(cx, 1.54, z, 0.54, 0.38, 0.14, SHADE.MetalDark)
    }
    big.box(cx, 0.72, z1 + 0.03, 0.64, 0.94, 0.12, SHADE.Metal, SHADE.MetalDark)
    big.box(cx, 0.74, z1 + 0.1, 0.42, 0.62, 0.12, SHADE.MetalDark, SHADE.Metal)
    big.box(cx, 1.44, z1 + 0.1, 0.46, 0.14, 0.12, SHADE.Accent)
  }
  canister(-1.12, -1.6, 1.5, 3)
  canister(0, -1.6, 0.72, 2)
  canister(1.12, -1.6, 0.0, 2)

  // Windows, on the one long wall the fixed camera actually gets. Small, evenly
  // spaced, capped by their own lintels, and the only lights in the base that are
  // on because somebody is behind them.
  for (const z of [-1.3, -0.78, -0.26]) {
    big.box(1.62, 0.84, z, 0.12, 0.3, 0.34, SHADE.Accent, SHADE.MetalDark)
  }

  // The airlock: a squat drum on the frontage under a lamp, with the inner door
  // lit and a step down off the podium, so there is an obvious way in on foot.
  big.cylinder(0, 0.72, 1.18, 0.44, 0.9, 8, SHADE.MetalMid, SHADE.Metal)
  big.box(0, 1.6, 1.18, 0.44, 0.2, 0.44, SHADE.MetalDark, SHADE.Accent)
  big.box(0, 0.8, 1.62, 0.5, 0.62, 0.14, SHADE.Accent, SHADE.MetalDark)
  big.box(0, 0, 1.82, 1.1, 0.5, 0.24, SHADE.MetalMid, SHADE.Metal)

  // The greenhouse fills the corner the short canister left open: a glazed bed
  // in a steel curb, mullioned into panes so the one bright surface on the roof
  // reads as glass rather than as a painted square.
  big.box(1.02, 0.72, 0.92, 1.2, 0.16, 1.32, SHADE.MetalDark, SHADE.MetalMid)
  big.box(1.02, 0.88, 0.92, 1.08, 0.32, 1.2, SHADE.Accent)
  for (const x of [-0.18, 0.18]) {
    big.box(1.02 + x, 1.18, 0.92, 0.1, 0.06, 1.2, SHADE.MetalDark)
  }
  for (const z of [-0.2, 0.2]) {
    big.box(1.02, 1.18, 0.92 + z, 1.08, 0.06, 0.1, SHADE.MetalDark)
  }
  big.box(1.54, 0.72, 1.46, 0.16, 0.7, 0.16, SHADE.Metal)

  // Plant on the roof, because the alternative is three unbroken ridges: a water
  // tank on the long can and a heat exchanger straddling the other two.
  big.cylinder(-1.12, 1.78, -0.9, 0.3, 0.46, 6, SHADE.MetalMid, SHADE.Metal)
  big.box(0.56, 1.7, -1.2, 0.54, 0.4, 0.54, SHADE.MetalDark, SHADE.Metal)

  hull.merge(big, 0, 0, 0, 0.5)

  // The one thread of metal above the roofline, on the part that never turns.
  // Aliased to the hull rather than its own part. `PART.Accent` never rotates and
  // `PART.Hull` rotates with the unit's heading — on a structure, whose heading never
  // changes, those are the same thing, and a second part costs a draw call per owner
  // for no behaviour. The roster budget is real: it went to 202 of 200 without this.
  const mast = hull
  const pole = new MeshBuilder()
  pole.box(1.12, 1.6, -1.3, 0.18, 1.62, 0.18, SHADE.Metal)
  pole.box(1.12, 3.22, -1.3, 0.22, 0.18, 0.22, SHADE.Accent)
  mast.merge(pole, 0, 0, 0, 0.5)
  return r.build()
}

const foundry: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  const big = new MeshBuilder()
  type Shade = (typeof SHADE)[keyof typeof SHADE]

  // Deck paint and rivet heads go down as single quads. At 22 px per unit the
  // colour step is the whole of what reads, and a box would cost five times the
  // triangles to add a thickness nobody can resolve.
  const paint = (x0: number, x1: number, z0: number, z1: number, y: number, shade: Shade) =>
    big.quad([x0, y, z1], [x1, y, z1], [x1, y, z0], [x0, y, z0], shade)
  const front = (x: number, y0: number, y1: number, z0: number, z1: number, shade: Shade) =>
    big.quad([x, y0, z1], [x, y0, z0], [x, y1, z0], [x, y1, z1], shade)

  // --- hardstand -------------------------------------------------------------
  big.wedge(0, 0, 0, 5.9, 0.3, 5.9, 0.22, SHADE.MetalDark)
  big.box(0, 0.3, 0, 5.62, 0.44, 5.62, SHADE.MetalDark, SHADE.Metal)
  big.box(0, 0.74, 0, 5.34, 0.32, 5.34, SHADE.MetalMid, SHADE.Metal)
  paint(0.24, 2.66, -1.06, 1.06, 1.08, SHADE.MetalDark)
  paint(0.24, 2.66, -1.34, -1.18, 1.09, SHADE.Accent)
  paint(0.24, 2.66, 1.18, 1.34, 1.09, SHADE.Accent)
  paint(0.28, 0.74, -1.4, 1.4, 1.09, SHADE.Accent)
  for (const sz of [-1, 1]) {
    big.box(2.6, 1.06, sz * 2.6, 0.44, 0.22, 0.44, SHADE.Owner, SHADE.OwnerLight)
  }
  big.box(-2.6, 1.06, 2.6, 0.44, 0.22, 0.44, SHADE.Owner, SHADE.OwnerLight)

  // --- hall ------------------------------------------------------------------
  big.box(-1.9, 1.06, 0, 2.1, 0.28, 5.1, SHADE.MetalDark, SHADE.Metal)
  big.box(-1.9, 1.34, 0, 2.0, 1.76, 5.0, SHADE.Owner)

  // --- the opening -----------------------------------------------------------
  // No lintel and no gable: the door runs the full height of the wall, so the
  // front of this building is two piers and a hole. Anything stacked above the
  // head beam was read as the top of the building and the opening as a slot
  // under it, which is the failure the whole massing exists to avoid.
  for (const sz of [-1, 1]) {
    big.box(-0.35, 1.06, sz * 1.95, 1.14, 0.28, 1.14, SHADE.MetalDark, SHADE.Metal)
    big.box(-0.35, 1.34, sz * 1.95, 1.1, 1.76, 1.1, SHADE.Owner)
    big.box(0.24, 1.34, sz * 1.62, 0.16, 1.76, 0.24, SHADE.Metal)
    front(0.21, 1.5, 1.94, sz * 1.46, sz * 2.44, SHADE.Accent)
    front(0.21, 2.4, 2.86, sz * 1.46, sz * 2.44, SHADE.MetalDark)
  }
  // Head beam under the eaves, with the hazard band on it.
  big.box(0.1, 2.8, 0, 0.44, 0.3, 3.36, SHADE.Metal)
  front(0.33, 2.86, 3.04, -1.6, 1.6, SHADE.Accent)
  // The bay is only 0.28 deep in final units, and that number is the whole of
  // what makes it read. The camera looks down a 1:1:1 diagonal, so a recess
  // hides one unit of its own back wall for every unit of depth: cut a metre
  // into this wall and the eave shadows the entire interior, and the door comes
  // back as a black slot with nothing legible inside it.
  big.box(-0.43, 1.06, 0, 0.16, 2.04, 2.8, SHADE.MetalDark)
  big.box(-0.08, 1.06, 0, 0.56, 0.12, 2.8, SHADE.MetalDark, SHADE.MetalMid)
  front(-0.34, 1.72, 2.34, -1.3, 1.3, SHADE.Accent)
  front(-0.34, 2.62, 2.78, -1.3, 1.3, SHADE.MetalMid)
  // The side wall of the reveal is the other surface the camera gets a clean
  // look at, so the arc light carries round onto it.
  big.quad([-0.34, 1.72, -1.38], [0.18, 1.72, -1.38], [0.18, 2.34, -1.38], [-0.34, 2.34, -1.38], SHADE.Accent)

  // --- roof ------------------------------------------------------------------
  // One slab over hall and piers together, so the head of the door is the
  // building's own eaves rather than a beam with more building on top of it.
  big.box(-1.35, 3.1, 0, 3.26, 0.3, 5.14, SHADE.MetalMid, SHADE.Metal)
  front(0.3, 3.16, 3.34, -2.5, 2.5, SHADE.Owner)
  for (const z of [-1.9, -0.3, 1.3, 2.2]) {
    big.box(-1.5, 3.4, z, 2.9, 0.16, 0.26, SHADE.MetalDark)
  }
  paint(-1.36, -1.04, -2.4, 2.4, 3.43, SHADE.MetalDark)
  paint(-2.74, -1.52, 1.62, 2.06, 3.43, SHADE.MetalMid)
  paint(-2.5, -2.06, 0.6, 1.1, 3.43, SHADE.Metal)
  paint(-2.5, -2.06, -0.2, 0.3, 3.43, SHADE.MetalMid)
  big.box(-1.95, 3.4, -1.6, 1.3, 0.62, 1.7, SHADE.MetalMid, SHADE.Metal)
  big.box(-1.95, 4.02, -1.6, 1.42, 0.12, 1.82, SHADE.Metal)
  front(-1.29, 3.54, 3.9, -2.3, -0.9, SHADE.Accent)
  big.box(-2.45, 3.4, -2.05, 0.52, 0.9, 0.52, SHADE.MetalDark, SHADE.Metal)
  big.box(-2.45, 4.3, -2.05, 0.66, 0.14, 0.66, SHADE.Metal)
  big.box(-1.55, 3.4, 1.85, 0.86, 0.44, 0.96, SHADE.MetalMid, SHADE.Metal)
  paint(-1.88, -1.22, 1.5, 2.2, 3.85, SHADE.Accent)

  // --- the flank the camera actually gets ------------------------------------
  for (const x of [-2.6, -1.9, -1.2]) {
    big.box(x, 1.34, 2.56, 0.3, 1.76, 0.2, SHADE.OwnerDark)
  }
  big.box(-1.9, 2.34, 2.56, 1.94, 0.18, 0.16, SHADE.Metal)
  big.box(-2.25, 1.55, 2.6, 0.56, 0.55, 0.14, SHADE.MetalDark, SHADE.Metal)
  big.box(-2.25, 1.68, 2.64, 0.44, 0.12, 0.08, SHADE.Accent)
  big.box(-1.55, 1.34, 2.58, 0.46, 0.92, 0.16, SHADE.MetalDark, SHADE.Metal)
  big.box(-1.55, 2.56, 2.6, 0.22, 0.14, 0.12, SHADE.Accent)

  // --- gantry ----------------------------------------------------------------
  // Four posts, two cross girders, two runway beams, in the light neutral so the
  // frame stays a bright thing standing in front of a dark one. There is sky
  // through the middle of it, which is the one kind of detail that survives to
  // gameplay zoom.
  //
  // It stands over the -z half of the yard rather than across all of it. On
  // centre, its near legs and cross girder sweep diagonally through the door on
  // screen; held to one side, hall and frame read as two separate objects and
  // the lane out of the bay stays empty.
  for (const x of [1.1, 2.5]) {
    for (const z of [-2.3, -0.7]) {
      big.box(x, 1.06, z, 0.32, 2.6, 0.32, SHADE.Metal)
    }
    big.box(x, 4.02, -1.5, 0.36, 0.3, 2.28, SHADE.Metal)
    big.box(x, 1.76, -0.7, 0.38, 0.22, 0.38, SHADE.Accent)
  }
  for (const z of [-2.3, -0.7]) {
    big.box(1.8, 3.66, z, 1.84, 0.36, 0.34, SHADE.MetalDark, SHADE.Metal)
  }
  big.box(1.8, 3.62, -1.5, 1.56, 0.4, 0.84, SHADE.Owner, SHADE.OwnerLight)
  big.box(1.8, 3.32, -1.5, 0.7, 0.3, 0.62, SHADE.MetalDark, SHADE.Metal)
  big.box(1.8, 2.3, -1.5, 0.14, 1.02, 0.14, SHADE.Metal)
  big.box(1.8, 1.94, -1.5, 0.42, 0.36, 0.42, SHADE.MetalDark, SHADE.Accent)

  // --- the thing on the hardstand --------------------------------------------
  // A hull standing on the lane with one track hung on it, on the near side
  // where the camera gets the track's flank. That track is why the model is
  // authored at twice size: `treadBand` counts cleats off its own length, so a
  // band drawn at 1.4 and halved comes back with twelve ribs on a run six pixels
  // long, which is finer than it could be built any other way.
  const track = new MeshBuilder()
  track.treadBand(2.28, 1.15, 1.4, 0.34, SHADE.MetalDark)
  big.merge(track, 0, 1.06, 0, 1)
  big.box(1.7, 1.14, 1.15, 1.16, 0.44, 1.4, SHADE.Owner)
  big.box(1.7, 1.58, 1.15, 0.9, 0.1, 1.06, SHADE.MetalDark, SHADE.Metal)
  // Plate stock stacked on the far corner of the yard, where the crane can
  // reach it and nothing else was standing.
  big.box(2.36, 1.06, -0.3, 0.72, 0.22, 1.4, SHADE.MetalDark, SHADE.Metal)
  big.box(2.32, 1.28, -0.3, 0.6, 0.18, 1.24, SHADE.MetalMid, SHADE.Metal)

  hull.merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const commsMast: Recipe = () => {
  const r = new RecipeBuilder()
  const big = new MeshBuilder()

  // --- footing ---------------------------------------------------------------
  // A machine block with a spur on each face rather than a slab: the corners are
  // missing, which is the cheapest way to keep a small pad from reading as one
  // more layered square.
  big.wedge(0, -0.2, 0, 1.9, 0.24, 1.9, 0.16, SHADE.MetalDark)
  big.box(0, 0, 0, 1.72, 1.06, 1.72, SHADE.MetalMid, SHADE.Metal)
  for (const [cx, cz, w, d] of [
    [1.02, 0, 0.44, 1.3],
    [-1.02, 0, 0.44, 1.3],
    [0, 1.02, 1.3, 0.44],
    [0, -1.02, 1.3, 0.44],
  ]) {
    big.box(cx, 0, cz, w, 1.06, d, SHADE.MetalMid, SHADE.Metal)
    big.box(cx * 1.08, 1.06, cz * 1.08, 0.32, 0.16, 0.32, SHADE.Owner, SHADE.OwnerLight)
  }
  // A dark reveal under the upper course, so two stacked blocks read as two.
  big.box(0, 1.06, 0, 1.62, 0.1, 1.62, SHADE.MetalDark)
  big.box(0, 1.14, 0, 1.5, 0.2, 1.5, SHADE.MetalMid, SHADE.Metal)
  // Warning stripes on the two spur faces the camera is on.
  big.box(0, 0.5, 1.26, 0.86, 0.18, 0.06, SHADE.Accent)
  big.box(1.26, 0.5, 0, 0.06, 0.18, 0.86, SHADE.Accent)

  // --- shaft -----------------------------------------------------------------
  // One welded box section, not a stack of tapering blocks, and left as bare
  // painted plate between its flanges: angle iron up the corners threw the whole
  // cheek into its own shadow and the column went black.
  big.box(0, 1.34, 0, 1.1, 2.12, 1.1, SHADE.Owner)
  for (const y of [1.3, 2.44]) {
    big.box(0, y, 0, 1.36, 0.16, 1.36, SHADE.MetalMid, SHADE.Metal)
    // Rivet heads: a tenth of a unit here, half that on screen, two triangles
    // each. They are the whole reason for building at twice size.
    for (const t of [-0.38, 0, 0.38]) {
      big.quad(
        [0.685, y + 0.03, t + 0.05],
        [0.685, y + 0.03, t - 0.05],
        [0.685, y + 0.13, t - 0.05],
        [0.685, y + 0.13, t + 0.05],
        SHADE.MetalDark,
      )
      big.quad(
        [t - 0.05, y + 0.03, 0.685],
        [t + 0.05, y + 0.03, 0.685],
        [t + 0.05, y + 0.13, 0.685],
        [t - 0.05, y + 0.13, 0.685],
        SHADE.MetalDark,
      )
    }
  }

  // Four braces off the spur decks. A right-triangle prism costs four triangles
  // where a tapered block costs ten, and it actually leans on the mast.
  const brace = new MeshBuilder()
  brace.quad([0.54, 2.14, -0.24], [0.54, 2.14, 0.24], [1.18, 1.06, 0.24], [1.18, 1.06, -0.24], SHADE.Owner)
  brace.tri([1.18, 1.06, 0.24], [0.54, 2.14, 0.24], [0.54, 1.06, 0.24], SHADE.MetalMid)
  brace.tri([0.54, 1.06, -0.24], [0.54, 2.14, -0.24], [1.18, 1.06, -0.24], SHADE.MetalMid)
  for (const yaw of [0, Math.PI / 2, Math.PI, -Math.PI / 2]) big.merge(brace, 0, 0, 0, 1, yaw)

  // A caged ladder up the lit cheek, rungs as flat quads on its backing plate.
  big.box(0.6, 1.5, 0, 0.14, 1.86, 0.42, SHADE.MetalDark)
  for (let i = 0; i < 10; i++) {
    const y = 1.58 + i * 0.18
    big.quad([0.672, y, 0.19], [0.672, y, -0.19], [0.672, y + 0.06, -0.19], [0.672, y + 0.06, 0.19], SHADE.Metal)
  }

  // Cable trunk up the other visible cheek, clamped at three stations and
  // running out of the switch cabinet on the deck, so the power comes from
  // somewhere.
  big.box(0, 1.14, 0.6, 0.3, 2.3, 0.14, SHADE.MetalDark)
  for (const y of [1.72, 2.42, 3.12]) big.box(0, y, 0.6, 0.44, 0.1, 0.24, SHADE.Metal)
  big.box(-0.4, 1.06, 1.0, 0.62, 0.48, 0.42, SHADE.MetalMid, SHADE.Metal)
  big.quad([-0.62, 1.14, 1.212], [-0.18, 1.14, 1.212], [-0.18, 1.46, 1.212], [-0.62, 1.46, 1.212], SHADE.Accent)
  big.box(-0.2, 1.44, 0.72, 0.5, 0.14, 0.24, SHADE.MetalDark)
  // A vent stack on the far spur, where a low mass cannot hide anything.
  big.box(0, 1.06, -1.0, 0.5, 0.5, 0.4, SHADE.MetalMid, SHADE.Metal)
  big.box(0, 1.56, -1.0, 0.2, 0.16, 0.2, SHADE.Accent)

  // --- head ------------------------------------------------------------------
  // A gantry wider than the shaft, so there is a gap of sky inside the outline
  // where the mast turns from structure into equipment.
  big.box(0, 3.46, 0, 1.56, 0.14, 1.56, SHADE.MetalDark, SHADE.Metal)
  for (const [cx, cz] of [
    [0.7, 0.7],
    [0.7, -0.7],
    [-0.7, 0.7],
  ]) {
    big.box(cx, 3.6, cz, 0.18, 0.4, 0.18, SHADE.Metal)
  }
  big.box(0, 3.9, 0.7, 1.56, 0.1, 0.14, SHADE.Metal)

  // Fin bank along the open cheek of the gantry: a tread band laid flat, because
  // its rib count comes from its length and nothing else in the kit can be
  // ribbed this finely. The rail on this side is left off rather than drawn
  // across it — a bar hanging a hundredth above the fins reads as a scratch.
  big.box(0.52, 3.6, 0, 0.44, 0.14, 1.0, SHADE.MetalDark)
  const fins = new MeshBuilder()
  fins.treadBand(0, 0, 0.92, 0.3, SHADE.MetalDark)
  big.merge(fins, 0.52, 3.74, 0, 1)

  big.box(-0.2, 3.6, -0.1, 1.08, 0.52, 1.0, SHADE.Owner, SHADE.OwnerLight)
  big.box(-0.2, 4.12, -0.1, 1.22, 0.12, 1.14, SHADE.MetalDark, SHADE.Metal)
  big.quad([0.342, 3.7, 0.28], [0.342, 3.7, -0.44], [0.342, 3.84, -0.44], [0.342, 3.84, 0.28], SHADE.MetalDark)
  big.box(-0.5, 3.68, 0.42, 0.48, 0.16, 0.08, SHADE.Accent)

  // --- array face ------------------------------------------------------------
  // The one plane on the model that is neither vertical nor flat: canted back
  // fifty-five degrees, so it faces the sun square and reads as a face instead
  // of an edge. Built from quads, since nothing in the kit tilts.
  const nx = Math.sin(0.96)
  const ny = Math.cos(0.96)
  const pt = (su: number, sv: number, off: number): number[] => [
    0.18 + su * 0.48 * ny + nx * off,
    4.46 - su * 0.48 * nx + ny * off,
    -0.08 + sv * 0.72,
  ]
  const th = 0.09
  big.quad(pt(-1, -1, th), pt(-1, 1, th), pt(1, 1, th), pt(1, -1, th), SHADE.MetalDark)
  for (const su of [-0.46, 0.46]) {
    for (const sv of [-0.62, 0, 0.62]) {
      big.quad(
        pt(su - 0.34, sv - 0.26, th + 0.03),
        pt(su - 0.34, sv + 0.26, th + 0.03),
        pt(su + 0.34, sv + 0.26, th + 0.03),
        pt(su + 0.34, sv - 0.26, th + 0.03),
        su > 0 && sv === 0 ? SHADE.Accent : SHADE.Metal,
      )
    }
  }
  // Rims: the two the camera can reach, plus the upper edge, which is the one
  // carrying the silhouette against the sky.
  big.quad(pt(1, 1, -th), pt(1, -1, -th), pt(1, -1, th), pt(1, 1, th), SHADE.Metal)
  big.quad(pt(-1, -1, -th), pt(-1, 1, -th), pt(-1, 1, th), pt(-1, -1, th), SHADE.MetalMid)
  big.quad(pt(-1, 1, -th), pt(1, 1, -th), pt(1, 1, th), pt(-1, 1, th), SHADE.MetalMid)
  for (const z of [-0.4, 0.4]) big.box(-0.06, 4.24, z, 0.18, 0.42, 0.18, SHADE.MetalDark)

  const hull = r.part(PART.Hull)
  hull.merge(big, 0, 0, 0, 0.5)

  // --- fittings that never turn ----------------------------------------------
  const top = new MeshBuilder()
  top.box(-0.54, 4.24, -0.1, 0.24, 0.14, 2.0, SHADE.Metal)
  top.box(-0.54, 4.38, -1.0, 0.32, 0.26, 0.32, SHADE.MetalDark)
  top.cylinder(-0.54, 4.64, -1.0, 0.22, 0.22, 6, SHADE.MetalDark, SHADE.Metal)
  top.box(-0.54, 4.38, 0.8, 0.28, 0.3, 0.28, SHADE.MetalMid, SHADE.Accent)
  top.box(-0.66, 4.24, -0.44, 0.22, 0.46, 0.22, SHADE.Metal)
  top.box(-0.66, 4.7, -0.44, 0.16, 0.16, 0.16, SHADE.Accent)
  r.part(PART.Accent).merge(top, 0, 0, 0, 0.5)
  return r.build()
}

const guardianTurret: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 1.09, 1.09, 2, 2)

  const big = new MeshBuilder()
  // The barbette. Two cast courses, and they are deliberately the only base
  // there is — a defence emplacement that stacks a plinth reads as a small
  // production building, which is the one mistake this shape cannot make.
  big.cylinder(0, 0.16, 0, 0.92, 0.18, 8, SHADE.MetalDark, SHADE.MetalMid)
  big.cylinder(0, 0.34, 0, 0.78, 0.12, 8, SHADE.MetalDark, SHADE.Metal)
  // Bolt heads round the flange between the two courses. The step is a lit
  // annulus a tenth of a unit wide, and it is the one place on the base where
  // something this small still throws its own shadow.
  for (let i = 0; i < 8; i++) {
    const a = ((i + 0.5) / 8) * Math.PI * 2
    big.box(Math.cos(a) * 0.85, 0.34, Math.sin(a) * 0.85, 0.12, 0.09, 0.12, SHADE.Metal)
  }
  // Hold-down toes on the four diagonals, drawn once and turned. They are kept
  // low and narrow on purpose: run out to full width they stop being buttresses
  // and become a second square platform, which is the exact shape four of this
  // faction's other buildings already have.
  const toe = new MeshBuilder()
  toe.wedge(0, 0.16, 0.88, 0.26, 0.16, 0.42, 0.06, SHADE.MetalMid, SHADE.Metal)
  for (let i = 0; i < 4; i++) big.merge(toe, 0, 0, 0, 1, Math.PI / 4 + (i * Math.PI) / 2)
  // Power trunking onto the pad on the two faces the camera can see.
  big.box(0.98, 0.16, 0.32, 0.16, 0.18, 0.34, SHADE.MetalDark, SHADE.Accent)
  big.box(0.32, 0.16, 0.98, 0.34, 0.18, 0.16, SHADE.MetalDark, SHADE.Accent)
  hull.merge(big, 0, 0, 0, 0.5)

  const gun = new MeshBuilder()
  // A collar sunk into the barbette, so the weapon looks seated in the
  // emplacement rather than parked on it.
  gun.cylinder(0, 0.4, 0, 0.66, 0.1, 8, SHADE.MetalDark)
  // The breech house: one blunt slab of team colour, wider than it is deep and
  // set well back, so the guns have clear ground under their whole length.
  gun.box(0, 0.46, -0.36, 1.14, 0.36, 0.84, SHADE.Owner, SHADE.OwnerLight)
  // Appliqué cheeks standing proud of the flanks, bolted in threes.
  for (const sx of [-1, 1]) {
    gun.box(sx * 0.61, 0.5, -0.38, 0.1, 0.26, 0.62, SHADE.OwnerDark, SHADE.Owner)
    for (const k of [-1, 1]) {
      gun.box(sx * 0.68, 0.57, -0.38 + k * 0.19, 0.06, 0.1, 0.1, SHADE.Metal)
    }
  }
  // A breech counterweight hanging off the back, and a bolted end plate on it.
  // It is the tail of the arrow — without it the plan silhouette is a square
  // with a stick out of the front.
  gun.box(0, 0.48, -0.9, 0.7, 0.32, 0.26, SHADE.OwnerDark, SHADE.Owner)
  gun.box(0, 0.48, -1.05, 0.8, 0.36, 0.06, SHADE.MetalDark, SHADE.MetalMid)
  // A ventilator grating let into the rear deck.
  const grate = new MeshBuilder()
  grate.treadBand(0, 0, 0.68, 0.34, SHADE.MetalDark)
  gun.merge(grate, 0, 0.82, -0.6, 1)
  // The roof, split by a raised cross seam, with an optic block on one side and
  // a capped extractor on the other. Both sit behind the trunnions, so nothing
  // stands beside the guns to compete with them.
  gun.box(0, 0.82, -0.36, 0.1, 0.06, 0.8, SHADE.MetalDark)
  gun.box(0, 0.82, -0.36, 1.1, 0.06, 0.1, SHADE.MetalDark)
  gun.box(-0.38, 0.82, -0.24, 0.26, 0.2, 0.22, SHADE.MetalMid, SHADE.Metal)
  gun.box(-0.38, 0.9, -0.11, 0.14, 0.08, 0.04, SHADE.Accent)
  gun.box(0.4, 0.82, -0.26, 0.2, 0.22, 0.2, SHADE.MetalMid, SHADE.Metal)
  gun.box(0.4, 1.04, -0.26, 0.26, 0.06, 0.26, SHADE.Metal)

  // Trunnion arms off the front of the roof and a yoke slung between them, both
  // in owner colour so the neutral on this model belongs to the ordnance alone.
  for (const sx of [-1, 1]) {
    gun.box(sx * 0.52, 0.82, -0.1, 0.18, 0.32, 0.4, SHADE.Owner, SHADE.OwnerLight)
  }
  gun.box(0, 0.86, 0.12, 0.86, 0.28, 0.36, SHADE.OwnerDark, SHADE.Owner)
  // The tubes: square section, stepped down once, flared into blunt brakes.
  for (const sx of [-1, 1]) {
    gun.box(sx * 0.34, 0.9, 0.62, 0.26, 0.24, 0.74, SHADE.Metal)
    gun.box(sx * 0.34, 0.92, 1.28, 0.2, 0.2, 0.62, SHADE.Metal)
    gun.box(sx * 0.34, 0.88, 1.72, 0.32, 0.28, 0.26, SHADE.MetalMid, SHADE.Metal)
    gun.box(sx * 0.34, 0.92, 1.72, 0.36, 0.1, 0.16, SHADE.Accent)
    gun.box(sx * 0.34, 0.94, 1.86, 0.14, 0.14, 0.04, SHADE.Accent)
  }
  // Banding strapped across both tubes at once, so the pair reads as one gun
  // with two barrels rather than as two guns that happen to be adjacent.
  for (const z of [0.42, 0.76]) {
    gun.box(0, 0.88, z, 0.86, 0.28, 0.08, SHADE.MetalMid)
  }
  r.part(PART.Turret).merge(gun, 0, 0, 0, 0.5)
  return r.build()
}

const orbitalMirrorLance: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 1.95, 1.95, 2, 2)

  // Everything above the pad is laid out at twice size and merged down at 0.5.
  // The traverse rack is the reason: `treadBand` counts its teeth off its own
  // length, so a band drawn at 1.0 comes back with eight machined teeth where
  // one drawn at final size would have had four.
  const bed = new MeshBuilder()
  bed.box(0, 0.16, 0, 3.6, 0.34, 3.6, SHADE.MetalDark, SHADE.MetalMid)
  bed.cylinder(0, 0.5, 0, 1.5, 0.3, 8, SHADE.MetalMid, SHADE.Metal)
  const rack = new MeshBuilder()
  rack.treadBand(1.62, 0, 1.0, 0.3, SHADE.MetalDark)
  bed.merge(rack, 0, 0.5, 0, 1)
  // Plant on the ledge the carriage never sweeps over: a coolant drum, the
  // trunk run, and the junction box it feeds.
  bed.cylinder(-1.42, 0.5, 1.5, 0.26, 0.6, 6, SHADE.MetalMid, SHADE.Metal)
  bed.box(0.3, 0.5, 1.62, 1.4, 0.14, 0.22, SHADE.MetalDark)
  bed.box(-0.72, 0.5, 1.62, 0.32, 0.22, 0.26, SHADE.Accent)
  hull.merge(bed, 0, 0, 0, 0.5)

  // The carriage: two armour cheeks bolted to end beams, low enough that the
  // optic clears them, with a slot of open sky down the middle of the building.
  const car = new MeshBuilder()
  for (const sz of [-1, 1]) {
    car.box(0, 0.72, sz * 1.32, 3.4, 0.3, 0.62, SHADE.MetalDark, SHADE.MetalMid)
    car.box(0, 1.02, sz * 1.32, 3.5, 0.58, 0.7, SHADE.Owner, SHADE.OwnerLight)
    car.box(0, 1.6, sz * 1.32, 0.5, 0.16, 0.78, SHADE.Metal)
    car.wedge(0, 1.6, sz * 1.32, 3.4, 0.16, 0.62, 0.1, SHADE.MetalMid, SHADE.Metal)
  }
  car.box(-1.56, 0.72, 0, 0.48, 0.88, 2.64, SHADE.MetalMid, SHADE.Metal)
  car.box(1.56, 0.72, 0, 0.48, 0.62, 2.64, SHADE.MetalDark, SHADE.MetalMid)
  car.box(1.56, 1.34, 0, 0.54, 0.16, 2.7, SHADE.Owner, SHADE.OwnerLight)
  // A firing post on the front beam, and the vent bank down its lit face.
  car.box(1.5, 1.5, 0.95, 0.44, 0.4, 0.5, SHADE.MetalMid, SHADE.Metal)
  car.box(1.72, 1.6, 0.95, 0.06, 0.16, 0.32, SHADE.Accent)
  car.box(1.82, 0.94, -0.3, 0.06, 0.16, 1.3, SHADE.Accent)
  // Cheek plating: louvres, a lamp, and a riveted strap under the cap. All of
  // it on the one flank that is both turned toward the camera and turned into
  // the sun, which for a part yawed a quarter turn is its own +z face.
  car.box(-1.05, 0.82, 1.69, 0.34, 0.3, 0.08, SHADE.Metal)
  car.box(-0.6, 0.82, 1.69, 0.22, 0.3, 0.08, SHADE.Metal)
  car.box(0.55, 0.82, 1.69, 0.5, 0.3, 0.08, SHADE.MetalMid)
  car.box(1.3, 0.9, 1.69, 0.26, 0.42, 0.1, SHADE.Metal)
  car.box(-1.4, 1.06, 1.7, 0.4, 0.12, 0.06, SHADE.Accent)
  car.box(0, 1.46, 1.7, 3.4, 0.1, 0.05, SHADE.MetalDark)
  // The breech in the slot, so the gap between the cheeks shows machinery
  // rather than an empty trough.
  car.box(-0.2, 0.8, 0, 1.7, 0.28, 1.7, SHADE.MetalDark, SHADE.MetalMid)
  car.cylinder(-0.9, 1.08, 0, 0.34, 0.44, 6, SHADE.MetalMid, SHADE.Metal)

  // The optic. A faceted bowl aimed up out of the pad, drawn in its own frame
  // — `s` down the slope, `t` across the trunnion, `h` out along the aim — and
  // swung so the face opens toward the camera instead of edge-on to it. Axis
  // -aligned primitives cannot tilt, and a stepped approximation of a dish this
  // size would eat the whole triangle budget in staircases. The swing is three
  // eighths of a turn rather than one eighth because a turret part is already
  // yawed a quarter turn when it is drawn, and a dish aimed without counting
  // that comes out edge-on: an oval of grey where the reflector should be.
  const cy = 3.56
  const tilt = 0.72
  const yaw = (-3 * Math.PI) / 4
  const cw = Math.cos(yaw)
  const sw = Math.sin(yaw)
  const R = 1.55
  const at = (s: number, t: number, h: number) => {
    const x = s * Math.cos(tilt) + h * Math.sin(tilt)
    return [
      x * cw + t * sw - 0.3,
      cy - s * Math.sin(tilt) + h * Math.cos(tilt),
      t * cw - x * sw + 0.3,
    ]
  }
  const rim: number[][] = []
  const mid: number[][] = []
  const face: number[][] = []
  const back: number[][] = []
  for (let k = 0; k < 8; k++) {
    const th = ((k + 0.5) / 8) * Math.PI * 2
    const s = Math.cos(th)
    const t = Math.sin(th)
    rim.push(at(s * R, t * R, 0.46))
    mid.push(at(s * R * 0.82, t * R * 0.82, 0.18))
    face.push(at(s * R * 0.66, t * R * 0.66, -0.04))
    back.push(at(s * R * 0.88, t * R * 0.88, -0.5))
  }
  const focus = at(0, 0, -0.55)
  const spine = at(0, 0, -0.74)
  // Three concentric zones rather than one plate: the reflector, a segmented
  // ring of panels round it, and an owner-coloured hoop at the edge. Concentric
  // colour steps are the only detail a disc this size can carry, and the hoop
  // is the whole of the team colour anyone reads from across the map.
  for (let k = 0; k < 8; k++) {
    const j = (k + 1) % 8
    car.tri(focus, face[j], face[k], SHADE.Accent)
    car.quad(face[k], face[j], mid[j], mid[k], SHADE.Metal)
    car.quad(mid[k], mid[j], rim[j], rim[k], SHADE.Owner)
    car.quad(back[k], back[j], rim[j], rim[k], SHADE.MetalDark)
    car.tri(spine, back[k], back[j], SHADE.MetalDark)
  }

  // A prism swept along an arbitrary axis, for the feed tripod. Three thin
  // struts standing off the reflector are what separate an optic from a plate,
  // and none of them lies on an axis a box can be built along.
  const beam = (
    from: readonly number[],
    to: readonly number[],
    w: number,
    shade: (typeof SHADE)[keyof typeof SHADE],
  ) => {
    const dl = Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2])
    const ex = (to[0] - from[0]) / dl
    const ey = (to[1] - from[1]) / dl
    const ez = (to[2] - from[2]) / dl
    const ul = Math.hypot(ex, ey)
    const ux = -ey / ul
    const uy = ex / ul
    const foot: number[][] = []
    const tip: number[][] = []
    for (let k = 0; k < 4; k++) {
      const a = (k / 4 + 0.125) * Math.PI * 2
      const c = Math.cos(a) * w
      const s = Math.sin(a) * w
      const ox = c * ux + s * uy * ez
      const oy = c * uy - s * ux * ez
      const oz = s * (ux * ey - uy * ex)
      foot.push([from[0] + ox, from[1] + oy, from[2] + oz])
      tip.push([to[0] + ox, to[1] + oy, to[2] + oz])
    }
    for (let k = 0; k < 4; k++) {
      const j = (k + 1) % 4
      car.quad(foot[k], foot[j], tip[j], tip[k], shade)
    }
  }
  // Outboard of the rim, not behind it: an arm inside the disc's outline is an
  // arm drawn across the reflector, and the two of them are what tell a player
  // the thing is hung on a trunnion rather than propped against the base.
  for (const st of [-1, 1]) {
    const hub = at(0, st * R * 1.02, -0.28)
    car.box(hub[0], 1.5, hub[2], 0.66, hub[1] - 1.76, 0.66, SHADE.MetalMid, SHADE.Metal)
    car.box(hub[0], 1.94, hub[2], 0.76, 0.14, 0.76, SHADE.MetalDark)
    car.box(hub[0], hub[1] - 0.26, hub[2], 0.54, 0.44, 0.54, SHADE.Owner, SHADE.OwnerLight)
  }
  const head = at(0, 0, 1.5)
  beam(rim[1], head, 0.08, SHADE.Metal)
  beam(rim[5], head, 0.08, SHADE.Metal)
  beam(rim[7], head, 0.08, SHADE.Metal)
  car.box(head[0], head[1] - 0.2, head[2], 0.38, 0.36, 0.38, SHADE.MetalDark, SHADE.Metal)
  car.box(head[0], head[1] + 0.16, head[2], 0.24, 0.12, 0.24, SHADE.Accent)

  r.part(PART.Turret).merge(car, 0, 0, 0, 0.5)
  return r.build()
}

// ---------------------------------------------------------------------------
// The Ascendancy — angular, asymmetric, vertical
// ---------------------------------------------------------------------------

/**
 * A casting on four legs.
 *
 * The Ascendancy's line unit is one continuous carapace lofted through five
 * stations — a tapered tail, a full midbody, a chisel prow — and nothing is
 * bolted to the outside of it. Every profile is the same six-point section: a
 * belly, a chine strake where the two flank planes meet, a dark lower flank, a
 * lit upper flank, a shoulder slope, and a narrow dorsal spine. The turret is
 * sunk into that shoulder slope rather than stood on the deck, the legs grow out
 * of cast haunches that are part of the hull, and the exhaust is a nozzle let
 * into the tail plate. What breaks the surface up is the angle at which planes
 * meet, not parts stuck on it.
 *
 * The chine strake is the load-bearing idea. It is a couple of centimetres of
 * bare alloy between two painted planes, it wraps the entire casting nose to
 * tail, and it follows every change of width in the hull because it *is* the
 * change of width. A raised rail along the same line would have to be re-cut at
 * every station and would stand off in mid air wherever the hull pulled in.
 *
 * Authored at twice final size and merged down at half scale, which is what pays
 * for the parts that would otherwise not be worth the arithmetic: a spine lane
 * running the length of the deck, roof louvres that narrow with the taper they
 * sit in, a nose light six hundredths tall, and legs lofted through four
 * stations — haunch, knee collar, shin, splayed foot — where two stacked posts
 * would have cost the same and read as scaffolding.
 *
 * It is also the tightest thing in the roster against the triangle budget, and
 * every economy in it is the same one: nothing is capped that something else
 * covers, and no surface the fixed overhead camera cannot reach is emitted at
 * all. That is what buys the six-band section, which is where the detail is.
 */
const siegeWalker: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]

  /**
   * A four-sided prism tapering between two points, used wherever something has
   * to run at an angle. Every primitive in the vocabulary is axis-aligned, so a
   * splayed leg or a swept fin has to be lofted; a stack of boxes stepping
   * sideways reads as a staircase at this size, and a limb that narrows along
   * its length reads as a limb. The top cap is optional because most of these
   * run into something that covers it.
   */
  const limb = (
    b: MeshBuilder,
    lo: readonly number[],
    hi: readonly number[],
    rLo: number,
    rHi: number,
    shade: Role,
    capShade?: Role,
  ): void => {
    const ring = (p: readonly number[], q: number) => [
      [p[0] - q, p[1], p[2] - q],
      [p[0] + q, p[1], p[2] - q],
      [p[0] + q, p[1], p[2] + q],
      [p[0] - q, p[1], p[2] + q],
    ]
    const A = ring(lo, rLo)
    const C = ring(hi, rHi)
    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4
      b.quad(A[i], C[i], C[j], A[j], shade)
    }
    if (capShade !== undefined) b.quad(C[3], C[2], C[1], C[0], capShade)
  }

  /** The same loft along the forward axis, for the gun tube. */
  const spar = (
    b: MeshBuilder,
    cy: number,
    z0: number,
    z1: number,
    r0: number,
    r1: number,
    shade: Role,
  ): void => {
    const ring = (z: number, q: number) => [
      [-q, cy - q, z],
      [q, cy - q, z],
      [q, cy + q, z],
      [-q, cy + q, z],
    ]
    const A = ring(z0, r0)
    const B = ring(z1, r1)
    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4
      b.quad(A[i], A[j], B[j], B[i], shade)
    }
  }

  // One half-section, belly to spine. Widest at the chine and low, so the flanks
  // flare out of the underbody and then fall back in to a narrow deck.
  const SECTION = [
    [0.36, 0.0],
    [0.58, 0.15],
    [0.545, 0.35],
    [0.47, 0.54],
    [0.34, 0.66],
    [0.17, 0.76],
  ]
  // Stations tail to prow: [z, widthScale, heightScale, belly height].
  // Both ends pull in on width and height together and lift their belly, which
  // is what makes them taper away rather than simply stop. The prow gets two
  // stations to the tail's one, so the front draws out to a chisel while the
  // back stops short — the only cue the camera has for which way a hull with no
  // wheels and a turret that turns is facing.
  const STATIONS = [
    [-0.74, 0.42, 0.58, 0.88],
    [-0.3, 0.98, 0.98, 0.765],
    [0.16, 1.0, 1.02, 0.76],
    [0.58, 0.8, 0.84, 0.8],
    [0.9, 0.34, 0.48, 0.88],
  ]
  const SPINE = SECTION.length - 1
  const at = (s: number[], i: number, sx: number): number[] => [
    sx * s[1] * SECTION[i][0],
    s[3] + s[2] * SECTION[i][1],
    s[0],
  ]
  // Underbody, chine strake, lower flank, upper flank, shoulder slope. The lit
  // flank is a belt with a dark plane above it and a dark plane below, which is
  // the value change the casting lives on: at this size a single painted mass
  // with a highlight belt around it reads as a shape, and the same mass in one
  // hue reads as a blob whatever is drawn on it.
  const BANDS: Role[] = [
    SHADE.MetalDark,
    SHADE.MetalMid,
    SHADE.Owner,
    SHADE.Owner,
    SHADE.Owner,
  ]

  const big = new MeshBuilder()
  for (let i = 0; i < STATIONS.length - 1; i++) {
    const a = STATIONS[i]
    const b = STATIONS[i + 1]
    for (const sx of [1, -1]) {
      for (let k = 0; k < BANDS.length; k++) {
        const q0 = at(a, k, sx)
        const q1 = at(a, k + 1, sx)
        const q2 = at(b, k + 1, sx)
        const q3 = at(b, k, sx)
        if (sx > 0) big.quad(q0, q1, q2, q3, BANDS[k])
        else big.quad(q3, q2, q1, q0, BANDS[k])
      }
    }
    // The spine lane: a strip of bare alloy down the crown of the casting,
    // between the two shoulder slopes. It costs two triangles a segment and it
    // follows every change of width the hull makes, which a raised rail could
    // not do without being re-cut at each station.
    const ay = a[3] + a[2] * SECTION[SPINE][1]
    const by = b[3] + b[2] * SECTION[SPINE][1]
    const aw = a[1] * SECTION[SPINE][0]
    const bw = b[1] * SECTION[SPINE][0]
    big.quad([-bw, by, b[0]], [bw, by, b[0]], [aw, ay, a[0]], [-aw, ay, a[0]], SHADE.MetalMid)
  }
  // End caps, closed band by band off the same section. The tail plate is the
  // powerplant bulkhead and takes a neutral; the prow is a few centimetres of
  // chisel and stays in colour. Nothing closes the underside — the camera is
  // fixed overhead and has never once seen it.
  for (const [s, front, shade] of [
    [STATIONS[0], false, SHADE.MetalMid],
    [STATIONS[STATIONS.length - 1], true, SHADE.Owner],
  ] as Array<[number[], boolean, Role]>) {
    for (let k = 0; k < BANDS.length; k++) {
      const p0 = at(s, k, 1)
      const p1 = at(s, k, -1)
      const p2 = at(s, k + 1, -1)
      const p3 = at(s, k + 1, 1)
      if (front) big.quad(p1, p0, p3, p2, shade)
      else big.quad(p0, p1, p2, p3, shade)
    }
  }

  // Cast haunches: the legs do not meet the hull, they come out of it. Each is a
  // bulge flaring down and outward from under the chine's overhang, in the body's
  // own paint, and the neutral limb starts up inside it.
  const PLANT = [
    [0.34, 0.36, 0.62, 0.48, 0.6, 0.57],
    [0.34, -0.4, 0.62, -0.58, 0.6, -0.68],
  ]
  for (const [hx, hz] of PLANT) {
    for (const sx of [-1, 1]) {
      limb(big, [sx * (hx + 0.14), 0.6, hz], [sx * hx, 1.02, hz], 0.16, 0.22, SHADE.Owner)
    }
  }

  // Two slots let into the rear deck, narrowing with the deck they sit in — a
  // shadowed one where the spine is still flat, a hot one further back where the
  // roof has begun to fall away to the tail and the camera can see down it.
  big.box(0, 1.462, -0.44, 0.28, 0.045, 0.07, SHADE.MetalDark)
  big.box(0, 1.442, -0.55, 0.23, 0.045, 0.07, SHADE.Accent)
  // The nozzle throat in the tail plate, and a sensor blister swept up off the
  // port shoulder — the one asymmetry, and cast into the shell rather than
  // clamped to it.
  big.box(0, 1.02, -0.79, 0.2, 0.14, 0.06, SHADE.Accent)
  limb(big, [-0.29, 1.28, 0.0], [-0.4, 1.54, -0.08], 0.11, 0.055, SHADE.MetalMid, SHADE.Accent)
  // A swept dorsal fin off the rear deck: the tallest thing on the field, and a
  // vertical, which is this faction's whole grammar in one part.
  limb(big, [0.0, 1.42, -0.4], [0.02, 2.04, -0.6], 0.1, 0.042, SHADE.Owner)
  big.box(0.02, 2.04, -0.6, 0.075, 0.09, 0.075, SHADE.Accent)
  // A ridge running forward off the turret ring and down the falling foredeck to
  // the prow, and the nose light down in the chisel at the end of it. The
  // foredeck is the largest unbroken face the camera ever gets of this hull, and
  // a ridge that follows it down is the only thing that can be put there without
  // standing off the surface somewhere along its length.
  limb(big, [0.0, 1.3, 0.8], [0.0, 1.47, 0.4], 0.055, 0.105, SHADE.MetalMid)
  big.box(0, 1.06, 0.87, 0.22, 0.06, 0.07, SHADE.Accent)
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // Four legs, each lofted through four stations — haunch, knee collar, shin,
  // splayed foot — and tapering the whole way down. The taper is the read: a
  // limb the same width at the sole as at the hip is a post, and four posts
  // under a cast shell is a table. The knee collar is the one place the running
  // gear is allowed the body's paint, and it lands halfway down the tallest
  // silhouette in the game.
  for (const [hx, hz, kx, kz, fx, fz] of PLANT) {
    for (const sx of [-1, 1]) {
      const legs = new MeshBuilder()
      limb(legs, [sx * kx, 0.52, kz], [sx * hx, 1.0, hz], 0.125, 0.165, SHADE.MetalMid)
      limb(legs, [sx * kx, 0.4, kz], [sx * kx, 0.52, kz], 0.135, 0.135, SHADE.Owner)
      limb(legs, [sx * fx, 0.14, fz], [sx * kx, 0.41, kz], 0.075, 0.125, SHADE.MetalDark)
      limb(legs, [sx * fx, 0.0, fz], [sx * fx, 0.15, fz], 0.145, 0.075, SHADE.MetalMid)
      legs.box(sx * (kx + 0.11), 0.39, kz, 0.1, 0.14, 0.26, SHADE.MetalDark, SHADE.Metal)
      const part = sx * hz > 0 ? PART.LegA : PART.LegB
      r.part(part).merge(legs, 0, 0, 0, 0.5)
    }
  }

  // The turret is sunk into the shoulder slope rather than stood on the deck: a
  // dark frustum rising out of the roofline, a lit cap pulling in again over it,
  // and a hatch off the centre line. Nothing on the cheeks — the taper is the
  // detail, and it is the same taper the hull has.
  const gun = new MeshBuilder()
  gun.cylinder(0, 1.24, -0.02, 0.37, 0.2, 6, SHADE.Owner, SHADE.Owner, 0.32)
  gun.cylinder(0, 1.44, -0.02, 0.32, 0.16, 6, SHADE.Owner, SHADE.Owner, 0.2)
  gun.cylinder(0, 1.6, -0.11, 0.14, 0.05, 6, SHADE.MetalMid, SHADE.Accent)
  // The mantlet is blended into the turret's face rather than hung off it, and
  // the gun grows out of the mantlet and narrows the whole way to the brake.
  gun.wedge(0, 1.26, 0.32, 0.42, 0.3, 0.32, 0.08, SHADE.MetalMid, SHADE.Metal)
  spar(gun, 1.42, 0.4, 0.98, 0.17, 0.115, SHADE.MetalMid)
  gun.box(0, 1.3, 1.02, 0.3, 0.24, 0.12, SHADE.MetalDark, SHADE.Accent)
  r.part(PART.Turret).merge(gun, 0, 0, 0, 0.5)
  return r.build()
}

const nanoHarvester: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]
  const big = new MeshBuilder()

  /** A decal on a flank, a hair proud of it. Two triangles, no silhouette. */
  const flank = (
    sx: number,
    y0: number,
    z0: number,
    y1: number,
    z1: number,
    x: number,
    shade: Role,
  ): void => {
    const px = sx * x
    if (sx > 0) big.quad([px, y0, z1], [px, y0, z0], [px, y1, z0], [px, y1, z1], shade)
    else big.quad([px, y0, z0], [px, y0, z1], [px, y1, z1], [px, y1, z0], shade)
  }

  // Undercarriage and the skirt over it. The skirt is the one owner-coloured
  // mass low on the machine and it runs the full length, so the track frame
  // reads as armoured rather than as a black stripe under a coloured box.
  big.box(0, 0.06, -0.02, 1.34, 0.26, 1.76, SHADE.MetalDark)
  big.box(0, 0.28, -0.02, 1.76, 0.16, 1.8, SHADE.Owner)
  for (const sx of [-1, 1]) flank(sx, 0.31, -0.86, 0.37, 0.84, 0.881, SHADE.MetalDark)

  // The tub. Four slab walls around a sunk floor, the front wall cut down so the
  // camera looks over it, and a load lying in the bottom. A container visibly
  // holding something cannot be read as a weapon, and the load is what turns the
  // opening from a dark patch into a depth.
  for (const sx of [-1, 1]) {
    big.box(sx * 0.6, 0.42, -0.18, 0.2, 0.56, 1.48, SHADE.Owner, SHADE.Metal)
  }
  big.box(0, 0.42, -0.84, 1.4, 0.58, 0.24, SHADE.Owner, SHADE.Metal)
  big.box(0, 0.42, 0.46, 1.4, 0.36, 0.2, SHADE.Owner, SHADE.Metal)
  big.quad(
    [-0.5, 0.445, 0.36],
    [0.5, 0.445, 0.36],
    [0.5, 0.445, -0.72],
    [-0.5, 0.445, -0.72],
    SHADE.MetalDark,
  )
  big.box(0, 0.44, -0.18, 0.9, 0.18, 1.0, SHADE.MetalDark, SHADE.MetalMid)
  big.quad(
    [-0.3, 0.625, 0.14],
    [0.3, 0.625, 0.14],
    [0.3, 0.625, -0.5],
    [-0.3, 0.625, -0.5],
    SHADE.Accent,
  )

  // Strapping up both flanks with a rivet column down each strap. Flat, because
  // a strap that stood proud would cost five times what it is worth: what makes
  // plate read as plate at this size is the value change across the seam, not
  // the two pixels of edge it would throw.
  for (const sx of [-1, 1]) {
    for (const z of [-0.74, -0.3, 0.14]) {
      flank(sx, 0.44, z - 0.09, 0.96, z + 0.09, 0.705, SHADE.MetalDark)
      for (const y of [0.56, 0.86]) {
        flank(sx, y - 0.05, z - 0.05, y + 0.05, z + 0.05, 0.71, SHADE.Metal)
      }
    }
  }

  // The intake, hung off the nose and deliberately wider than the machine — the
  // one part that leaves the chassis' own box, and the reason the thing is
  // legible head-on rather than only from above. A lit slot across the housing
  // face, and four teeth under it heavy enough to survive at gameplay zoom.
  big.box(0, 0.14, 1.1, 1.6, 0.28, 0.4, SHADE.MetalMid, SHADE.Metal)
  big.quad(
    [-0.72, 0.2, 1.31],
    [0.72, 0.2, 1.31],
    [0.72, 0.34, 1.31],
    [-0.72, 0.34, 1.31],
    SHADE.Accent,
  )
  for (let i = 0; i < 4; i++) {
    big.box(-0.6 + i * 0.4, 0.02, 1.36, 0.24, 0.18, 0.14, SHADE.MetalDark)
  }

  // The conveyor out of the mouth and over the tub's front wall. Three slabs
  // stepping up rather than one ramp: a staircase is the blocky vocabulary's own
  // way of drawing a slope, each riser gives the light an edge to find, and the
  // top slab discharges hot over the rim.
  for (const [y, z, w] of [
    [0.4, 0.9, 0.8],
    [0.54, 0.7, 0.74],
    [0.68, 0.5, 0.68],
  ] as Array<[number, number, number]>) {
    big.box(0, y, z, w, 0.18, 0.26, SHADE.MetalDark, y > 0.6 ? SHADE.Accent : SHADE.Metal)
  }

  // Cab on the right flank ahead of the tub, low enough to sit under the rim. It
  // is a fitting on the side of the machine; the front belongs to the mouth.
  big.box(0.66, 0.44, 0.84, 0.44, 0.42, 0.44, SHADE.Owner, SHADE.OwnerLight)
  big.quad(
    [0.46, 0.6, 1.07],
    [0.86, 0.6, 1.07],
    [0.86, 0.76, 1.07],
    [0.46, 0.76, 1.07],
    SHADE.Accent,
  )
  flank(1, 0.6, 0.68, 0.76, 1.0, 0.885, SHADE.Accent)

  // Stern: a louvred blower housing on the right of the rim, two square stacks
  // strapped to the left flank opposite it. Welded up out of plate like
  // everything else here — nothing on this machine was turned on a lathe — and
  // the only pair of masses taller than they are wide.
  big.box(0.36, 1.0, -0.84, 0.56, 0.2, 0.3, SHADE.MetalDark, SHADE.MetalMid)
  big.box(-0.72, 0.44, -0.86, 0.26, 0.72, 0.26, SHADE.MetalDark, SHADE.Accent)
  big.box(-0.72, 0.44, -0.56, 0.19, 0.5, 0.19, SHADE.MetalDark, SHADE.Accent)

  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  const track = new MeshBuilder()
  for (const sx of [-1, 1]) track.treadBand(sx * 0.68, -0.02, 0.96, 0.34, SHADE.MetalDark)
  r.part(PART.Tread).merge(track, 0, 0, 0, 0.5)
  return r.build()
}

const droneSwarm: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]

  /** A value band laid a hair proud of a face — two triangles, no outline. */
  const bandZ = (
    b: MeshBuilder,
    cx: number,
    y0: number,
    cz: number,
    w: number,
    h: number,
    shade: Role,
  ): void => {
    b.quad(
      [cx - w / 2, y0, cz],
      [cx + w / 2, y0, cz],
      [cx + w / 2, y0 + h, cz],
      [cx - w / 2, y0 + h, cz],
      shade,
    )
  }
  const bandX = (
    b: MeshBuilder,
    cx: number,
    y0: number,
    cz: number,
    d: number,
    h: number,
    shade: Role,
  ): void => {
    b.quad(
      [cx, y0, cz + d / 2],
      [cx, y0, cz - d / 2],
      [cx, y0 + h, cz - d / 2],
      [cx, y0 + h, cz + d / 2],
      shade,
    )
  }
  /** The same thing lying flat, which is the face this camera actually gets. */
  const deck = (
    b: MeshBuilder,
    cx: number,
    y: number,
    cz: number,
    w: number,
    d: number,
    shade: Role,
  ): void => {
    b.quad(
      [cx - w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz - d / 2],
      [cx - w / 2, y, cz - d / 2],
      shade,
    )
  }

  /**
   * One gun-drone, origin at the centre of its belly.
   *
   * The stack top to bottom is bare alloy duct, team-coloured deck, dark rib,
   * team-coloured hull, dark belly, near-black gear. A drone is mostly deck from
   * this camera, so the deck carries the owner colour and the duct sitting in
   * the middle of it is what stops that deck being an unbroken coloured tile.
   */
  const drone = (w: number, d: number, kit: number): MeshBuilder => {
    const b = new MeshBuilder()

    // Belly tub, inset all round so the hull plate overhangs it.
    b.box(0, -0.02, 0, w - 0.12, 0.07, d - 0.12, SHADE.MetalDark)

    // The hull: one plate mass, owner colour on every face of it.
    b.box(0, 0.04, 0, w, 0.16, d, SHADE.Owner)

    // The rib. A strip of dark alloy standing proud right round the plate join —
    // the most useful ten triangles on the model, because it cuts the flank in
    // two and throws a line across the top of the outline at the same time.
    b.box(0, 0.1, 0, w + 0.05, 0.035, d + 0.05, SHADE.MetalDark, SHADE.Metal)

    // Corner lugs, full hull height and standing out past it, on three corners
    // of four. The open corner is deliberate: nothing this faction builds is
    // symmetrical, and three lugs cost a third less than four.
    for (const [sx, sz] of [
      [-1, 1],
      [1, 1],
      [1, -1],
    ]) {
      b.box(
        sx * (w / 2 - 0.02),
        0.03,
        sz * (d / 2 - 0.02),
        0.09,
        0.185,
        0.09,
        SHADE.MetalMid,
        SHADE.Metal,
      )
    }

    // Panel line and hazard stripe, on the two faces the sun reaches.
    bandZ(b, 0, 0.055, d / 2 + 0.002, w * 0.6, 0.04, SHADE.MetalDark)
    bandX(b, w / 2 + 0.002, 0.055, 0, d * 0.6, 0.04, SHADE.MetalDark)

    // The duct: an alloy collar standing on the deck with a dark mouth, and the
    // rotor laid across it as two blades that overhang the collar. A hole in a
    // coloured surface, for ten triangles and four.
    const duct = Math.min(w, d) * 0.62
    b.box(0, 0.2, 0, duct, 0.075, duct, SHADE.Metal, SHADE.Accent)
    deck(b, 0, 0.278, 0, duct * 1.2, 0.055, SHADE.MetalMid)
    deck(b, 0, 0.278, 0, 0.055, duct * 1.2, SHADE.MetalMid)

    if (kit === 0) {
      // Twin chin guns, carried far enough forward that the muzzles clear the
      // airframe's own outline.
      for (const sx of [-1, 1]) {
        b.box(sx * 0.08, -0.1, 0.15, 0.075, 0.09, 0.32, SHADE.MetalDark, SHADE.Metal)
      }
    } else if (kit === 1) {
      // A magazine canister slung under the belly and a stub mast above the
      // deck: one drone in three breaks the group's outline both ways.
      b.box(0.02, -0.14, -0.02, 0.22, 0.12, 0.3, SHADE.MetalDark, SHADE.MetalMid)
      b.box(0.06, 0.26, -0.1, 0.055, 0.22, 0.055, SHADE.MetalDark, SHADE.Accent)
    } else {
      // One heavy barrel on a yoke, thick enough to survive a pixel per voxel.
      b.box(0, -0.11, 0.14, 0.12, 0.1, 0.32, SHADE.MetalDark, SHADE.MetalMid)
      b.box(0, -0.115, 0.36, 0.14, 0.105, 0.07, SHADE.Metal)
    }
    return b
  }

  /**
   * A spotter: a drum with a rotor cross laid over it, two thirds the size of a
   * gun drone and round where the others are square. A group reads as a swarm
   * when its members are visibly not the same thing, and shape is a difference
   * that survives to gameplay zoom where surface detail does not.
   */
  const spotter = (): MeshBuilder => {
    const b = new MeshBuilder()
    b.box(0, -0.09, 0, 0.11, 0.09, 0.11, SHADE.MetalDark)
    b.cylinder(0, 0, 0, 0.16, 0.12, 6, SHADE.MetalMid, SHADE.Owner)
    deck(b, 0, 0.128, 0, 0.44, 0.05, SHADE.Metal)
    deck(b, 0, 0.128, 0, 0.05, 0.44, SHADE.Metal)
    bandZ(b, 0, -0.02, 0.145, 0.11, 0.05, SHADE.Accent)
    return b
  }

  /*
   * Spacing is set in the *camera's* plane rather than on the ground, and it has
   * to hold at every heading. The view flattens one map diagonal to 58% of the
   * other, and the whole recipe turns under that projection as the unit turns,
   * so a pair of drones with a comfortable gap between them on the map can end
   * up stacked one over the other a quarter turn later — which is exactly how a
   * swarm collapses back into a single object. These five positions were walked
   * until the tightest pair, over a full circle of headings, was as loose as the
   * cell allows. Height does most of the work: it is the one separation the
   * projection cannot rotate away, and it costs none of the footprint.
   */
  const big = new MeshBuilder()
  big.merge(drone(0.42, 0.38, 0), 0.407, 0.48, 0.553, 1, 0.14)
  big.merge(drone(0.4, 0.36, 1), -0.434, 0.28, -0.345, 1, -0.42)
  big.merge(drone(0.36, 0.34, 2), 0.527, 0.66, -0.271, 1, 0.3)
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // The spotters ride in the tread part, which bobs: the little ones hunt while
  // the gun drones hold station. One rides above the formation and one skims, so
  // the swarm has a top and a bottom as well as a spread.
  const air = new MeshBuilder()
  air.merge(spotter(), -0.238, 0.86, -0.687, 1, 0.5)
  air.merge(spotter(), -0.494, 0.2, 0.629, 1, -0.6)
  r.part(PART.Tread).merge(air, 0, 0, 0, 0.5)

  // Dug in, the swarm drops a bolted deck and settles over it to rearm. Not a
  // parapet: five machines hanging in the air have nothing to take cover
  // behind, and a wall tall enough to look protective would stand in front of
  // the one thing this unit has to be read by. A plated pallet with the ammunition
  // on it says dug in without touching the outline that says drone swarm.
  const works = new MeshBuilder()
  works.box(0, 0, 0, 0.92, 0.1, 0.82, SHADE.MetalMid, SHADE.Metal)
  works.seams(0, 0.1, 0, 0.92, 0.82, 2, 2, SHADE.MetalDark)
  works.box(-0.24, 0.1, 0.2, 0.32, 0.22, 0.26, SHADE.MetalDark, SHADE.MetalMid)
  bandZ(works, -0.24, 0.16, 0.331, 0.24, 0.05, SHADE.Accent)
  works.box(0.26, 0.1, -0.16, 0.26, 0.17, 0.24, SHADE.Metal, SHADE.MetalMid)
  r.part(PART.Works).merge(works, 0, 0, 0, 0.5)
  return r.build()
}

const infiltrator: Recipe = () => {
  const r = new RecipeBuilder()

  type Shade = (typeof SHADE)[keyof typeof SHADE]

  // Modelled at twice final size and merged down at the end. The whole reason
  // is the small stuff: a 0.03 bolt head, a 0.02 visor slit, a strap band
  // across a shin — parts you would think to place at this size and would talk
  // yourself out of at the other.
  const big = new MeshBuilder()

  /** A value band a hair proud of a face pointing down the unit's front. */
  const front = (cx: number, y0: number, cz: number, w: number, h: number, shade: Shade): void => {
    big.quad(
      [cx - w / 2, y0, cz],
      [cx + w / 2, y0, cz],
      [cx + w / 2, y0 + h, cz],
      [cx - w / 2, y0 + h, cz],
      shade,
    )
  }

  /** The same on a face pointing at +x — the other flank the fixed camera gets. */
  const flank = (cx: number, y0: number, cz: number, d: number, h: number, shade: Shade): void => {
    big.quad(
      [cx, y0, cz + d / 2],
      [cx, y0, cz - d / 2],
      [cx, y0 + h, cz - d / 2],
      [cx, y0 + h, cz + d / 2],
      shade,
    )
  }

  /** A rib laid across a top face. */
  const seam = (cx: number, y: number, cz: number, w: number, d: number, shade: Shade): void => {
    big.quad(
      [cx - w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz - d / 2],
      [cx - w / 2, y, cz - d / 2],
      shade,
    )
  }

  /** A row of bolt heads along x. Plate this thick is fastened, not folded. */
  const bolts = (
    y: number,
    cz: number,
    x0: number,
    x1: number,
    n: number,
    shade: Shade,
  ): void => {
    for (let i = 0; i < n; i++) {
      big.box(x0 + ((x1 - x0) * i) / (n - 1), y, cz, 0.055, 0.045, 0.055, shade)
    }
  }

  // Legs. Two of them, kept genuinely apart: the strip of ground between a
  // figure's shins is what separates a person from a crate at this size, and it
  // is the first thing a wide armoured suit loses. Boots flare out under narrow
  // greaves so the stance is heavy without closing that gap, and they are
  // staggered in z because a pair set level reads as parked.
  for (const sx of [-1, 1]) {
    const z = sx * 0.035
    big.box(sx * 0.12, 0, z, 0.16, 0.095, 0.26, SHADE.MetalDark, SHADE.MetalDark)
    big.box(sx * 0.12, 0, z + 0.15, 0.18, 0.08, 0.08, SHADE.MetalMid, SHADE.Metal)
    big.box(sx * 0.115, 0.095, z - 0.01, 0.13, 0.21, 0.17, SHADE.Metal, SHADE.Metal)
    front(sx * 0.115, 0.145, z + 0.076, 0.11, 0.045, SHADE.MetalDark)
  }

  // Hips: one slab bridging both legs, flared at the top edge. `wedge` with a
  // negative inset is how plate gets a lip instead of a taper, and the lip is
  // why the waist is not the narrow point of an hourglass. Nothing on this
  // figure has a narrow point.
  big.wedge(0, 0.28, 0, 0.32, 0.135, 0.24, -0.035, SHADE.OwnerDark, SHADE.MetalDark)

  // A corrugated tool belt round the flare. `treadBand` counts its ribs off its
  // own length, so a belt run long at this scale and shrunk with everything
  // else comes back finer than anything hand-placed would be, and it is also
  // the dark line that divides the legs from the body.
  const belt = new MeshBuilder()
  belt.treadBand(0, 0, 0.46, 0.14, SHADE.OwnerDark)
  big.merge(belt, 0, 0.285, 0.15, 1, Math.PI / 2)

  // Torso: the team surface, and a square slab left mostly bare colour. Steel
  // is what this unit is *fitted* with — the tool, the shoulder cops, the
  // bottles — not what it is made of, because a figure in bare alloy at this
  // size is a crate lying on the ground.
  big.box(0, 0.4, -0.005, 0.34, 0.19, 0.25, SHADE.Owner, SHADE.OwnerLight)
  flank(0.171, 0.435, -0.005, 0.2, 0.055, SHADE.OwnerDark)

  // A breastplate bolted over the chest and standing proud of it. Overhang is
  // the only panel break that survives flat lighting: a groove cut into a
  // flat-lit face has nothing to shade it, a plate laid on it throws an edge.
  big.box(0, 0.42, 0.145, 0.24, 0.145, 0.075, SHADE.OwnerLight, SHADE.OwnerLight)
  front(0, 0.455, 0.183, 0.19, 0.04, SHADE.MetalDark)
  bolts(0.565, 0.145, -0.085, 0.085, 3, SHADE.MetalDark)

  // The shoulder yoke: one continuous plate carried across both shoulders,
  // wider than the hips by half again. That flare is the proportion this unit
  // is found by — the Nullifier is a spire on a tripod, the Drone Swarm a
  // scattered cloud, and nothing else in the faction is a top-heavy wedge
  // standing on two legs.
  big.wedge(0, 0.54, -0.02, 0.5, 0.12, 0.3, 0.03, SHADE.Owner, SHADE.Owner)
  seam(0, 0.661, -0.02, 0.4, 0.045, SHADE.MetalDark)
  bolts(0.659, 0.085, -0.15, 0.15, 3, SHADE.MetalDark)

  // Shoulder cops in bare plate, capping the yoke at both ends and flaring out
  // over it — a negative inset again, so the outer edge is a lip rather than a
  // chamfer. They are the widest thing on the model, and the value step out at
  // the shoulder is what stops the yoke reading as one flat lid.
  for (const sx of [-1, 1]) {
    big.wedge(sx * 0.275, 0.5, -0.02, 0.16, 0.195, 0.28, -0.025, SHADE.Metal, SHADE.Metal)
  }
  flank(0.348, 0.55, -0.02, 0.2, 0.06, SHADE.Metal)

  // A work lamp clamped to the free shoulder. It is four hundredths of a unit
  // across and it earns its place, because it is the only lit thing on the side
  // of the model the tool is not on — without it the figure is bright down one
  // flank and dead down the other.
  big.box(-0.275, 0.69, 0.08, 0.09, 0.07, 0.09, SHADE.Accent)

  // Head: a skin band standing well clear of the shoulders, banded top and
  // bottom, under a helmet that overhangs it on all four sides. It is narrow on
  // purpose — a plate this wide with nothing rising out of it is a vehicle
  // whatever else is bolted to it, and the knob above the shoulder line is the
  // whole of the difference. The armour opens here and nowhere else, which is
  // also the only warm value on a model that is otherwise steel and team green.
  big.box(0, 0.6, 0.035, 0.2, 0.21, 0.16, SHADE.Accent, SHADE.MetalDark)
  front(0, 0.7, 0.116, 0.15, 0.045, SHADE.MetalDark)
  big.wedge(0, 0.8, 0.035, 0.26, 0.08, 0.22, 0.025, SHADE.OwnerLight, SHADE.OwnerLight)
  seam(0, 0.881, 0.035, 0.05, 0.16, SHADE.MetalDark)

  // Back rack: a frame and one pressure bottle, kept below the helmet so the
  // top of the silhouette stays a head. The bottle is off to the flank the
  // camera gets rather than centred, because a symmetrical pack behind a
  // symmetrical yoke came out as one lozenge from overhead.
  big.box(0.015, 0.32, -0.19, 0.32, 0.23, 0.12, SHADE.MetalMid, SHADE.Metal)
  seam(0.015, 0.551, -0.19, 0.22, 0.045, SHADE.Metal)
  big.cylinder(0.15, 0.3, -0.215, 0.075, 0.37, 6, SHADE.MetalMid, SHADE.Metal)
  big.cylinder(0.15, 0.67, -0.215, 0.042, 0.04, 6, SHADE.Metal, SHADE.MetalMid)

  // The feed line: a ribbed hose off the bottle, down the flank and forward to
  // the tool, run long for the same reason the belt is. It stands outboard of
  // the arm rather than against it, so the flank the camera gets carries a
  // broken edge instead of one flat plate.
  const hose = new MeshBuilder()
  hose.treadBand(0, 0, 0.4, 0.075, SHADE.MetalDark)
  big.merge(hose, 0.235, 0.345, -0.03, 1, 0)

  // The right arm ends in a cutting rig carried at the waist, and this is the
  // part that says *support*: blunt, short, angled down, with no straight line
  // coming out of it. A barrel carried level would make this a rifleman at the
  // size it is actually seen. It sits high enough to clear the legs, because a
  // tool hung down beside the shins fills in the one gap that proves there is a
  // person here rather than a crate.
  big.box(0.235, 0.33, 0.075, 0.14, 0.22, 0.16, SHADE.MetalMid, SHADE.Metal)
  big.box(0.235, 0.245, 0.185, 0.18, 0.175, 0.22, SHADE.MetalMid, SHADE.Metal)
  front(0.235, 0.285, 0.296, 0.14, 0.05, SHADE.MetalMid)
  big.box(0.235, 0.275, 0.335, 0.1, 0.06, 0.11, SHADE.Accent)

  // The left hand carries a cable drum instead, so the two sides never read as
  // a matched pair of arms — and so the unit walking into somebody's building
  // is holding something to plug in rather than something to fire.
  big.cylinder(-0.235, 0.3, -0.02, 0.095, 0.17, 6, SHADE.MetalMid, SHADE.Metal)
  big.box(-0.235, 0.46, -0.02, 0.045, 0.12, 0.045, SHADE.MetalMid)

  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const skirmisher: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]

  // A dart, poured in one piece. Stations tail to prow, at twice final size:
  // [z, xBelly, xChine, xDeck, yBelly, yChine, yDeck]. Every station is the
  // same four-point section — a belly, a narrow chine facet where the two
  // flank planes meet, a deck — so the whole flank is two continuous planes
  // with a strip of bare alloy between them, and the shape of the vehicle is
  // the shape of that strip.
  const stations = [
    [-0.88, 0.24, 0.34, 0.22, 0.2, 0.33, 0.44],
    [-0.62, 0.29, 0.42, 0.3, 0.16, 0.32, 0.53],
    [-0.1, 0.27, 0.39, 0.29, 0.14, 0.3, 0.55],
    [0.42, 0.2, 0.3, 0.18, 0.16, 0.28, 0.46],
    [0.96, 0.05, 0.09, 0.04, 0.2, 0.25, 0.29],
  ]
  /** The section at any z, so fittings can be let into a surface that moves. */
  const sample = (z: number): number[] => {
    let i = 0
    while (i < stations.length - 2 && z > stations[i + 1][0]) i++
    const a = stations[i]
    const b = stations[i + 1]
    const t = (z - a[0]) / (b[0] - a[0])
    return a.map((v, k) => v + (b[k] - v) * t)
  }
  const profile = (s: number[], sx: number): number[][] => [
    [sx * s[1], s[4]],
    [sx * s[2], s[5] - 0.075],
    [sx * s[2], s[5] + 0.075],
    [sx * s[3], s[6]],
  ]
  const BANDS: Role[] = [SHADE.OwnerDark, SHADE.MetalMid, SHADE.Owner]

  const big = new MeshBuilder()
  for (let i = 0; i < stations.length - 1; i++) {
    const a = stations[i]
    const b = stations[i + 1]
    const za = a[0]
    const zb = b[0]
    for (const sx of [1, -1]) {
      const pa = profile(a, sx)
      const pb = profile(b, sx)
      for (let k = 0; k < 3; k++) {
        const q0 = [pa[k][0], pa[k][1], za]
        const q1 = [pa[k + 1][0], pa[k + 1][1], za]
        const q2 = [pb[k + 1][0], pb[k + 1][1], zb]
        const q3 = [pb[k][0], pb[k][1], zb]
        if (sx > 0) big.quad(q0, q1, q2, q3, BANDS[k])
        else big.quad(q3, q2, q1, q0, BANDS[k])
      }
    }
    // The deck in three lanes, so a panel line runs the length of the casting
    // and bends wherever the casting bends. A raised strip could not do that
    // without being re-cut at every station; a lane costs two triangles a
    // segment and follows every change of width in the shape.
    for (const [f0, f1, shade] of [
      [-1, -0.5, SHADE.OwnerDark],
      [-0.5, 0.5, SHADE.Owner],
      [0.5, 1, SHADE.OwnerDark],
    ] as Array<[number, number, Role]>) {
      big.quad(
        [f0 * b[3], b[6], zb],
        [f1 * b[3], b[6], zb],
        [f1 * a[3], a[6], za],
        [f0 * a[3], a[6], za],
        shade,
      )
    }
  }
  // End caps, closed band by band off the same section. The tail plate is the
  // one hard value break along the length of the body; the prow is four
  // triangles of chisel and stays in colour.
  for (const [s, front, shade] of [
    [stations[0], false, SHADE.MetalDark],
    [stations[stations.length - 1], true, SHADE.Owner],
  ] as Array<[number[], boolean, Role]>) {
    const p = profile(s, 1)
    for (let k = 0; k < 3; k++) {
      const q = [
        [p[k][0], p[k][1], s[0]],
        [-p[k][0], p[k][1], s[0]],
        [-p[k + 1][0], p[k + 1][1], s[0]],
        [p[k + 1][0], p[k + 1][1], s[0]],
      ]
      if (front) big.quad(q[1], q[0], q[3], q[2], shade)
      else big.quad(q[0], q[1], q[2], q[3], shade)
    }
  }

  // A crest down the nose: the deck's two panels meeting in a raised
  // centreline that runs out of height as it reaches the prow. It is not a
  // strip laid on the deck — it is the deck, folded.
  const CREST0 = 0.5
  const CREST1 = 0.96
  for (let i = 0; i < 2; i++) {
    const za = CREST0 + ((CREST1 - CREST0) * i) / 2
    const zb = CREST0 + ((CREST1 - CREST0) * (i + 1)) / 2
    const sa = sample(za)
    const sb = sample(zb)
    const ha = 0.02 + 0.075 * (1 - (za - CREST0) / (CREST1 - CREST0))
    const hb = 0.02 + 0.075 * (1 - (zb - CREST0) / (CREST1 - CREST0))
    const wa = Math.min(0.13, sa[3] * 0.85)
    const wb = Math.min(0.13, sb[3] * 0.85)
    const c = 0.035
    big.quad(
      [-c, sb[6] + hb, zb],
      [c, sb[6] + hb, zb],
      [c, sa[6] + ha, za],
      [-c, sa[6] + ha, za],
      SHADE.MetalMid,
    )
    big.quad([wb, sb[6], zb], [wa, sa[6], za], [c, sa[6] + ha, za], [c, sb[6] + hb, zb], SHADE.Owner)
    big.quad(
      [-wa, sa[6], za],
      [-wb, sb[6], zb],
      [-c, sb[6] + hb, zb],
      [-c, sa[6] + ha, za],
      SHADE.Owner,
    )
  }

  // A dorsal fin swept back over the engine deck, drawn as one blade rather
  // than a stack of boxes: four points in the ZY plane, extruded a finger's
  // width either side. It is the whole rear silhouette, and the reason this
  // reads as fast from directly above.
  const fin = [
    [-0.88, 0.44],
    [-0.38, 0.55],
    [-0.6, 0.76],
    [-0.86, 0.69],
  ]
  const ft = 0.036
  big.quad(
    [ft, fin[0][1], fin[0][0]],
    [ft, fin[3][1], fin[3][0]],
    [ft, fin[2][1], fin[2][0]],
    [ft, fin[1][1], fin[1][0]],
    SHADE.Owner,
  )
  big.quad(
    [-ft, fin[0][1], fin[0][0]],
    [-ft, fin[1][1], fin[1][0]],
    [-ft, fin[2][1], fin[2][0]],
    [-ft, fin[3][1], fin[3][0]],
    SHADE.Owner,
  )
  for (let i = 1; i < fin.length; i++) {
    const p = fin[i]
    const q = fin[(i + 1) % fin.length]
    // The crest of the blade is lit and its edges are bare alloy: from directly
    // above the fin is a line rather than a face, so the one band the camera is
    // guaranteed to get has to be the brightest thing on the vehicle.
    const shade = i === 2 ? SHADE.Accent : SHADE.MetalMid
    big.quad([ft, p[1], p[0]], [ft, q[1], q[0]], [-ft, q[1], q[0]], [-ft, p[1], p[0]], shade)
  }

  // Louvres let into the rear deck either side of the fin, stepped down with
  // the deck's own fall so they sit in the surface rather than on it.
  for (let i = 0; i < 3; i++) {
    const z = -0.36 - i * 0.13
    const s = sample(z)
    for (const sx of [-1, 1]) {
      big.box(sx * s[3] * 0.62, s[6] - 0.015, z, 0.16, 0.045, 0.07, SHADE.MetalDark)
    }
  }

  // Intakes half-sunk into the rear cheeks, under the chine's overhang.
  for (const sx of [-1, 1]) {
    big.wedge(sx * 0.3, 0.2, -0.5, 0.16, 0.14, 0.34, 0.04, SHADE.MetalDark, SHADE.MetalMid)
  }
  // Gills let into the flank between the intakes and the waist, each one
  // sitting on the chine line so the alloy strip appears to widen and close
  // again along the length of the vehicle.
  for (let i = 0; i < 2; i++) {
    const z = -0.14 + i * 0.17
    const s = sample(z)
    for (const sx of [-1, 1]) {
      big.box(sx * (s[2] - 0.02), s[5] - 0.02, z, 0.05, 0.11, 0.07, SHADE.MetalDark)
    }
  }
  // The sensor rail this faction hangs its scouting on, faired along the
  // starboard chine and lit along its top. One side only: it is the single
  // thing on the vehicle that is not mirrored, and at this size an asymmetry
  // is worth more than another pair of anything.
  big.wedge(0.33, 0.28, 0.16, 0.1, 0.1, 0.56, 0.03, SHADE.MetalMid, SHADE.Accent)
  // A low faceted canopy let into the forward deck, glazed on top, sitting
  // clear of the lance's swing so it is not one grey mass with the gun.
  big.wedge(0, 0.4, 0.42, 0.24, 0.13, 0.3, 0.06, SHADE.MetalDark, SHADE.Accent)
  // An intake trunk across the deck behind the canopy, lit along the top: the
  // forward deck is the one large surface the camera never loses, and it
  // cannot be left bare.
  big.box(0, sample(0.24)[6] - 0.03, 0.24, 0.42, 0.06, 0.14, SHADE.MetalDark, SHADE.Accent)
  // A marker light let into the crest, right on the point of the prow.
  big.box(0, 0.3, 0.84, 0.08, 0.06, 0.14, SHADE.Accent)
  // One thrust cowl spanning the transom with a lit throat at each end. The
  // tail is the widest part of the casting and this is what it is wide for.
  big.wedge(0, 0.19, -0.84, 0.5, 0.2, 0.16, 0.05, SHADE.MetalDark, SHADE.MetalMid)
  for (const sx of [-1, 1]) {
    big.box(sx * 0.17, 0.23, -0.93, 0.15, 0.12, 0.07, SHADE.Accent)
  }
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // A hover skirt lofted to the same plan as the belly above it, tucked under
  // the chine's overhang and drawn in two bands: dark plenum, lit lip. The
  // Ascendancy does not touch the ground, and the lip is how that reads at
  // gameplay zoom — a thin line of light all the way round the plan.
  const skirt = new MeshBuilder()
  const SKZ = [-0.86, -0.3, 0.3, 0.9]
  for (let i = 0; i < SKZ.length - 1; i++) {
    const za = SKZ[i]
    const zb = SKZ[i + 1]
    const sa = sample(za)
    const sb = sample(zb)
    const bands: Array<[number, number, number, number, Role]> = [
      [0.72, 0.86, 0.04, 0.11, SHADE.Accent],
      [0.86, 1.02, 0.11, -1, SHADE.MetalDark],
    ]
    for (const [f0, f1, y0, y1, shade] of bands) {
      const xa0 = sa[1] * f0
      const xb0 = sb[1] * f0
      const xa1 = sa[1] * f1
      const xb1 = sb[1] * f1
      const ya1 = y1 < 0 ? sa[4] + 0.02 : y1
      const yb1 = y1 < 0 ? sb[4] + 0.02 : y1
      skirt.quad([xb0, y0, zb], [xa0, y0, za], [xa1, ya1, za], [xb1, yb1, zb], shade)
      skirt.quad([-xa0, y0, za], [-xb0, y0, zb], [-xb1, yb1, zb], [-xa1, ya1, za], shade)
    }
  }
  // Close both ends of the plenum so the skirt is a hull, not two fences.
  for (const [z, front] of [
    [SKZ[0], false],
    [SKZ[SKZ.length - 1], true],
  ] as Array<[number, boolean]>) {
    const s = sample(z)
    const x0 = s[1] * 0.72
    const x1 = s[1] * 1.02
    const y1 = s[4] + 0.02
    if (front) skirt.quad([-x0, 0.04, z], [x0, 0.04, z], [x1, y1, z], [-x1, y1, z], SHADE.MetalDark)
    else skirt.quad([x0, 0.04, z], [-x0, 0.04, z], [-x1, y1, z], [x1, y1, z], SHADE.MetalDark)
  }
  r.part(PART.Tread).merge(skirt, 0, 0, 0, 0.5)

  // The gun is a cupola cast into the deck, not a box bolted to it: a frustum
  // whose base sits below the deck line and pulls in as it rises, capped by a
  // second, and a bare-alloy mantlet that flows out of it into the lance.
  const gun = new MeshBuilder()
  gun.cylinder(0, 0.46, 0, 0.3, 0.24, 6, SHADE.Owner, SHADE.Owner, 0.21)
  gun.cylinder(0, 0.7, 0, 0.21, 0.06, 6, SHADE.MetalDark, SHADE.Owner, 0.17)
  // A lit periscope off the cupola's centre — the only asymmetry the turret
  // carries, and the thing that says which way it is looking.
  gun.box(0.09, 0.74, 0.02, 0.08, 0.08, 0.08, SHADE.MetalMid, SHADE.Accent)
  gun.wedge(0, 0.52, 0.24, 0.26, 0.18, 0.28, 0.05, SHADE.MetalMid, SHADE.Metal)
  // A lance that steps down once before the emitter, with a lit collar where
  // it leaves the mantlet. A gun that narrows along its length reads as a gun.
  gun.box(0, 0.575, 0.5, 0.13, 0.11, 0.4, SHADE.MetalMid)
  gun.box(0, 0.585, 0.78, 0.09, 0.08, 0.2, SHADE.MetalDark)
  gun.box(0, 0.575, 0.9, 0.12, 0.1, 0.07, SHADE.Accent)
  gun.box(0, 0.585, 0.44, 0.17, 0.09, 0.09, SHADE.Accent)
  r.part(PART.Turret).merge(gun, 0, 0, 0, 0.5)
  return r.build()
}

const mobileNexusFrame: Recipe = () => {
  const r = new RecipeBuilder()

  // Four short bogies rather than two long tracks. The gap between the front and
  // rear pair is the whole silhouette idea: it puts ground under the middle of
  // the vehicle, which nothing else in the faction has anywhere in its outline.
  // The guard over each one takes the mid neutral on its top face only, so the
  // running gear is a lit rail over a near-black track rather than one dark slab.
  const running = new MeshBuilder()
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const x = sx * 0.5
      const z = sz * 0.55
      running.treadBand(x, z, 0.41, 0.3, SHADE.MetalDark)
      running.wedge(x, 0.12, z, 0.32, 0.26, 0.64, 0.03, SHADE.MetalDark, SHADE.MetalMid)
    }
  }
  r.part(PART.Tread).merge(running, 0, 0, 0, 0.5)

  const big = new MeshBuilder()
  // The deck, and a channel fender down each flank. The fenders are the longest
  // edge on the vehicle and they carry the owner colour; their top faces take the
  // dark step, which draws a border round the deck for no triangles at all.
  big.box(0, 0.44, 0, 1.16, 0.14, 1.62, SHADE.Owner, SHADE.Owner)
  for (const sx of [-1, 1]) {
    big.box(sx * 0.6, 0.36, 0, 0.14, 0.24, 1.62, SHADE.Owner, SHADE.OwnerDark)
  }
  // A bow plate raked back off the deck line, and a towing beam closing the tail.
  big.wedge(0, 0.32, 0.88, 1.3, 0.3, 0.16, 0.05, SHADE.OwnerDark, SHADE.MetalMid)
  big.box(0, 0.34, -0.86, 1.32, 0.24, 0.12, SHADE.MetalDark, SHADE.MetalMid)

  // The freight: a dark cradle, the module, and two straps girthed over the top
  // and down both flanks. The straps do the work of six parts — they break the
  // largest horizontal surface on the vehicle and put a raised rib on each of its
  // flanks with the same two boxes.
  big.box(0, 0.58, -0.36, 1.12, 0.08, 0.96, SHADE.MetalDark, SHADE.MetalMid)
  big.box(0, 0.66, -0.36, 0.98, 0.36, 0.88, SHADE.Owner, SHADE.Owner)
  for (const z of [-0.66, -0.12]) {
    big.box(0, 0.62, z, 1.1, 0.42, 0.12, SHADE.MetalDark, SHADE.MetalMid)
  }
  big.box(-0.16, 1.02, -0.42, 0.46, 0.08, 0.4, SHADE.MetalMid, SHADE.Metal)
  // The folded spire, standing on the far corner of the load: a blunt stack, a
  // collar wider than it, and a lit cap. It is the tallest thing on the model and
  // the only part that says what the freight is going to become.
  big.box(0.28, 1.02, -0.24, 0.3, 0.28, 0.3, SHADE.OwnerDark, SHADE.OwnerDark)
  big.box(0.28, 1.3, -0.24, 0.4, 0.1, 0.4, SHADE.MetalMid, SHADE.Metal)
  big.box(0.28, 1.4, -0.24, 0.16, 0.12, 0.16, SHADE.MetalDark, SHADE.Accent)

  // The lifting gantry on the bare deck. An arch is worth its three parts because
  // it is a *hole* in the profile, and a hole survives the quantizer better than
  // any amount of surface does.
  for (const sx of [-1, 1]) {
    big.box(sx * 0.54, 0.58, 0.26, 0.16, 0.62, 0.2, SHADE.MetalDark, SHADE.MetalMid)
  }
  big.box(0, 1.2, 0.26, 1.26, 0.14, 0.24, SHADE.MetalMid, SHADE.MetalMid)

  // Cab on one front corner, engine bay and stacks on the other, both low enough
  // to leave the gantry beam as the roofline forward of the load.
  big.wedge(0.34, 0.58, 0.6, 0.52, 0.32, 0.42, 0.03, SHADE.Owner, SHADE.Owner)
  big.box(0.34, 0.9, 0.6, 0.46, 0.08, 0.38, SHADE.MetalDark, SHADE.MetalMid)
  big.box(0.34, 0.74, 0.8, 0.44, 0.12, 0.06, SHADE.Accent)
  big.box(-0.34, 0.58, 0.62, 0.5, 0.26, 0.38, SHADE.Owner, SHADE.MetalDark)
  big.box(-0.24, 0.84, 0.72, 0.14, 0.34, 0.14, SHADE.MetalDark, SHADE.Accent)
  big.box(-0.46, 0.84, 0.64, 0.11, 0.26, 0.11, SHADE.MetalDark, SHADE.Accent)
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const coreNexus: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 2.95, 2.95, 2, 2)

  // Authored at twice size and merged down, which is what pays for the bolt
  // heads, the door frame and the capped stacks — every one of them is a tenth
  // of a unit or under at final scale, and none of them would have been drawn.
  const big = new MeshBuilder()

  // The deck: one thick armour course laid over the pad, with a raised kerb
  // along the near edge so the slab ends in a lip rather than a printed border.
  big.box(0, 0.16, 0, 5.1, 0.5, 5.1, SHADE.OwnerDark, SHADE.Metal)
  big.box(0, 0.66, 2.35, 5.1, 0.18, 0.4, SHADE.MetalMid, SHADE.Metal)

  // The keep. One blunt welded mass, ribbed down the two faces the fixed camera
  // is ever given and belted across above the door head. Nothing here is
  // chamfered and nothing overhangs: it is plate cut square and welded.
  big.box(-0.84, 0.66, -0.6, 3.2, 1.3, 3.72, SHADE.Owner)
  for (const x of [-2.3, -0.06, 0.62]) {
    big.box(x, 0.66, 1.34, 0.3, 1.3, 0.16, SHADE.OwnerDark)
  }
  for (const z of [-2.1, -1.0, 0.4]) {
    big.box(0.84, 0.66, z, 0.16, 1.3, 0.3, SHADE.OwnerDark)
  }
  big.box(-0.84, 1.22, -0.6, 3.36, 0.22, 3.88, SHADE.MetalDark, SHADE.Metal)
  // Bolt heads along the belt, proud of the ribs so the light finds them.
  for (let i = 0; i < 4; i++) {
    big.box(-1.86 + i * 0.78, 1.26, 1.42, 0.24, 0.24, 0.2, SHADE.Metal)
  }

  // Blast door, framed in heavy section, with a lit sill at the threshold and a
  // hazard strip on the wall beside it.
  big.box(-1.1, 0.66, 1.34, 1.5, 0.56, 0.16, SHADE.MetalMid, SHADE.Metal)
  big.box(-1.1, 0.7, 1.44, 1.5, 0.16, 0.1, SHADE.Accent)
  for (const x of [-1.94, -0.26]) {
    big.box(x, 0.66, 1.38, 0.28, 0.7, 0.2, SHADE.Metal)
  }
  big.box(-1.1, 1.22, 1.38, 2.12, 0.14, 0.2, SHADE.Metal)
  big.box(0.24, 0.94, 1.42, 0.5, 0.16, 0.1, SHADE.Accent)

  // Roof: a sunken deck inside a parapet, so the top of the building has a
  // section to it rather than being one lit rectangle.
  big.box(-0.84, 1.96, -0.6, 3.0, 0.08, 3.5, SHADE.MetalDark)
  big.box(-0.84, 1.96, 1.1, 3.2, 0.22, 0.32, SHADE.OwnerDark, SHADE.Owner)
  big.box(0.6, 1.96, -0.6, 0.32, 0.22, 3.72, SHADE.OwnerDark, SHADE.Owner)
  for (const [x, w, h] of [
    [-1.9, 0.36, 0.24],
    [-1.4, 0.28, 0.42],
    [-0.95, 0.44, 0.18],
  ]) {
    big.box(x, 2.04, -1.8, w, h, 0.5, SHADE.MetalMid, SHADE.Metal)
  }

  // The core housing, stepped in off the roof on its own dark course, and the
  // well itself: a bolted flange, a drum, a lit throat, and an armoured lid
  // held clear of it so the glow escapes through the gap.
  big.box(-0.5, 2.04, 0.05, 2.32, 0.12, 2.02, SHADE.MetalDark)
  big.box(-0.5, 2.16, 0.05, 2.2, 0.36, 1.9, SHADE.Owner)
  big.box(-0.5, 2.52, 0.05, 1.7, 0.14, 1.7, SHADE.MetalDark, SHADE.Metal)
  big.cylinder(-0.5, 2.66, 0.05, 0.6, 0.26, 6, SHADE.MetalMid, SHADE.Metal)
  big.cylinder(-0.5, 2.92, 0.05, 0.54, 0.12, 6, SHADE.Accent)
  big.box(-0.5, 3.04, 0.05, 0.34, 0.14, 0.34, SHADE.MetalDark)
  big.box(-0.5, 3.18, 0.05, 0.96, 0.12, 0.96, SHADE.MetalDark, SHADE.Metal)

  // Plant wing down the near flank: a lower slab in bare alloy, banded in owner
  // colour under its roofline, ribbed on the outboard face, with the material
  // door at the near end and a cooler on the roof.
  big.box(1.74, 0.66, 0.6, 1.5, 0.9, 3.3, SHADE.MetalMid, SHADE.Metal)
  big.box(1.74, 1.28, 0.6, 1.6, 0.2, 3.4, SHADE.Owner)
  for (const z of [-0.4, 1.2]) {
    big.box(2.53, 0.66, z, 0.16, 0.9, 0.32, SHADE.MetalDark)
  }
  big.box(1.74, 1.56, 0.6, 1.3, 0.1, 3.0, SHADE.MetalDark, SHADE.Metal)
  big.box(1.74, 0.7, 2.3, 1.0, 0.66, 0.14, SHADE.Accent)
  big.box(1.74, 1.36, 2.28, 1.4, 0.18, 0.18, SHADE.Metal)
  big.box(1.8, 1.66, 1.5, 1.0, 0.28, 0.9, SHADE.MetalDark, SHADE.Metal)
  big.box(1.8, 1.94, 1.5, 0.7, 0.08, 0.6, SHADE.Accent)

  // Two stacks off the wing roof, unequal, both capped in a wider plate.
  big.box(1.4, 1.66, -0.3, 0.5, 1.2, 0.5, SHADE.MetalDark)
  big.box(1.4, 2.86, -0.3, 0.66, 0.16, 0.66, SHADE.Metal)
  big.box(2.05, 1.66, 0.35, 0.38, 0.86, 0.38, SHADE.MetalDark)
  big.box(2.05, 2.52, 0.35, 0.5, 0.14, 0.5, SHADE.Metal, SHADE.Accent)

  // Gantry over the yard, standing clear above the roofline. It is the one part
  // of the building with sky inside its own outline, and it is doing work: a
  // trolley, a fall, and a container stack on the deck under it. Kept off the
  // door so the two read as separate things rather than as one dark patch.
  for (const x of [-2.34, -0.2]) {
    big.box(x, 0.66, 1.86, 0.3, 2.44, 0.3, SHADE.Metal)
  }
  big.box(-1.27, 3.1, 1.86, 2.5, 0.3, 0.44, SHADE.Metal, SHADE.MetalDark)
  big.box(-1.9, 2.8, 1.86, 0.56, 0.3, 0.54, SHADE.MetalDark, SHADE.Metal)
  big.box(-1.9, 1.9, 1.86, 0.14, 0.9, 0.14, SHADE.Metal)
  big.box(-1.9, 0.66, 1.86, 0.8, 0.64, 0.7, SHADE.Owner, SHADE.OwnerLight)
  big.box(-1.9, 1.3, 1.9, 0.62, 0.34, 0.56, SHADE.MetalMid, SHADE.Metal)

  hull.merge(big, 0, 0, 0, 0.5)

  // A floodlight on a plain box mast in the far corner of the yard, where the
  // deck would otherwise be bare.
  const accent = r.part(PART.Accent)
  const pole = new MeshBuilder()
  pole.box(2.25, 0.66, -2.15, 0.2, 2.5, 0.2, SHADE.Metal)
  pole.box(2.25, 3.16, -1.95, 0.5, 0.3, 0.36, SHADE.MetalDark)
  pole.box(2.25, 3.22, -1.74, 0.36, 0.2, 0.1, SHADE.Accent)
  accent.merge(pole, 0, 0, 0, 0.5)
  return r.build()
}

const fusionCore: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 1.95, 1.95, 2, 2)

  const big = new MeshBuilder()

  // The pedestal: two octagonal courses of plate filling the pad corner to
  // corner, stepped so the building meets the ground in a shoulder rather than
  // an edge. Its lit top is the walkway ring the vessel stands in.
  big.cylinder(0, 0, 0, 1.9, 0.62, 8, SHADE.MetalDark)
  big.cylinder(0, 0.62, 0, 1.7, 0.36, 8, SHADE.MetalDark)
  big.cylinder(0, 0.98, 0, 1.78, 0.08, 8, SHADE.MetalDark, SHADE.Metal)

  // The vessel: six plates with a weld rib standing proud of every joint, set so
  // one plate faces the camera square on. The ribs are the whole reason for the
  // hexagon — a drum of lit facets separated by dark lines has a section to it
  // from any angle, where a smooth barrel has none.
  const facet = new MeshBuilder()
  facet.box(0, 1.06, 1.24, 1.3, 2.5, 0.3, SHADE.Owner)
  facet.box(0.63, 1.02, 1.28, 0.18, 2.58, 0.38, SHADE.MetalDark)
  for (let i = 0; i < 6; i++) big.merge(facet, 0, 0, 0, 1, Math.PI / 4 + (i * Math.PI) / 3)

  // Foot ring, pressure belt, head flange: three heavy rings clamping the drum,
  // each standing out past the plates so it throws its own line.
  big.cylinder(0, 1.24, 0, 1.54, 0.24, 8, SHADE.MetalDark, SHADE.Metal)
  big.cylinder(0, 2.16, 0, 1.6, 0.34, 8, SHADE.MetalDark, SHADE.Metal)
  big.cylinder(0, 3.28, 0, 1.62, 0.3, 8, SHADE.MetalDark, SHADE.Metal)

  // Sight glasses into the containment, on the three plates the camera is given,
  // each with a downcomer run from it to the walkway.
  const port = new MeshBuilder()
  port.box(0, 2.66, 1.32, 0.56, 0.44, 0.26, SHADE.Accent)
  port.box(0.3, 1.48, 1.4, 0.24, 1.18, 0.24, SHADE.MetalDark)
  for (const t of [-1, 0, 1]) big.merge(port, 0, 0, 0, 1, Math.PI / 4 + (t * Math.PI) / 3)

  // The head: one dark slab clamped down across the drum, a lug at each corner in
  // the owner's colour, and the throat it vents through open in the middle of it.
  big.box(0, 3.58, 0, 1.66, 0.32, 1.66, SHADE.MetalDark, SHADE.MetalMid)
  for (const [x, z] of [
    [0.7, 0.7],
    [-0.7, 0.7],
    [0.7, -0.7],
    [-0.7, -0.7],
  ]) {
    big.box(x, 3.9, z, 0.36, 0.18, 0.36, SHADE.Owner, SHADE.OwnerLight)
  }
  big.box(0, 3.9, 0, 0.86, 0.16, 0.86, SHADE.Accent)

  // Vent pipes strapped to the drum's four flanks, running from the pad to a
  // capped throat below the head. Narrow and bright on purpose: they have to
  // break the outline without hiding the vessel they are cooling.
  const pipe = new MeshBuilder()
  pipe.box(0, 0, 1.68, 0.34, 3.2, 0.34, SHADE.Metal)
  pipe.box(0, 1.06, 1.68, 0.48, 0.18, 0.48, SHADE.MetalDark)
  pipe.box(0, 3.2, 1.68, 0.5, 0.16, 0.5, SHADE.MetalDark, SHADE.Metal)
  pipe.box(0, 3.36, 1.68, 0.3, 0.14, 0.3, SHADE.Accent)
  for (let i = 0; i < 4; i++) big.merge(pipe, 0, 0, 0, 1, (i * Math.PI) / 2)

  // Control blockhouse on the ground at the near corner, where the two nearest
  // pipes leave a gap. The one asymmetric thing on the building, and the only
  // part of it at a person's height.
  const shed = new MeshBuilder()
  shed.box(0, 0, 2.02, 1.34, 0.86, 0.86, SHADE.MetalMid, SHADE.Metal)
  shed.box(0, 0.7, 2.02, 1.4, 0.18, 0.92, SHADE.Owner)
  shed.box(0, 0.88, 2.02, 1.48, 0.14, 0.98, SHADE.MetalDark, SHADE.Metal)
  shed.box(0, 0.08, 2.42, 0.54, 0.58, 0.1, SHADE.Accent)
  big.merge(shed, 0, 0, 0, 1, Math.PI / 4)

  hull.merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const naniteRefinery: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 2.95, 1.95, 3, 2)

  type Shade = (typeof SHADE)[keyof typeof SHADE]
  const big = new MeshBuilder()

  /**
   * A vat lying on its side: an eight-sided barrel extruded along x, drawn only
   * on the facets a fixed overhead camera can reach. The crown and its two
   * chamfers carry the owner colour, because they are the faces the sun is on;
   * the near flank falls away into the dark step under them, which is what makes
   * a flat-shaded prism read as round rather than as a hexagonal beam.
   */
  const vat = (
    x0: number,
    x1: number,
    ay: number,
    cz: number,
    rad: number,
    flank: Shade,
    crown: Shade,
    head: Shade | null = null,
  ): void => {
    const at = (x: number, k: number, rr = rad): number[] => {
      const a = ((k + 0.5) / 8) * Math.PI * 2
      return [x, ay + Math.sin(a) * rr, cz + Math.cos(a) * rr]
    }
    for (const k of [6, 7, 0, 1, 2]) {
      big.quad(at(x0, k), at(x1, k), at(x1, k + 1), at(x0, k + 1), k >= 6 ? flank : crown)
    }
    if (head === null) return
    for (let i = 1; i < 7; i++) {
      big.tri(at(x1, 0), at(x1, i + 1), at(x1, i), head)
    }
  }

  // The plinth: two courses of plate, the deck high enough to stand clear of the
  // ground clutter, with a kerb bolted along the near edge so the slab ends in a
  // lip rather than in a printed border.
  big.box(0, 0.16, 0, 5.7, 0.3, 3.7, SHADE.MetalDark)
  big.box(0, 0.46, 0, 5.54, 0.6, 3.54, SHADE.MetalMid, SHADE.Metal)
  big.box(0, 1.06, 1.64, 5.54, 0.2, 0.26, SHADE.Owner, SHADE.OwnerLight)
  // Buttresses down the near wall of the plinth. It is the longest unbroken
  // surface on the building and it faces the camera, so it gets ribs standing
  // proud of it rather than a painted line, which that wall is too shaded to
  // hold — drawn a step lighter than the wall, so each one catches its own edge.
  for (const x of [-2.0, -0.7, 0.6, 1.9]) {
    big.box(x, 0.46, 1.79, 0.3, 0.6, 0.16, SHADE.Metal)
  }
  // The corners of a pad are the only part of it a building does not cover, so
  // they are where this one puts its colour on the ground.
  for (const sz of [-1, 1]) {
    big.box(2.66, 1.06, sz * 1.64, 0.22, 0.24, 0.26, SHADE.Owner, SHADE.OwnerLight)
  }

  // Cross frames the barrels sit in, spanning both of them so the pair reads as
  // one rack rather than as two tanks parked side by side.
  for (const x of [-1.2, 0.1, 1.1]) {
    big.box(x, 1.06, -0.2, 0.34, 0.28, 3.0, SHADE.MetalDark, SHADE.Metal)
  }

  // The two vats. The far one is fatter and taller so the pair steps in profile,
  // and standing at -z it is seen whole over the near one.
  vat(-1.7, 1.55, 2.0, -1.0, 0.74, SHADE.OwnerDark, SHADE.Owner, SHADE.MetalMid)
  vat(-1.7, 1.25, 1.86, 0.68, 0.6, SHADE.OwnerDark, SHADE.Owner, SHADE.MetalMid)

  // Saddle straps over the crowns, a heavier flange at each head, and the manway
  // itself — a bolted plate on the one face of a barrel the camera gets square.
  for (const x of [-1.2, -0.1, 1.0]) {
    vat(x - 0.09, x + 0.09, 2.0, -1.0, 0.8, SHADE.MetalDark, SHADE.MetalDark)
  }
  for (const x of [-1.15, -0.15, 0.85]) {
    vat(x - 0.09, x + 0.09, 1.86, 0.68, 0.66, SHADE.MetalDark, SHADE.MetalDark)
  }
  vat(1.43, 1.55, 2.0, -1.0, 0.84, SHADE.Metal, SHADE.Metal)
  vat(1.13, 1.25, 1.86, 0.68, 0.7, SHADE.Metal, SHADE.Metal)
  big.box(1.6, 1.76, -1.0, 0.14, 0.5, 0.5, SHADE.MetalDark, SHADE.Metal)
  big.box(1.3, 1.66, 0.68, 0.14, 0.42, 0.42, SHADE.MetalDark, SHADE.Metal)
  // A lit level gauge on the near barrel's shaded flank: the one thing on a tank
  // that says how full it is, and the only light on that whole face.
  big.box(0.55, 1.94, 1.24, 0.8, 0.14, 0.1, SHADE.Accent)

  // Relief plant standing off the far vat's crown, and the feed line running the
  // length of the building in the trough between the two barrels.
  big.box(-0.55, 2.68, -1.0, 0.22, 0.4, 0.22, SHADE.Metal)
  big.box(-0.55, 3.08, -1.0, 0.3, 0.1, 0.3, SHADE.Accent)
  big.box(0.62, 2.68, -1.0, 0.18, 0.24, 0.18, SHADE.MetalDark, SHADE.Accent)
  big.box(-0.2, 2.42, -0.16, 4.0, 0.18, 0.18, SHADE.Metal)

  // The crusher house at the far end: one blunt welded mass on a dark course,
  // stepped back once under a dark roof so the tallest thing on the building is
  // not one grey plate, with a lit charge window on the face that is seen.
  big.box(-2.22, 1.06, -0.2, 1.3, 0.18, 3.2, SHADE.MetalDark)
  big.box(-2.22, 1.24, -0.2, 1.22, 1.5, 3.1, SHADE.Owner)
  big.box(-2.22, 2.74, -0.2, 1.34, 0.18, 3.24, SHADE.MetalDark, SHADE.MetalMid)
  big.box(-2.22, 2.92, -0.5, 1.0, 0.36, 2.3, SHADE.Owner, SHADE.MetalDark)
  big.box(-2.22, 2.28, 1.36, 0.86, 0.18, 0.08, SHADE.Accent)
  big.box(-2.3, 1.34, 1.38, 0.62, 0.66, 0.1, SHADE.MetalMid, SHADE.Metal)
  big.box(-1.68, 1.4, 1.38, 0.16, 0.5, 0.1, SHADE.Accent)
  // A surge drum and a capped stack on the roof, both standing clear of it.
  big.cylinder(-2.5, 3.28, -1.1, 0.32, 0.5, 6, SHADE.MetalMid, SHADE.Metal)
  big.box(-1.95, 3.28, 0.2, 0.34, 0.42, 0.34, SHADE.MetalDark)
  big.box(-1.95, 3.7, 0.2, 0.48, 0.14, 0.48, SHADE.Metal)

  // The loading yard at the near end: a raised grate a harvester tips onto, the
  // intake throat under the barrel heads, and bumpers on the outside corners.
  big.box(2.2, 1.06, -0.1, 1.16, 0.16, 2.9, SHADE.MetalDark, SHADE.MetalMid)
  for (const z of [-1.1, -0.3, 0.5]) {
    big.box(2.2, 1.22, z, 1.16, 0.1, 0.3, SHADE.MetalDark, SHADE.Metal)
  }
  big.box(2.2, 1.07, 1.46, 1.16, 0.02, 0.28, SHADE.Accent)
  big.box(1.8, 1.06, 0.68, 0.44, 0.94, 0.94, SHADE.MetalMid, SHADE.Metal)
  big.box(2.04, 1.34, 0.68, 0.1, 0.44, 0.54, SHADE.Accent)
  big.box(1.84, 1.06, -1.3, 0.5, 0.66, 0.7, SHADE.Owner, SHADE.OwnerLight)
  big.box(1.86, 1.72, -1.3, 0.42, 0.12, 0.6, SHADE.MetalDark)
  for (const z of [-1.5, 1.4]) {
    big.box(2.74, 1.06, z, 0.22, 0.44, 0.22, SHADE.Metal, SHADE.Accent)
  }

  // A gantry over the yard, the one place on the building with sky inside its
  // own outline: two legs, a beam, and a grab hanging off it.
  for (const z of [-1.5, 1.3]) {
    big.box(2.5, 1.06, z, 0.24, 1.9, 0.24, SHADE.Metal)
  }
  big.box(2.5, 2.96, -0.1, 0.3, 0.24, 3.2, SHADE.Metal, SHADE.MetalDark)
  big.box(2.5, 2.72, 0.5, 0.44, 0.28, 0.5, SHADE.MetalDark, SHADE.Metal)
  big.box(2.5, 2.34, 0.5, 0.12, 0.4, 0.12, SHADE.Metal)

  hull.merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const replicationVat: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 1.95, 1.95, 3, 3)

  const big = new MeshBuilder()
  type Shade = (typeof SHADE)[keyof typeof SHADE]

  // A drum lying on its side, which is the one mass this faction's primitives
  // cannot make: `cylinder` extrudes along Y and its walls take an inward
  // normal, so it is wound here by hand as an octagonal prism about X. Only the
  // four upper facets and the +x cap are ever seen, and the value has to step
  // across them — a flat-shaded prism reads as round only if it is shaded round.
  const at = (x: number, k: number, cy: number, cz: number, rad: number): number[] => {
    const a = ((k + 0.5) / 8) * Math.PI * 2
    return [x, cy + Math.sin(a) * rad, cz + Math.cos(a) * rad]
  }
  const drum = (
    x0: number,
    x1: number,
    cy: number,
    cz: number,
    rad: number,
    crown: Shade,
    chine: Shade,
    belly: Shade,
    cap?: Shade,
  ): void => {
    for (const k of [7, 0, 1, 2]) {
      big.quad(
        at(x0, k, cy, cz, rad),
        at(x1, k, cy, cz, rad),
        at(x1, k + 1, cy, cz, rad),
        at(x0, k + 1, cy, cz, rad),
        k === 7 ? belly : k === 0 ? chine : crown,
      )
    }
    if (cap !== undefined) {
      for (let i = 1; i < 7; i++) {
        big.tri(at(x1, 0, cy, cz, rad), at(x1, i + 1, cy, cz, rad), at(x1, i, cy, cz, rad), cap)
      }
    }
  }

  // The deck. Two courses of plate, the upper one taken above the debris line,
  // kerbed along the two edges the camera is given and bolted the length of
  // both — the rivet heads are the cheapest thing on the model and the one that
  // says how it was put together.
  big.box(0, 0, 0, 3.5, 0.6, 3.5, SHADE.MetalDark)
  big.box(0, 0.6, 0, 3.36, 0.46, 3.36, SHADE.MetalMid, SHADE.Metal)
  big.box(1.6, 1.06, 0, 0.22, 0.2, 3.36, SHADE.Metal)
  big.box(0, 1.06, 1.6, 3.36, 0.2, 0.22, SHADE.Metal)
  for (const t of [-1.2, -0.4, 0.4, 1.2]) {
    big.box(1.6, 1.26, t, 0.22, 0.12, 0.22, SHADE.MetalDark)
    big.box(t, 1.26, 1.6, 0.22, 0.12, 0.22, SHADE.MetalDark)
  }
  big.quad(
    [1.76, 0.2, -0.3],
    [1.76, 0.2, -1.0],
    [1.76, 0.78, -1.0],
    [1.76, 0.78, -0.3],
    SHADE.Accent,
  )

  // The vat: one fat pressure drum across the back of the deck, laid in a bed
  // of plate and ribbed under each belt.
  big.box(-0.12, 1.06, -1.05, 3.2, 0.16, 1.62, SHADE.MetalMid, SHADE.Metal)
  drum(-1.62, 1.38, 2.02, -1.05, 0.72, SHADE.Owner, SHADE.Owner, SHADE.OwnerDark, SHADE.Owner)
  for (const x of [-1.05, -0.1, 0.9]) {
    big.box(x, 1.22, -1.05, 0.46, 0.08, 1.7, SHADE.MetalDark)
    drum(x - 0.16, x + 0.16, 2.02, -1.05, 0.84, SHADE.Metal, SHADE.MetalMid, SHADE.MetalDark)
    big.box(x, 2.78, -1.02, 0.2, 0.14, 0.2, SHADE.MetalDark)
  }

  // The level gauge: one lit line down the shaded quarter of the barrel, which
  // is the only way to say the thing is full of something.
  big.box(-0.15, 2.44, -0.58, 2.5, 0.12, 0.16, SHADE.Accent)

  // Its head: a bolted collar and a lit port, square on to the camera. The
  // round face is the whole point of the building — nothing else in the faction
  // shows the camera a disc.
  drum(1.38, 1.5, 2.02, -1.05, 0.54, SHADE.Metal, SHADE.MetalDark, SHADE.MetalDark)
  drum(1.5, 1.58, 2.02, -1.05, 0.42, SHADE.MetalDark, SHADE.MetalDark, SHADE.MetalDark, SHADE.Accent)

  // Relief valves standing on the flat of the crown, capped wider than their
  // stems so the light catches a lip.
  big.box(-0.7, 2.66, -1.02, 0.34, 0.3, 0.34, SHADE.MetalDark, SHADE.Metal)
  big.box(-0.7, 2.96, -1.02, 0.5, 0.12, 0.5, SHADE.Metal)
  big.box(0.42, 2.66, -1.02, 0.26, 0.16, 0.26, SHADE.MetalDark)
  big.box(0.42, 2.82, -1.02, 0.4, 0.12, 0.4, SHADE.Metal)

  // Two incubator pods on a shared bed plate, lower and further forward, so the
  // vat behind them is seen whole and the front of the building is round too.
  big.box(0, 1.06, 0.95, 3.3, 0.2, 1.2, SHADE.MetalMid, SHADE.Metal)
  for (const [x0, x1] of [
    [-1.56, -0.26],
    [0.14, 1.44],
  ]) {
    drum(x0, x1, 1.76, 0.95, 0.5, SHADE.Owner, SHADE.Owner, SHADE.OwnerDark, SHADE.Owner)
    const mid = (x0 + x1) / 2
    drum(mid - 0.13, mid + 0.13, 1.76, 0.95, 0.62, SHADE.Metal, SHADE.MetalMid, SHADE.MetalDark)
    drum(x1, x1 + 0.1, 1.76, 0.95, 0.32, SHADE.MetalDark, SHADE.MetalDark, SHADE.MetalDark, SHADE.Accent)
    for (const f of [0.28, 0.68]) {
      big.box(x0 + (x1 - x0) * f, 2.01, 1.28, 0.2, 0.2, 0.22, SHADE.Accent)
    }
  }

  // Feed lines out of the vat's shoulder and into the crown of each pod: the
  // only part of the building with sky under it, and what makes the two rows
  // read as one plant rather than as four barrels parked together.
  for (const x of [-0.95, 0.45]) {
    big.box(x, 2.2, 0.3, 0.18, 0.18, 1.4, SHADE.MetalMid, SHADE.Metal)
    big.box(x, 2.16, -0.34, 0.28, 0.24, 0.24, SHADE.MetalDark, SHADE.Metal)
  }

  // The pump house, wedged in the gutter between the two rows and squared off
  // against everything around it that curves.
  big.box(-1.2, 1.06, 0.05, 0.86, 0.88, 0.62, SHADE.MetalMid, SHADE.Metal)
  big.box(-1.2, 1.94, 0.05, 0.98, 0.14, 0.72, SHADE.OwnerDark, SHADE.Owner)
  big.quad(
    [-0.77, 1.3, 0.28],
    [-0.77, 1.3, -0.18],
    [-0.77, 1.8, -0.18],
    [-0.77, 1.8, 0.28],
    SHADE.Accent,
  )

  hull.merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const assemblyHive: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 2.95, 2.95, 2, 2)

  const big = new MeshBuilder()

  // One thick armour course over the pad, kerbed along the near edge so the yard
  // ends in a lip rather than a printed border.
  big.box(0, 0.16, 0, 5.4, 0.42, 5.4, SHADE.MetalDark, SHADE.MetalMid)
  big.box(0, 0.58, 2.52, 5.4, 0.16, 0.36, SHADE.MetalMid, SHADE.Metal)

  // The plant block across the back, kept deliberately low: nothing on this
  // building is allowed above the roofline except the pylons, or they stop being
  // the thing a player picks it out by.
  big.box(0, 0.58, -1.66, 5.0, 1.18, 2.18, SHADE.Owner)
  big.box(0, 1.62, -1.66, 5.16, 0.14, 2.34, SHADE.MetalDark, SHADE.Metal)
  big.box(0, 1.76, -0.66, 5.0, 0.24, 0.22, SHADE.OwnerDark, SHADE.Owner)
  big.box(2.44, 1.76, -1.66, 0.22, 0.24, 2.18, SHADE.OwnerDark, SHADE.Owner)

  // Roof plant, all of it squat: one capped extract and a radiator bank cut
  // thick enough to survive the halving.
  big.box(-1.6, 1.76, -1.85, 0.62, 0.5, 0.62, SHADE.MetalDark)
  big.box(-1.6, 2.26, -1.85, 0.82, 0.18, 0.82, SHADE.Metal)
  big.box(1.5, 1.76, -1.9, 1.5, 0.34, 1.3, SHADE.MetalDark, SHADE.Metal)

  // The pylons: a heavy base course, a belt seam, a narrower shaft stepped in
  // above it with a second seam cut across it, a capping plate, and a strap down
  // the one face the camera gets. This faction accretes its buildings, and a
  // stack of unequal slabs banded at every join is what that looks like once it
  // has been welded out of inch plate.
  for (const sx of [-1, 1]) {
    const x = sx * 1.5
    big.box(x, 0.58, 0, 1.4, 1.32, 1.6, SHADE.Owner)
    big.box(x, 1.84, 0, 1.56, 0.14, 1.74, SHADE.MetalDark, SHADE.Metal)
    big.box(x, 1.98, 0, 1.24, 1.8, 1.44, SHADE.Owner)
    big.box(x, 2.82, 0, 1.36, 0.14, 1.56, SHADE.MetalDark, SHADE.Metal)
    big.box(x, 3.78, 0, 1.4, 0.18, 1.6, SHADE.OwnerDark, SHADE.Owner)
    big.box(x, 0.58, 0.81, 0.28, 1.32, 0.14, SHADE.MetalDark)
  }
  // Their heads differ, because a matched pair reads as decoration: the far
  // pylon takes a beacon, the near one a bolted extract hood.
  big.box(-1.5, 3.96, 0, 0.44, 0.22, 0.44, SHADE.Accent)
  big.box(1.5, 3.96, 0, 0.74, 0.16, 0.74, SHADE.MetalDark, SHADE.Metal)

  // The bay. A dark throat welded between the pylons, a slatted shutter lit from
  // behind it, and the frame bolted on over the top of both — jambs, header, and
  // a threshold the walker steps over on its way out onto the yard.
  big.box(0, 0.58, 0.05, 1.7, 2.6, 1.3, SHADE.MetalDark)
  big.box(0, 0.62, 0.68, 1.62, 2.1, 0.1, SHADE.Accent)
  for (let i = 0; i < 2; i++) {
    big.box(0, 1.24 + i * 0.76, 0.75, 1.66, 0.16, 0.06, SHADE.MetalDark)
  }
  for (const sx of [-1, 1]) {
    big.box(sx * 0.9, 0.58, 0.86, 0.34, 2.5, 0.5, SHADE.MetalMid, SHADE.Metal)
  }
  big.box(0, 2.74, 0.86, 2.3, 0.34, 0.5, SHADE.MetalMid, SHADE.Metal)
  big.box(0, 2.8, 1.14, 0.8, 0.18, 0.1, SHADE.Accent)
  big.box(0, 0.58, 1.26, 1.9, 0.16, 0.7, SHADE.MetalDark, SHADE.Accent)

  // The lintel tying the pylons together over the bay, banded across its face,
  // with their heads left standing well above it.
  big.box(0, 3.08, 0.3, 3.3, 0.44, 0.9, SHADE.MetalDark, SHADE.MetalMid)
  big.box(0, 3.34, 0.74, 3.1, 0.14, 0.12, SHADE.Owner)

  // The yard, decked in owner colour rather than more grey, with a hazard band
  // along the lip where the deck steps down.
  big.box(0, 0.58, 1.68, 4.6, 0.1, 1.94, SHADE.OwnerDark, SHADE.Owner)
  for (const sx of [-1, 1]) {
    big.box(sx * 0.95, 0.68, 2.5, 1.1, 0.07, 0.22, SHADE.Accent)
  }

  // The haul-out rack, run out from under the shutter down the middle of the
  // yard. Drawn long here and halved, which is the only way to get teeth this
  // fine out of the primitive.
  const rack = new MeshBuilder()
  rack.treadBand(0, 1.55, 1.2, 0.9, SHADE.MetalDark)
  big.merge(rack, 0, 0.68, 0, 1)

  hull.merge(big, 0, 0, 0, 0.5)

  // The erecting cradle: four posts and three rails standing open in the yard,
  // with a hook down where it would be holding a limb. It is the only part of
  // the building with sky inside its own outline, and at this zoom that is worth
  // more than any amount of surface on the pylons.
  // Aliased to the hull rather than its own part. `PART.Accent` never rotates and
  // `PART.Hull` rotates with the unit's heading — on a structure, whose heading never
  // changes, those are the same thing, and a second part costs a draw call per owner
  // for no behaviour. The roster budget is real: it went to 202 of 200 without this.
  const accent = hull
  const g = new MeshBuilder()
  for (const sx of [-1, 1]) {
    for (const z of [1.0, 2.24]) {
      g.box(sx * 1.66, 0.58, z, 0.28, 1.76, 0.28, SHADE.MetalMid, SHADE.Metal)
    }
    g.box(sx * 1.66, 2.34, 1.62, 0.32, 0.2, 1.6, SHADE.Metal, SHADE.MetalDark)
  }
  g.box(0, 2.34, 2.24, 3.64, 0.2, 0.32, SHADE.Metal, SHADE.MetalDark)
  g.box(0.65, 1.72, 2.24, 0.12, 0.62, 0.12, SHADE.Metal)
  g.box(0.65, 1.46, 2.24, 0.34, 0.26, 0.34, SHADE.MetalMid, SHADE.Metal)
  accent.merge(g, 0, 0, 0, 0.5)
  return r.build()
}

const signalSpire: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  // A long footprint rather than a square one. The mast stands on a ballast
  // block at one end and a low plant shed is welded on at the other, so the
  // building has a front and a back where every other structure in the faction
  // is a square that reads the same from any side.
  foundation(hull, 1.55, 1.15, 2, 2)

  // Authored at twice size and merged down. The bolt heads on the flanges, the
  // clamps up the cable trunk and the mullions across the array face are three
  // to six hundredths of a unit at final scale, and not one of them would have
  // been drawn if the numbers had been typed at final scale.
  const big = new MeshBuilder()
  const mx = -0.6

  // Ballast. Two courses of plate with a strap welded round the joint, then a
  // team-coloured block ribbed on the two faces the camera is ever given.
  big.box(mx, 0.16, 0, 1.36, 0.32, 1.36, SHADE.MetalMid, SHADE.Metal)
  big.box(mx, 0.36, 0, 1.5, 0.16, 1.5, SHADE.MetalDark, SHADE.Metal)
  big.box(mx, 0.52, 0, 1.1, 0.42, 1.1, SHADE.Owner)
  for (const z of [-0.32, 0.32]) big.box(mx + 0.56, 0.52, z, 0.14, 0.42, 0.26, SHADE.OwnerDark)
  for (const x of [-0.32, 0.32]) big.box(mx + x, 0.52, 0.56, 0.26, 0.42, 0.14, SHADE.OwnerDark)
  // A man-door at the foot of it, which is the only thing on the model that
  // says how big the rest of it is. Its lit panel is a bare quad rather than a
  // box: a structure has seven hundred triangles to spend, a decal costs two
  // where a box costs ten, and the difference buys parts that change the
  // outline. Everything flat and lit on this model is drawn this way.
  big.box(mx + 0.58, 0.52, 0.14, 0.12, 0.36, 0.3, SHADE.MetalDark, SHADE.Metal)
  big.quad(
    [mx + 0.65, 0.56, 0.24],
    [mx + 0.65, 0.56, 0.04],
    [mx + 0.65, 0.8, 0.04],
    [mx + 0.65, 0.8, 0.24],
    SHADE.Accent,
  )
  // A stepped course off the block rather than a taper — this thing is cut and
  // welded, not cast — and a gusset against each face the camera is given.
  big.box(mx, 0.94, 0, 1.24, 0.26, 1.24, SHADE.MetalDark, SHADE.Metal)
  big.box(mx + 0.34, 1.2, 0, 0.3, 0.36, 0.16, SHADE.MetalMid, SHADE.Metal)
  big.box(mx, 1.2, 0.34, 0.16, 0.36, 0.3, SHADE.MetalMid, SHADE.Metal)

  // The mast: plate sections of unequal length, each narrower than the one
  // under it, joined by bolted flanges. The steps in the outline are what makes
  // it read as a mast rather than as a post.
  big.box(mx, 1.2, 0, 0.8, 0.64, 0.8, SHADE.Owner)
  big.box(mx + 0.42, 1.3, -0.14, 0.06, 0.44, 0.3, SHADE.OwnerDark)
  big.box(mx, 1.84, 0, 1.0, 0.12, 1.0, SHADE.MetalDark, SHADE.Metal)
  for (const z of [-0.3, 0, 0.3]) big.box(mx + 0.47, 1.86, z, 0.12, 0.1, 0.14, SHADE.Metal)
  big.box(mx, 1.96, 0, 0.72, 0.54, 0.72, SHADE.Owner)

  // A cable trunk clamped up the near flank, off centre and running the whole
  // height: the one unbroken line on a body made entirely of steps.
  big.box(mx + 0.46, 0.94, -0.22, 0.18, 2.4, 0.24, SHADE.MetalDark)
  for (const y of [1.4, 2.1, 2.9]) {
    big.box(mx + 0.46, y, -0.22, 0.32, 0.12, 0.38, SHADE.Metal)
  }

  // Gallery: a thick deck inside a plate parapet, halfway up, with the mast
  // carrying on out of the middle of it. It is the one place where the outline
  // steps outward instead of in.
  big.box(mx, 2.5, 0, 1.16, 0.14, 1.16, SHADE.MetalDark, SHADE.Metal)
  for (const sz of [-1, 1]) {
    big.box(mx, 2.64, sz * 0.51, 1.16, 0.2, 0.14, SHADE.OwnerDark, SHADE.Owner)
  }
  for (const sx of [-1, 1]) {
    big.box(mx + sx * 0.51, 2.64, 0, 0.14, 0.2, 1.16, SHADE.OwnerDark, SHADE.Owner)
  }

  // Accreted plant, hung on the mast wherever there was room for it: an
  // amplifier case with a louvred face under the gallery, and a cabinet stood on
  // the gallery deck beside the mast.
  big.box(mx + 0.06, 1.9, 0.56, 0.62, 0.5, 0.32, SHADE.MetalMid, SHADE.Metal)
  big.quad(
    [mx - 0.16, 2.0, 0.73],
    [mx + 0.28, 2.0, 0.73],
    [mx + 0.28, 2.28, 0.73],
    [mx - 0.16, 2.28, 0.73],
    SHADE.Accent,
  )
  big.box(mx + 0.32, 2.64, -0.24, 0.3, 0.42, 0.44, SHADE.MetalDark, SHADE.Metal)
  big.quad(
    [mx + 0.48, 2.74, -0.1],
    [mx + 0.48, 2.74, -0.38],
    [mx + 0.48, 2.94, -0.38],
    [mx + 0.48, 2.94, -0.1],
    SHADE.Accent,
  )

  // The top section, in bare alloy so the team-coloured array above it has
  // something to stand against, and the collar and bearing it turns on.
  big.box(mx, 2.5, 0, 0.62, 0.84, 0.62, SHADE.MetalMid, SHADE.Metal)
  big.box(mx, 3.34, 0, 0.84, 0.12, 0.84, SHADE.MetalDark, SHADE.Metal)
  big.cylinder(mx, 3.46, 0, 0.38, 0.18, 8, SHADE.MetalDark, SHADE.Metal)

  // The head: a slab array standing broadside, the widest thing on the model
  // carried at the top of the narrowest. That is the whole silhouette, and
  // everything under it is there to hold it up.
  big.box(mx, 3.64, 0, 0.34, 0.96, 1.66, SHADE.Owner)
  big.box(mx + 0.1, 3.64, 0, 0.36, 0.14, 1.66, SHADE.Owner, SHADE.OwnerLight)
  big.box(mx + 0.1, 4.46, 0, 0.36, 0.14, 1.66, SHADE.Owner, SHADE.OwnerLight)
  for (const sz of [-1, 1]) {
    big.box(mx + 0.06, 3.64, sz * 0.87, 0.42, 0.96, 0.1, SHADE.OwnerDark, SHADE.Owner)
  }
  // The array face itself, proud of the frame so it is lit rather than shaded,
  // and split into panels by mullions.
  big.box(mx + 0.22, 3.78, 0, 0.14, 0.68, 1.44, SHADE.Accent)
  for (const z of [-0.36, 0.36]) {
    big.box(mx + 0.25, 3.78, z, 0.1, 0.68, 0.12, SHADE.MetalDark)
  }
  big.box(mx + 0.26, 4.08, 0, 0.08, 0.08, 1.44, SHADE.MetalDark)
  // A dark hood over the array, overhanging it at both ends so the top of the
  // model is a hard line rather than a taper.
  big.box(mx, 4.6, 0, 0.5, 0.18, 1.78, SHADE.MetalDark)
  // A ranging drum bolted to the hood at one end, because a thing that grew
  // gets its next instrument welded to whatever was already up there.
  big.cylinder(mx, 4.78, -0.6, 0.24, 0.22, 8, SHADE.MetalDark, SHADE.Metal)
  big.box(mx + 0.2, 4.84, -0.6, 0.14, 0.16, 0.28, SHADE.Accent)

  // The shed grafted on at the far end of the pad: a low slab of a thing with a
  // coloured roof course, a cooler, a stack and a door on the near face. It is
  // here to give the mast something squat to be tall against, and to put the
  // building's mass at one end of its footprint rather than in the middle.
  big.box(0.86, 0.16, 0.1, 1.16, 0.66, 1.5, SHADE.MetalMid, SHADE.Metal)
  big.box(0.86, 0.82, 0.1, 1.3, 0.18, 1.62, SHADE.Owner)
  big.box(1.45, 0.16, -0.3, 0.14, 0.66, 0.34, SHADE.MetalDark)
  big.box(0.66, 0.16, 0.86, 0.5, 0.54, 0.12, SHADE.MetalDark, SHADE.Metal)
  big.quad(
    [0.41, 0.2, 0.93],
    [0.91, 0.2, 0.93],
    [0.91, 0.34, 0.93],
    [0.41, 0.34, 0.93],
    SHADE.Accent,
  )
  big.box(0.72, 1.0, -0.26, 0.68, 0.2, 0.62, SHADE.MetalMid, SHADE.Metal)
  big.quad(
    [0.47, 1.21, -0.04],
    [0.97, 1.21, -0.04],
    [0.97, 1.21, -0.48],
    [0.47, 1.21, -0.48],
    SHADE.Accent,
  )
  big.box(1.24, 1.0, 0.5, 0.28, 0.72, 0.28, SHADE.MetalDark)
  big.box(1.24, 1.72, 0.5, 0.42, 0.14, 0.42, SHADE.Metal)
  // The trunking that ties shed to mast, so the two read as one installation
  // rather than as a tower with a crate next to it.
  big.box(0.16, 0.86, 0.1, 1.2, 0.16, 0.22, SHADE.MetalDark, SHADE.Metal)

  hull.merge(big, 0, 0, 0, 0.5)

  // The beacon, on its own whip off one end of the hood.
  const accent = r.part(PART.Accent)
  const lamp = new MeshBuilder()
  lamp.box(mx - 0.1, 4.78, 0.6, 0.16, 0.36, 0.16, SHADE.Metal)
  lamp.box(mx - 0.1, 5.14, 0.6, 0.26, 0.2, 0.26, SHADE.Accent)
  accent.merge(lamp, 0, 0, 0, 0.5)
  return r.build()
}

const wraithSkimmer: Recipe = () => {
  type Shade = (typeof SHADE)[keyof typeof SHADE]
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  const big = new MeshBuilder()

  /**
   * A lifting body, lofted rather than stacked.
   *
   * Five spanwise stations, each a seven-sided section, skinned between: a knife
   * leading edge, a chamfer up onto the deck, a crest running the span, a
   * chamfer back down to the trailing edge, and a flat floor. Every surface on
   * the airframe is a plane meeting another plane at an angle the light can
   * find, which is the only way a shape this thin reads as cast rather than as a
   * coloured tile with parts sitting on it.
   *
   * `[x, leading z, trailing z, deck, floor]`.
   */
  const STATIONS: ReadonlyArray<readonly [number, number, number, number, number]> = [
    [0.0, 1.26, -0.98, 0.62, 0.06],
    [0.64, 0.66, -0.96, 0.45, 0.09],
    [0.99, 0.32, -0.86, 0.33, 0.12],
    [1.28, 0.04, -0.72, 0.25, 0.14],
    // The outer panel bends down. Deck and floor drop together, so the last
    // span is a plane of its own catching its own light, and the crease where
    // it breaks is a line across the wing that no painted seam could match.
    [1.58, -0.28, -0.54, 0.12, 0.02],
  ]

  /** The section at any span position, as `[z, y]` round the profile. */
  const sectionAt = (x: number): Array<[number, number]> => {
    let i = 0
    while (i < STATIONS.length - 2 && STATIONS[i + 1][0] < x) i++
    const a = STATIONS[i]
    const b = STATIONS[i + 1]
    const t = Math.max(0, Math.min(1, (x - a[0]) / (b[0] - a[0])))
    const le = a[1] + (b[1] - a[1]) * t
    const te = a[2] + (b[2] - a[2]) * t
    const top = a[3] + (b[3] - a[3]) * t
    const bot = a[4] + (b[4] - a[4]) * t
    const chord = le - te
    const fore = Math.min(chord * 0.2, 0.5)
    const thick = top - bot
    return [
      [le, bot + thick * 0.34],
      [le - fore, top - thick * 0.16],
      [te + chord * 0.45, top],
      [te + chord * 0.26, top - thick * 0.12],
      [te, bot + Math.max(0.04, thick * 0.2)],
      [te, bot],
      [le - fore * 0.55, bot],
    ]
  }

  /** Where the upper skin sits over a point on the plan, for mounting to. */
  const deckAt = (x: number, z: number): number => {
    const p = sectionAt(Math.abs(x))
    for (let k = 0; k < 4; k++) {
      if (z <= p[k][0] && z >= p[k + 1][0]) {
        const f = (z - p[k][0]) / (p[k + 1][0] - p[k][0] || 1)
        return p[k][1] + (p[k + 1][1] - p[k][1]) * f
      }
    }
    return z > p[0][0] ? p[0][1] : p[4][1]
  }

  const v = (x: number, p: readonly [number, number]): number[] => [x, p[1], p[0]]

  // The left half is the same skin wound backwards. Mirroring a quad reverses
  // its winding, so the vertex order has to be reversed again or that whole wing
  // faces inward and renders as a hole.
  const face = (
    s: number,
    a: number[],
    b: number[],
    c: number[],
    d: number[],
    shade: Shade,
  ): void => {
    if (s > 0) big.quad(a, b, c, d, shade)
    else big.quad(d, c, b, a, shade)
  }

  // Leading chamfer, forward deck, aft deck, elevon, trailing edge, floor, lower
  // chamfer. The airframe is one mass and takes owner colour across all of it;
  // only the elevon band and the shaded underside step off it.
  const SKIN: readonly Shade[] = [
    SHADE.Owner,
    SHADE.Owner,
    SHADE.Owner,
    SHADE.OwnerDark,
    SHADE.MetalDark,
    SHADE.MetalDark,
    SHADE.MetalDark,
  ]

  for (const s of [1, -1]) {
    for (let i = 0; i < STATIONS.length - 1; i++) {
      const a = sectionAt(STATIONS[i][0])
      const b = sectionAt(STATIONS[i + 1][0])
      const xa = STATIONS[i][0] * s
      const xb = STATIONS[i + 1][0] * s
      for (let k = 0; k < 7; k++) {
        const n = (k + 1) % 7
        // The outboard third of the deck steps down a value. A wing is the
        // largest unbroken surface on any aircraft and a step across it is the
        // one panel break that survives to gameplay zoom.
        const outer = i >= 2 && (k === 1 || k === 2)
        const shade = outer ? SHADE.OwnerDark : SKIN[k]
        face(s, v(xa, a[k]), v(xb, b[k]), v(xb, b[n]), v(xa, a[n]), shade)
      }
      // Two proud lines run tip to tip: the elevon hinge along the back, and a
      // swept rib a little inboard of the leading edge. Continuous lines that
      // cross the whole model organise a deck the way a dozen separate panels
      // never could, and they are what says this shape is one casting.
      const rib = (za: number, zb: number, w: number, step: boolean): void => {
        const ya = deckAt(xa, za) + 0.045
        const yb = deckAt(xb, zb) + 0.045
        face(
          s,
          [xa, ya, za + w],
          [xb, yb, zb + w],
          [xb, yb, zb - w],
          [xa, ya, za - w],
          SHADE.MetalDark,
        )
        if (!step) return
        face(
          s,
          [xa, ya - 0.06, za + w],
          [xb, yb - 0.06, zb + w],
          [xb, yb, zb + w],
          [xa, ya, za + w],
          SHADE.MetalDark,
        )
      }
      rib(a[3][0], b[3][0], 0.035, true)
      rib(a[1][0] - 0.14, b[1][0] - 0.14, 0.035, false)
    }
    // Cap the tip, or the wing ends in an open shell.
    const tip = sectionAt(STATIONS[STATIONS.length - 1][0])
    const tx = STATIONS[STATIONS.length - 1][0] * s
    for (let k = 1; k < tip.length - 1; k++) {
      if (s > 0) big.tri(v(tx, tip[0]), v(tx, tip[k + 1]), v(tx, tip[k]), SHADE.OwnerDark)
      else big.tri(v(tx, tip[0]), v(tx, tip[k]), v(tx, tip[k + 1]), SHADE.OwnerDark)
    }
  }

  /**
   * A dorsal fairing blended into the skin: sampled along its length, tapering
   * in width and height towards a point at the front and a nozzle at the back,
   * and sitting on the deck wherever the deck happens to be.
   *
   * A box laid on this hull stands proud at one end and is buried at the other,
   * and a deck of them reads as cargo. Following the surface is what lets the
   * spine belong to the airframe instead of standing on it.
   */
  const fairing = (
    x: number,
    z0: number,
    z1: number,
    hw: number,
    h: number,
    tailF: number,
    shade: Shade,
    topShade: Shade,
  ): Array<{ z: number; w: number; y: number; base: number }> => {
    const steps = 4
    const nodes: Array<{ z: number; w: number; y: number; base: number }> = []
    for (let i = 0; i <= steps; i++) {
      const t = i / steps
      const k =
        Math.min(1, t / 0.3) * (tailF + (1 - tailF) * Math.min(1, (1 - t) / 0.28))
      const z = z0 + (z1 - z0) * t
      const base = deckAt(x, z) - 0.06
      nodes.push({ z, w: hw * k, y: base + h * k, base })
    }
    for (let i = 0; i < steps; i++) {
      const a = nodes[i]
      const b = nodes[i + 1]
      big.quad(
        [x - a.w, a.y, a.z],
        [x + a.w, a.y, a.z],
        [x + b.w, b.y, b.z],
        [x - b.w, b.y, b.z],
        topShade,
      )
      big.quad(
        [x + a.w, a.base, a.z],
        [x + b.w, b.base, b.z],
        [x + b.w, b.y, b.z],
        [x + a.w, a.y, a.z],
        shade,
      )
      big.quad(
        [x - b.w, b.base, b.z],
        [x - a.w, a.base, a.z],
        [x - a.w, a.y, a.z],
        [x - b.w, b.y, b.z],
        shade,
      )
    }
    return nodes
  }

  // One dorsal mass from the nose to the tail carries the cockpit and the
  // intakes. Split into a spine and a pair of engine trunks it was three grey
  // stripes lying side by side, and the wing under them stopped reading at all.
  const spine = fairing(0, 1.2, -0.96, 0.34, 0.28, 0.64, SHADE.OwnerDark, SHADE.Owner)
  const tail = spine[spine.length - 1]
  big.quad(
    [tail.w, tail.base, tail.z],
    [-tail.w, tail.base, tail.z],
    [-tail.w, tail.y, tail.z],
    [tail.w, tail.y, tail.z],
    SHADE.MetalDark,
  )
  // The engine bank sits across the back, either side of the spine: a light
  // neutral mass, which is the one value on this aircraft that is neither green
  // nor near-black, and a lit nozzle in each.
  for (const s of [1, -1]) {
    const ey = deckAt(s * 0.34, -0.7)
    big.box(s * 0.34, ey - 0.16, -0.7, 0.34, 0.22, 0.52, SHADE.MetalMid, SHADE.Metal)
    big.box(s * 0.34, ey - 0.12, -0.94, 0.26, 0.13, 0.1, SHADE.Accent)
  }
  // The canopy, let into the crown rather than perched on it, with a dark frame
  // round it so the brass has something to be bright against, and a lit intake
  // on each shoulder behind it.
  big.box(0, spine[1].y - 0.05, 0.78, 0.3, 0.05, 0.66, SHADE.MetalDark)
  big.box(0, spine[1].y - 0.02, 0.78, 0.22, 0.04, 0.56, SHADE.Accent)
  for (const s of [1, -1]) {
    big.box(s * 0.25, spine[2].y - 0.07, 0.2, 0.14, 0.05, 0.34, SHADE.Accent)
  }
  // A sensor prow standing out of the nose edge — the one part of this airframe
  // allowed to break the outline forwards.
  big.box(0, 0.11, 1.28, 0.16, 0.09, 0.2, SHADE.Accent)

  // Canted tail fins. Nothing else on the airframe leaves the plan, so these are
  // the whole of the vertical read.
  for (const s of [1, -1]) {
    const fy = deckAt(s * 0.82, -0.64)
    big.wedge(s * 0.82, fy - 0.05, -0.66, 0.12, 0.4, 0.56, 0.045, SHADE.OwnerDark, SHADE.OwnerLight)
    // A lamp on each tip. Four pixels of brass at the ends of the span is what
    // measures the span.
    big.box(s * 1.52, deckAt(s * 1.52, -0.36) - 0.04, -0.36, 0.1, 0.07, 0.16, SHADE.Accent)
  }

  // Three recessed racks, dark against the deck, so a spent one still reads as
  // the place a round used to sit.
  const bays: ReadonlyArray<readonly [number, number, number]> = [
    [-0.86, 0.06, 0],
    [0.86, 0.06, 0],
    [0, -0.38, 0.18],
  ]
  for (const [bx, bz, lift] of bays) {
    const by = deckAt(bx, bz) + lift
    big.box(bx, by - 0.04, bz, 0.24, 0.05, 0.6, SHADE.MetalDark)
  }

  hull.merge(big, 0, 0, 0, 0.5)

  // A round rides light against its dark rack, or three of them read as more of
  // the same shadow the rack already is.
  const dart = new MeshBuilder()
  dart.box(0, 0, 0, 0.14, 0.12, 0.5, SHADE.MetalMid, SHADE.Metal)
  dart.box(0, 0.03, -0.29, 0.1, 0.07, 0.1, SHADE.Accent)
  for (let i = 0; i < ORDNANCE.length; i++) {
    const [bx, bz, lift] = bays[i]
    const by = deckAt(bx, bz) + lift
    r.part(ORDNANCE[i]).merge(dart, bx * 0.5, by * 0.5, bz * 0.5, 0.5)
  }
  return r.build()
}

const landingField: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  type Shade = (typeof SHADE)[keyof typeof SHADE]
  const big = new MeshBuilder()

  // A six-sided plan — a wide slab with its two camera-facing corners struck
  // off — so the one building on the map that is mostly floor is still not a
  // rectangle from above.
  const foot = [
    [-1.4, 2.86],
    [1.4, 2.86],
    [2.94, 1.32],
    [2.94, -0.54],
    [-2.94, -0.54],
    [-2.94, 1.32],
  ]
  const seat = [
    [-1.24, 2.6],
    [1.24, 2.6],
    [2.68, 1.16],
    [2.68, -0.54],
    [-2.68, -0.54],
    [-2.68, 1.16],
  ]
  const rim = [
    [-1.36, 2.74],
    [1.36, 2.74],
    [2.8, 1.28],
    [2.8, -0.54],
    [-2.8, -0.54],
    [-2.8, 1.28],
  ]
  const between = (a: number[][], b: number[][], t: number) =>
    a.map((p, i) => [p[0] + (b[i][0] - p[0]) * t, p[1] + (b[i][1] - p[1]) * t])

  const wall = (lo: number[][], hi: number[][], y0: number, y1: number, shade: Shade) => {
    for (let i = 0; i < lo.length; i++) {
      const j = (i + 1) % lo.length
      big.quad(
        [lo[i][0], y0, lo[i][1]],
        [lo[j][0], y0, lo[j][1]],
        [hi[j][0], y1, hi[j][1]],
        [hi[i][0], y1, hi[i][1]],
        shade,
      )
    }
  }
  const paint = (cx: number, y: number, cz: number, w: number, d: number, shade: Shade) => {
    big.quad(
      [cx - w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz - d / 2],
      [cx - w / 2, y, cz - d / 2],
      shade,
    )
  }

  // Ground chamfer, battered wall, and the deck plate proud of both: three
  // courses of edge so half a unit of apron side is never one black band.
  wall(foot, between(foot, seat, 0.34), -0.14, 0.24, SHADE.MetalDark)
  wall(between(foot, seat, 0.34), seat, 0.24, 1.0, SHADE.MetalMid)
  wall(rim, rim, 1.0, 1.22, SHADE.Metal)
  big.quad(
    [rim[0][0], 1.22, rim[0][1]],
    [rim[1][0], 1.22, rim[1][1]],
    [rim[2][0], 1.22, rim[2][1]],
    [rim[5][0], 1.22, rim[5][1]],
    SHADE.MetalMid,
  )
  big.quad(
    [rim[5][0], 1.22, rim[5][1]],
    [rim[2][0], 1.22, rim[2][1]],
    [rim[3][0], 1.22, rim[3][1]],
    [rim[4][0], 1.22, rim[4][1]],
    SHADE.MetalMid,
  )

  // The touchdown octagon: the largest owner-coloured face on the building and
  // the only thing about it a player has to read from straight overhead.
  big.cylinder(0, 1.22, 0.9, 1.36, 0.08, 8, SHADE.OwnerDark, SHADE.Owner)
  paint(0, 1.32, 0.9, 1.44, 0.24, SHADE.Accent)
  paint(0, 1.32, 0.9, 0.24, 1.44, SHADE.Accent)
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      paint(sx * 0.66, 1.32, 0.9 + sz * 0.66, 0.44, 0.12, SHADE.OwnerDark)
      paint(sx * 0.66, 1.32, 0.9 + sz * 0.66, 0.12, 0.44, SHADE.OwnerDark)
    }
  }

  // Lamps round the octagon, standing on the deck rather than painted on it.
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + Math.PI / 8
    big.box(Math.cos(a) * 1.62, 1.22, 0.9 + Math.sin(a) * 1.62, 0.22, 0.16, 0.22, SHADE.Accent)
  }

  // A hazard kerb along the open edge, lane lines down the two margins, and a
  // hatch over the ordnance lift.
  for (let i = -3; i <= 3; i++) {
    paint(i * 0.36, 1.24, 2.52, 0.24, 0.3, i % 2 === 0 ? SHADE.Accent : SHADE.MetalDark)
  }
  paint(2.2, 1.24, 0.9, 0.1, 2.6, SHADE.MetalDark)
  paint(-2.2, 1.24, 0.9, 0.1, 2.6, SHADE.MetalDark)
  paint(2.32, 1.24, -0.02, 0.72, 0.96, SHADE.MetalDark)
  paint(2.32, 1.25, -0.02, 0.5, 0.74, SHADE.Metal)

  // The taxi line, dashed from the octagon back to the middle bay, and cleats
  // to lash a parked airframe down with.
  for (let i = 0; i < 4; i++) {
    paint(0, 1.24, -0.72 - i * 0.4, 0.16, 0.22, SHADE.Accent)
  }
  for (const sx of [-1, 1]) {
    for (const z of [2.0, 0.2]) {
      big.box(sx * 2.2, 1.22, z, 0.3, 0.14, 0.18, SHADE.MetalDark)
    }
  }

  // Bolt heads along the two edges the camera can see.
  for (let i = -2; i <= 2; i++) {
    big.box(i * 0.6, 1.22, 2.64, 0.24, 0.12, 0.24, SHADE.MetalDark)
  }
  for (let i = 0; i < 4; i++) {
    big.box(2.68, 1.22, 0.94 - i * 0.5, 0.24, 0.12, 0.24, SHADE.MetalDark)
  }

  // --- service bar along the back edge ---------------------------------------
  big.wedge(0, -0.14, -1.71, 5.72, 1.14, 2.34, 0.1, SHADE.MetalDark, SHADE.MetalMid)

  // Three armour plates with buttresses between them: the shed the aircraft is
  // pulled into, built as separate slabs rather than as one wall.
  for (const [x, h] of [
    [-1.86, 0.96],
    [0.0, 1.18],
    [1.86, 0.84],
  ]) {
    big.box(x, 1.0, -2.3, 1.7, h, 0.62, SHADE.Owner, SHADE.OwnerLight)
    big.box(x, 1.1, -1.97, 1.12, h * 0.6, 0.08, SHADE.Accent)
  }
  for (const [x, h] of [
    [-2.74, 0.9],
    [-0.93, 1.04],
    [0.93, 1.04],
    [2.74, 0.78],
  ]) {
    big.box(x, 1.0, -2.28, 0.26, h, 0.74, SHADE.MetalMid, SHADE.Metal)
  }

  // Fuel drum and its line out onto the pad, a cabinet, and a crate stack.
  big.cylinder(2.25, 1.0, -1.22, 0.46, 0.9, 6, SHADE.MetalMid, SHADE.Metal)
  big.cylinder(2.25, 1.86, -1.22, 0.3, 0.14, 6, SHADE.MetalDark, SHADE.Accent)
  big.box(1.35, 1.3, -1.22, 1.4, 0.18, 0.18, SHADE.Metal)
  big.box(-2.1, 1.0, -1.2, 0.86, 0.62, 0.6, SHADE.MetalMid, SHADE.Metal)
  big.box(-2.1, 1.28, -0.92, 0.5, 0.2, 0.06, SHADE.Accent)
  big.box(-0.9, 1.0, -1.14, 0.66, 0.36, 0.5, SHADE.MetalDark, SHADE.Metal)
  big.box(-0.9, 1.36, -1.14, 0.5, 0.26, 0.4, SHADE.MetalDark, SHADE.Metal)

  // The rearming gantry: the one place this building puts a gap of sky inside
  // its own outline, and the only part of it taller than a man.
  for (const x of [-1.1, 1.1]) {
    big.box(x, 1.22, -0.24, 0.22, 1.06, 0.22, SHADE.MetalDark)
  }
  big.box(0, 2.28, -0.24, 2.66, 0.24, 0.3, SHADE.Metal)
  big.box(0.5, 2.0, -0.24, 0.34, 0.3, 0.38, SHADE.MetalMid, SHADE.Accent)

  // A loaded ordnance trolley parked clear of the pad.
  big.box(-2.24, 1.22, 1.5, 0.6, 0.2, 1.0, SHADE.MetalDark)
  big.box(-2.24, 1.42, 1.5, 0.44, 0.18, 0.84, SHADE.Accent)

  // The beacon, and the only thread this building puts against the sky.
  big.box(-2.74, 1.9, -2.28, 0.18, 1.1, 0.18, SHADE.MetalDark)
  big.box(-2.74, 3.0, -2.28, 0.26, 0.26, 0.26, SHADE.Accent)

  // Approach lights round the open side, on short posts.
  for (const [x, z] of [
    [2.5, 1.1],
    [1.06, 2.4],
    [-1.06, 2.4],
    [-2.5, 1.1],
  ]) {
    big.box(x, 1.22, z, 0.2, 0.56, 0.2, SHADE.MetalDark)
    big.box(x, 1.78, z, 0.26, 0.24, 0.26, SHADE.Accent)
  }

  hull.merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const launchSpire: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 2.95, 2.95, 2, 2)

  // Authored at twice size and merged down. Almost everything on this building
  // is at the scale of a rivet head, a strake or a lattice brace, and none of it
  // would have been drawn at final size.
  const big = new MeshBuilder()
  const tx = -0.55
  const tz = -0.9

  // The apron: two thick courses taken to 1.06 so the deck stands clear of the
  // ground clutter, shoved back and left rather than centred, which leaves an
  // open yard along the near edge for the plant.
  big.box(-0.35, 0.16, -0.25, 4.2, 0.5, 3.6, SHADE.MetalDark, SHADE.MetalMid)
  big.box(-0.35, 0.66, -0.25, 3.9, 0.4, 3.3, SHADE.MetalMid, SHADE.Metal)
  // Deck lanes as flat quads rather than proud strips: on a horizontal surface
  // the colour step alone carries them, at two triangles each.
  big.quad(
    [-2.15, 1.065, 1.38],
    [1.45, 1.065, 1.38],
    [1.45, 1.065, 1.24],
    [-2.15, 1.065, 1.24],
    SHADE.Accent,
  )
  big.quad(
    [-2.15, 1.065, 0.42],
    [1.45, 1.065, 0.42],
    [1.45, 1.065, 0.3],
    [-2.15, 1.065, 0.3],
    SHADE.MetalDark,
  )

  // --- the shaft -------------------------------------------------------------
  // Narrow and blunt, on a flare course: four heavy corner columns of plate with
  // the barrel welded in between them. Nothing is chamfered and nothing
  // overhangs. The proportion is the whole identity — a sleeve barely wider than
  // what stands in it, on a pad twice its width.
  big.box(tx, 1.06, tz, 2.3, 0.32, 2.3, SHADE.MetalDark, SHADE.MetalMid)
  big.box(tx, 1.38, tz, 1.9, 1.9, 1.9, SHADE.Owner)
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      big.box(tx + sx * 0.95, 1.38, tz + sz * 0.95, 0.48, 1.9, 0.48, SHADE.OwnerDark, SHADE.Metal)
    }
  }

  // Two armour belts, with rivet heads along the upper one on the wall the sun
  // reaches. Everything here is bolted; nothing is welded smooth.
  big.box(tx, 1.86, tz, 2.04, 0.26, 2.04, SHADE.MetalDark, SHADE.Metal)
  big.box(tx, 2.66, tz, 2.04, 0.26, 2.04, SHADE.MetalMid, SHADE.Metal)
  for (const z of [-0.5, 0.5]) {
    big.box(tx + 1.06, 2.71, tz + z, 0.16, 0.2, 0.22, SHADE.Metal)
  }

  // Strakes standing proud of the two walls the fixed camera is ever given, and
  // a lit conduit climbing the outboard one. A raised rib beats a cut groove
  // here: the groove has nothing to shade it, the rib throws its own line.
  for (const x of [-0.5, 0.5]) {
    big.box(tx + x, 1.38, tz + 0.99, 0.26, 1.9, 0.14, SHADE.OwnerDark)
  }
  big.box(tx + 0.99, 1.38, tz - 0.5, 0.14, 1.9, 0.26, SHADE.OwnerDark)
  big.box(tx + 1.02, 1.38, tz + 0.42, 0.16, 1.8, 0.2, SHADE.Accent)
  big.quad(
    [tx + 0.951, 2.1, tz + 0.1],
    [tx + 0.951, 2.1, tz - 1.3],
    [tx + 0.951, 2.26, tz - 1.3],
    [tx + 0.951, 2.26, tz + 0.1],
    SHADE.Accent,
  )

  // The crew door at the foot, framed and silled, kept shallow so the reveal
  // does not swallow itself.
  big.box(tx, 1.38, tz + 1.02, 0.9, 0.8, 0.16, SHADE.MetalMid, SHADE.Metal)
  big.box(tx, 1.42, tz + 1.11, 0.9, 0.16, 0.1, SHADE.Accent)
  big.box(tx, 2.14, tz + 1.04, 1.2, 0.14, 0.22, SHADE.Metal)

  // --- the open top ----------------------------------------------------------
  // The shaft has no roof. It stops in a sunken armoured floor inside a low
  // sill, and the four corner columns carry on past it as posts, so what the
  // camera looks down into is a dark square well with something standing in it.
  big.box(tx, 3.22, tz, 1.6, 0.12, 1.6, SHADE.MetalDark)
  for (const sz of [-1, 1]) {
    big.box(tx, 3.28, tz + sz * 0.85, 1.9, 0.42, 0.2, SHADE.OwnerDark, SHADE.Owner)
  }
  for (const sx of [-1, 1]) {
    big.box(tx + sx * 0.85, 3.28, tz, 0.2, 0.42, 1.5, SHADE.OwnerDark, SHADE.Owner)
  }
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      big.box(tx + sx * 0.95, 3.28, tz + sz * 0.95, 0.48, 0.87, 0.48, SHADE.MetalDark, SHADE.Metal)
    }
  }
  big.box(tx, 3.34, tz + 0.55, 0.9, 0.14, 0.16, SHADE.Accent)

  // The airframe standing in it, nose up and clamped at a collar band. It is
  // deliberately in bare alloy — light against a dark well inside a coloured
  // sleeve — and it is the one thing allowed above the posts, because it is the
  // whole reason the building has the shape it has.
  big.box(tx, 3.34, tz, 0.7, 1.56, 0.7, SHADE.Metal, SHADE.MetalDark)
  big.box(tx, 4.05, tz, 0.82, 0.16, 0.82, SHADE.Accent)
  big.wedge(tx, 4.9, tz, 0.7, 0.46, 0.7, 0.18, SHADE.Owner, SHADE.OwnerLight)
  big.wedge(tx, 5.36, tz, 0.34, 0.34, 0.34, 0.1, SHADE.Owner, SHADE.OwnerLight)
  big.box(tx, 5.7, tz, 0.14, 0.22, 0.14, SHADE.Accent)

  // The umbilical arm, laid from the mast head across the sill to the collar.
  big.box(tx + 1.15, 3.7, tz, 1.5, 0.18, 0.44, SHADE.MetalMid, SHADE.Metal)

  // --- service mast ----------------------------------------------------------
  // Two legs braced together, standing level with the posts. Their separation
  // runs across the view rather than along it: a gap between two axis-aligned
  // legs closes into a dark crack under this camera, and the one piece of open
  // sky inside the outline is worth an eighth of a turn.
  const mast = new MeshBuilder()
  for (const sz of [-1, 1]) {
    mast.box(0, 1.06, sz * 0.6, 0.38, 3.0, 0.38, SHADE.Metal)
  }
  for (const y of [1.9, 2.8, 3.7]) {
    mast.box(0, y, 0, 0.24, 0.18, 1.2, SHADE.MetalDark)
  }
  mast.box(0, 4.06, 0, 0.54, 0.22, 1.66, SHADE.Metal, SHADE.MetalDark)
  mast.box(0, 4.28, -0.5, 0.5, 0.4, 0.54, SHADE.MetalDark, SHADE.Metal)
  mast.box(0, 4.68, -0.5, 0.18, 0.22, 0.18, SHADE.Accent)
  big.merge(mast, 1.15, 0, tz, 1, -Math.PI / 4)

  // --- yard ------------------------------------------------------------------
  // A bank of propellant bottles, squared off rather than turned, uneven in
  // height, with a manifold run across their heads.
  for (const [x, h] of [
    [-1.95, 1.0],
    [-1.45, 1.3],
    [-0.95, 0.85],
  ]) {
    big.box(x, 1.06, 0.55, 0.4, h, 0.4, SHADE.Owner, SHADE.MetalDark)
  }
  big.box(-1.45, 1.92, 0.55, 1.5, 0.14, 0.18, SHADE.Metal)

  // The fire control blockhouse, low and thick, with a lit door facing out.
  big.box(0.6, 1.06, 0.85, 1.5, 0.62, 0.92, SHADE.MetalMid, SHADE.MetalDark)
  big.box(0.6, 1.1, 1.33, 0.75, 0.44, 0.1, SHADE.Accent)

  // Corrugated ducting carried from the blockhouse roof to the shaft wall,
  // above the deck rather than on it so its ribs are seen against the tower.
  // `treadBand` counts its cleats off its own length in builder units, so a band
  // drawn at twice size and shrunk comes back with twice the ribs — the one
  // detail here that the 2x authoring buys outright rather than merely affords.
  const duct = new MeshBuilder()
  duct.treadBand(0, 0, 0.72, 0.42, SHADE.MetalDark)
  big.merge(duct, 0.25, 1.86, 0.5, 1, 0)

  hull.merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const arcPylon: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  // A tight pad under a short shaft. Every other building this faction puts
  // down spreads across its slab; a defence post is the one that can say from
  // its plan alone that it takes up almost none of it, and that two thirds of
  // what does stand there is the weapon.
  foundation(hull, 0.92, 0.92, 2, 2)

  const big = new MeshBuilder()
  big.box(0, 0.16, 0, 1.3, 0.2, 1.1, SHADE.MetalDark, SHADE.MetalMid)
  // Four footings splayed to the corners of the base course and nothing above
  // them but a straight shaft: the waist is what keeps this off the pile of
  // stepped square podiums it stands among.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      big.box(sx * 0.53, 0.16, sz * 0.44, 0.34, 0.5, 0.34, SHADE.Metal, SHADE.MetalMid)
    }
  }
  big.box(0, 0.36, 0, 0.86, 0.7, 0.78, SHADE.MetalMid, SHADE.Metal)
  // Straps welded up the two faces the camera gets, with bolt heads proud of
  // them. The shaft is a plain slab and the straps are all that says it is
  // plate rather than cast.
  for (const z of [-0.22, 0.22]) {
    big.box(0.44, 0.36, z, 0.08, 0.7, 0.14, SHADE.MetalDark)
    big.box(0.47, 0.86, z, 0.1, 0.1, 0.1, SHADE.Metal)
  }
  for (const x of [-0.22, 0.22]) {
    big.box(x, 0.36, 0.4, 0.14, 0.7, 0.08, SHADE.MetalDark)
  }
  // The feed trunk, clamped to one flank the whole way up, with two inspection
  // windows lit down its length and a gland where it enters the capital.
  big.box(0.5, 0.16, 0, 0.2, 0.86, 0.26, SHADE.MetalMid, SHADE.Metal)
  big.box(0.56, 0.4, 0, 0.12, 0.16, 0.18, SHADE.Accent)
  big.box(0.56, 0.7, 0, 0.12, 0.16, 0.18, SHADE.Accent)
  big.box(0.5, 0.9, 0, 0.26, 0.12, 0.32, SHADE.MetalDark)
  // An armoured access hatch on the face that gets looked at, with a lamp over
  // it. At this size a door is the only thing that gives the shaft a scale.
  big.box(0, 0.36, 0.41, 0.3, 0.42, 0.06, SHADE.MetalDark, SHADE.Metal)
  big.box(0, 0.8, 0.43, 0.12, 0.08, 0.06, SHADE.Accent)
  // A machined capital with a lip, so the head sits on a shoulder rather than
  // sprouting from the top of the shaft.
  big.wedge(0, 1.06, 0, 0.98, 0.14, 0.9, 0.07, SHADE.Metal, SHADE.MetalMid)
  hull.merge(big, 0, 0, 0, 0.5)

  // The bolted collar under the capital, wound from a tread band: run long and
  // scaled down, its bolt pitch comes out finer than boxes could be placed.
  const collar = new MeshBuilder()
  collar.treadBand(0.46, 0, 0.72, 0.1, SHADE.MetalDark)
  hull.merge(collar, 0, 0.43, 0, 0.5)
  hull.merge(collar, 0, 0.43, 0, 0.5, -Math.PI / 2)

  const turret = r.part(PART.Turret)
  const head = new MeshBuilder()
  head.box(0, 1.2, 0, 0.9, 0.26, 0.76, SHADE.Owner, SHADE.OwnerLight)
  head.box(0, 1.46, 0.02, 0.8, 0.24, 0.62, SHADE.OwnerDark, SHADE.Owner)

  // The emitter is a fork: two slabs of plate stood on end and stepped apart,
  // with the arc gap open between them. Nothing else in the base has a hole in
  // its outline.
  const fork = new MeshBuilder()
  for (const s of [-1, 1]) {
    fork.box(s * 0.34, 1.7, 0.04, 0.26, 0.7, 0.54, SHADE.Owner, SHADE.OwnerLight)
    fork.box(s * 0.42, 2.4, 0.04, 0.24, 0.62, 0.48, SHADE.Owner, SHADE.OwnerLight)
    fork.box(s * 0.42, 3.02, 0.04, 0.3, 0.18, 0.54, SHADE.MetalDark, SHADE.Metal)
    // Bolt heads across the face that carries the arc, and the electrode
    // facing its opposite number over the gap.
    fork.box(s * 0.34, 1.86, 0.32, 0.11, 0.11, 0.11, SHADE.MetalDark)
    fork.box(s * 0.34, 2.16, 0.32, 0.11, 0.11, 0.11, SHADE.MetalDark)
    fork.box(s * 0.42, 2.56, 0.29, 0.11, 0.11, 0.11, SHADE.MetalDark)
    fork.box(s * 0.28, 2.76, 0.04, 0.2, 0.16, 0.26, SHADE.Accent)
    // The inner faces are lined with conductor, and each blade tip is capped
    // with it: the arc has somewhere to come from at the top of the notch as
    // well as somewhere to sit at the bottom.
    fork.box(s * 0.2, 1.86, 0.04, 0.04, 0.58, 0.34, SHADE.Accent)
    fork.box(s * 0.42, 3.2, 0.04, 0.16, 0.06, 0.24, SHADE.Accent)
  }
  // The back plate closes the throat behind the arc and ties the prongs
  // together low down, so everything above it is open sky between two blades —
  // the one outline in this base with a hole in it.
  fork.box(0, 1.74, -0.26, 0.94, 0.76, 0.16, SHADE.MetalDark, SHADE.Metal)
  // The node hangs in the throat where the fork is narrowest, on a lit crossbar
  // bridging the prongs.
  fork.box(0, 2.04, 0.04, 0.56, 0.1, 0.34, SHADE.Accent)
  fork.cylinder(0, 1.9, 0.04, 0.17, 0.34, 6, SHADE.Accent)
  // Turned a further eighth so the gap lies across the camera rather than along
  // it: seen down its own axis the two prongs overlap and the notch closes.
  head.merge(fork, 0, 0, 0, 1, -Math.PI / 4)
  turret.merge(head, 0, 0, 0, 0.5)
  return r.build()
}
const logicBomb: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]
  const hull = r.part(PART.Hull)
  foundation(hull, 2.05, 2.05, 3, 3)

  // Authored at twice size and merged down at half. Everything below that reads
  // as heavy — the stiffener ribs, the bolt pads on the cradle cheeks, the
  // riveted ledge — is a tenth of a unit or less once it lands, and none of it
  // would have been drawn at final scale.
  const big = new MeshBuilder()

  // A casing lying on its side, which `cylinder` cannot make: it extrudes along
  // Y. Wound by hand as an eight-sided prism about the X axis, and only the four
  // facets the fixed camera is ever given are emitted. Their lit order runs
  // 2 > 1 > 0 > 7, so the value ramp across them is what makes flat quads read
  // as a round mass.
  const AY = 1.7
  const at = (x: number, k: number, radius: number): number[] => {
    const a = ((k + 0.5) / 8) * Math.PI * 2
    return [x, AY + Math.sin(a) * radius, Math.cos(a) * radius]
  }
  const SEEN = [7, 0, 1, 2]
  const drum = (x0: number, x1: number, radius: number, bands: Role[], cap?: Role): void => {
    for (let i = 0; i < SEEN.length; i++) {
      const k = SEEN[i]
      const a = at(x0, k, radius)
      const b = at(x1, k, radius)
      const c = at(x1, k + 1, radius)
      const d = at(x0, k + 1, radius)
      big.quad(a, b, c, d, bands[i])
    }
    if (cap !== undefined) {
      for (let i = 1; i < 7; i++) {
        big.tri(at(x1, 0, radius), at(x1, i + 1, radius), at(x1, i, radius), cap)
      }
    }
  }

  // The sled. Two courses of plate rather than one slab, and long across the
  // pad instead of square on it, so the thing has a barrel axis before anything
  // is laid in it. The top course clears the terrain debris line.
  big.box(0, 0.16, 0, 3.9, 0.42, 1.7, SHADE.MetalDark, SHADE.Metal)
  big.box(0, 0.58, 0, 3.5, 0.5, 1.3, SHADE.MetalMid, SHADE.Metal)
  // Stiffeners down the near flank, and bolt heads along the ledge above them.
  for (const x of [-1.55, -0.75, 0.05, 0.85, 1.55]) {
    big.box(x, 0.16, 0.88, 0.26, 0.42, 0.3, SHADE.Metal)
  }
  for (const x of [-1.35, -0.45, 0.45, 1.35]) {
    big.box(x, 0.58, 0.75, 0.2, 0.14, 0.18, SHADE.Metal)
  }

  // Breech head: a squared block swallowing the open end of the casing, belted
  // round its middle, with the charge slot lit on the face the camera gets.
  big.box(-1.74, 0.16, 0, 0.46, 1.94, 1.46, SHADE.Owner)
  big.box(-1.74, 1.5, 0, 0.56, 0.18, 1.56, SHADE.MetalDark)
  big.box(-1.74, 2.1, 0, 0.6, 0.14, 1.6, SHADE.MetalDark, SHADE.Metal)
  big.box(-1.74, 0.86, 0.78, 0.26, 0.8, 0.16, SHADE.Accent)
  for (const x of [-1.94, -1.54]) {
    big.box(x, 1.52, 0.82, 0.2, 0.2, 0.2, SHADE.Metal)
  }

  // Cradle: three cross members standing proud of the sled on both flanks, each
  // with a bolt pad on its near end. They are what make the casing read as
  // freight set down and clamped rather than as a pipe growing out of a plinth.
  for (const x of [-1.05, 0.15, 1.05]) {
    big.box(x, 1.08, 0, 0.36, 0.38, 1.94, SHADE.MetalDark, SHADE.Metal)
    big.box(x, 1.46, 0.82, 0.44, 0.2, 0.3, SHADE.Metal)
  }

  // The casing itself, and three saddle straps over the cradle members.
  const CASING: Role[] = [SHADE.Owner, SHADE.Owner, SHADE.Owner, SHADE.OwnerLight]
  const STRAP: Role[] = [SHADE.MetalMid, SHADE.MetalMid, SHADE.Metal, SHADE.Metal]
  drum(-1.66, 1.24, 0.62, CASING, SHADE.MetalDark)
  for (const x of [-1.05, 0.15, 1.05]) {
    drum(x - 0.14, x + 0.14, 0.72, STRAP)
  }

  // A riveted seam down the crown, laid on the ridge between the two lit
  // facets so the light finds every head. It is the one place on a welded
  // casing where the fabrication shows from overhead.
  big.box(-0.2, 2.25, -0.19, 2.7, 0.14, 0.14, SHADE.MetalDark)
  // An inspection hatch let into the near flank, where the casing is otherwise
  // a blank half-metre of plate between two straps.
  big.box(0.6, 1.5, 0.6, 0.44, 0.34, 0.12, SHADE.MetalMid, SHADE.Metal)
  big.box(-0.45, 1.56, 0.6, 0.3, 0.22, 0.1, SHADE.MetalDark)

  // Muzzle end, aimed down the camera axis: a bolted head flange, a blunt block
  // of plate, and the aperture lit on its outer face.
  drum(1.24, 1.52, 0.74, [SHADE.MetalDark, SHADE.Metal, SHADE.Metal, SHADE.Metal], SHADE.Metal)
  big.box(1.7, 1.34, 0, 0.36, 0.72, 0.72, SHADE.OwnerDark, SHADE.Owner)
  big.box(1.9, 1.42, 0, 0.06, 0.56, 0.56, SHADE.Accent)
  for (const x of [1.6, 1.8]) {
    big.box(x, 1.5, 0.4, 0.18, 0.18, 0.16, SHADE.Metal)
  }

  // Feed trunk along the near apron, collared where it is flanged and running a
  // lit line the length of the emplacement, with two risers into the sled.
  big.box(0.1, 0.16, 1.5, 3.0, 0.36, 0.42, SHADE.MetalMid, SHADE.Metal)
  big.box(0.1, 0.52, 1.5, 2.9, 0.08, 0.18, SHADE.Accent)
  for (const x of [-1.0, 0.2, 1.4]) {
    big.box(x, 0.16, 1.5, 0.16, 0.5, 0.56, SHADE.MetalDark)
  }
  for (const x of [-0.7, 1.0]) {
    big.box(x, 0.34, 1.1, 0.2, 0.42, 0.5, SHADE.Metal)
  }

  // The firing console, set on the apron clear of the trunk and lit on top,
  // where a crew would stand to arm the thing.
  big.box(-1.76, 0.16, 1.5, 0.5, 0.8, 0.56, SHADE.MetalMid, SHADE.Metal)
  big.box(-1.76, 0.96, 1.5, 0.34, 0.08, 0.4, SHADE.Accent)

  // Two arming stanchions off the far edge, unequal, capped in plate.
  big.box(-1.1, 0.16, -1.4, 0.56, 1.9, 0.56, SHADE.MetalMid, SHADE.Metal)
  big.box(-1.1, 2.06, -1.4, 0.7, 0.16, 0.7, SHADE.MetalDark, SHADE.Metal)
  big.box(1.6, 0.16, -1.5, 0.44, 1.16, 0.44, SHADE.MetalMid, SHADE.Metal)
  big.box(1.6, 1.32, -1.5, 0.56, 0.14, 0.56, SHADE.MetalDark, SHADE.Metal)

  hull.merge(big, 0, 0, 0, 0.5)

  // Aliased to the hull rather than its own part. `PART.Accent` never rotates and
  // `PART.Hull` rotates with the unit's heading — on a structure, whose heading never
  // changes, those are the same thing, and a second part costs a draw call per owner
  // for no behaviour. The roster budget is real: it went to 202 of 200 without this.
  const accent = hull
  const lamp = new MeshBuilder()
  lamp.box(-1.1, 2.22, -1.4, 0.26, 0.26, 0.26, SHADE.Accent)
  accent.merge(lamp, 0, 0, 0, 0.5)
  return r.build()
}

// ---------------------------------------------------------------------------
// Tier three
//
// These have to read as *late* units from across the map, and the cue that
// carries at this size is proportion rather than detail: the beam tanks are
// front-heavy with an obvious emitter, the artillery carries a barrel longer
// than its own hull, and the brawlers are wider and squatter than anything
// below them. A player should be able to tell a Warden from a Guardian Tank in
// a moving crowd without reading a health bar.
// ---------------------------------------------------------------------------

const prismWorks: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 2.95, 2.95, 2, 2)

  // Authored at twice size and merged down at the end, which is what pays for
  // the ridge pitch: four north lights across a shed this wide sit 0.3 apart at
  // final scale, and nobody draws a 0.3 feature by choosing to.
  //
  // Surface detail goes in as single quads standing a hair proud of the face
  // they sit on rather than as boxes. A bolt strap drawn as a box costs twelve
  // triangles to show one face of it; drawn as a quad it costs two, and this
  // building spends what that saves on the crane.
  const big = new MeshBuilder()
  const faceX = (x: number, y: number, z: number, h: number, d: number, s: (typeof SHADE)[keyof typeof SHADE]) =>
    big.quad([x, y, z + d / 2], [x, y, z - d / 2], [x, y + h, z - d / 2], [x, y + h, z + d / 2], s)

  // --- melt hall -------------------------------------------------------------
  // A long narrow shed down one side of the pad and open yard down the other.
  // Nothing else the faction builds splits its footprint in two like this, and
  // the split is what reads at zoom, before any of the detail on it does.
  big.box(-1.62, 0.16, 0, 2.72, 0.24, 5.7, SHADE.MetalDark, SHADE.Metal)
  big.box(-1.65, 0.4, 0, 2.4, 1.32, 5.4, SHADE.Owner)
  big.box(-1.65, 1.72, 0, 2.46, 0.18, 5.5, SHADE.MetalDark, SHADE.Metal)

  // Pilasters down the yard wall: inch plate stiffened from the outside because
  // that was quicker than folding it, strapped across top and bottom.
  for (const z of [-2.5, -1.9, 0.2]) {
    big.box(-0.36, 0.4, z, 0.36, 1.32, 0.5, SHADE.MetalMid, SHADE.Metal)
    faceX(-0.17, 0.54, z, 0.12, 0.54, SHADE.MetalDark)
    faceX(-0.17, 1.3, z, 0.12, 0.54, SHADE.MetalDark)
  }
  // The charging door, in a frame deep enough to shade its own opening, under a
  // lit lintel, with a kerb stepped out onto the yard.
  big.box(-0.32, 0.4, -1.0, 0.46, 1.16, 1.44, SHADE.MetalDark, SHADE.Metal)
  faceX(-0.08, 0.44, -1.0, 0.92, 1.12, SHADE.MetalMid)
  faceX(-0.08, 1.4, -1.0, 0.14, 1.28, SHADE.Accent)
  big.box(0.02, 0.16, -1.0, 0.56, 0.16, 1.24, SHADE.MetalMid, SHADE.Metal)
  for (const z of [-2.2, 0.9]) {
    faceX(-0.44, 1.06, z, 0.28, 0.72, SHADE.OwnerLight)
  }

  // Sawtooth roof: four ridges stepping across the shed, each a slope up to a
  // glazed riser facing the yard. North lights are what a glassworks looks like
  // from above, and above is where a fixed camera spends most of its pixels.
  const roofY = 1.9
  for (let i = 0; i < 4; i++) {
    const xlo = -2.81 + i * 0.6
    const xhi = xlo + 0.6
    const yTop = roofY + 0.52
    big.quad(
      [xlo, roofY, 2.72],
      [xhi, yTop, 2.72],
      [xhi, yTop, -2.72],
      [xlo, roofY, -2.72],
      SHADE.Owner,
    )
    big.quad(
      [xhi, roofY, 2.72],
      [xhi, roofY, -2.72],
      [xhi, yTop, -2.72],
      [xhi, yTop, 2.72],
      SHADE.OwnerLight,
    )
    big.tri([xlo, roofY, 2.72], [xhi, roofY, 2.72], [xhi, yTop, 2.72], SHADE.OwnerDark)
  }
  // Mullions across the front light, the one the camera is nearest.
  for (const z of [-2.1, -0.9, 0.3, 1.5]) {
    faceX(-0.39, roofY, z, 0.52, 0.16, SHADE.MetalDark)
  }

  // A flue out through the ridges and a whip off the far end: the tallest things
  // on the shed are thin, which is what stops a long roof reading as one slab.
  big.box(-1.31, 2.0, -1.9, 0.38, 1.16, 0.38, SHADE.MetalDark, SHADE.Metal)
  big.box(-1.31, 3.16, -1.9, 0.5, 0.12, 0.5, SHADE.Metal)
  big.box(-0.71, 2.0, -2.5, 0.12, 1.34, 0.12, SHADE.Metal)
  big.box(-0.71, 3.34, -2.5, 0.18, 0.14, 0.18, SHADE.Accent)

  // --- casting yard ----------------------------------------------------------
  big.box(1.25, 0.16, 0, 3.1, 0.18, 5.7, SHADE.MetalDark, SHADE.OwnerDark)

  // The tank: a pool of melt inside a coaming thick enough to stand on. The one
  // warm surface on the building, and the thing all that cold glass is made of.
  big.box(1.3, 0.34, -0.5, 1.34, 0.06, 1.94, SHADE.Accent)
  big.box(0.53, 0.34, -0.5, 0.18, 0.42, 2.3, SHADE.OwnerDark, SHADE.Metal)
  big.box(2.07, 0.34, -0.5, 0.18, 0.42, 2.3, SHADE.OwnerDark, SHADE.Metal)
  big.box(1.3, 0.34, -1.56, 1.7, 0.42, 0.18, SHADE.OwnerDark, SHADE.Metal)
  big.box(1.3, 0.34, 0.56, 1.7, 0.42, 0.18, SHADE.OwnerDark, SHADE.Metal)
  for (const z of [-1.2, -0.5, 0.2]) {
    faceX(2.17, 0.46, z, 0.24, 0.4, SHADE.Accent)
  }

  // A launder stepping down out of the hall wall and over the coaming, so the
  // melt in the tank has somewhere to have come from.
  big.box(-0.1, 1.12, -1.0, 0.6, 0.36, 0.52, SHADE.MetalMid, SHADE.Metal)
  big.box(0.3, 0.88, -1.0, 0.5, 0.3, 0.48, SHADE.MetalMid, SHADE.Metal)
  big.box(0.62, 0.66, -1.0, 0.44, 0.28, 0.44, SHADE.MetalDark, SHADE.Accent)

  // --- portal crane ----------------------------------------------------------
  // The one part of the pad with sky under it, and the only thing on it that is
  // not a solid. Splayed feet, box legs strapped at the joints, a plate girder,
  // and a billet hanging off the hook.
  for (const x of [0.12, 2.48]) {
    big.box(x, 0.34, -0.5, 0.72, 0.24, 0.88, SHADE.MetalDark, SHADE.Metal)
    big.box(x, 0.58, -0.5, 0.44, 2.02, 0.58, SHADE.MetalMid, SHADE.Metal)
    faceX(x + 0.23, 0.86, -0.5, 1.3, 0.34, SHADE.Owner)
    for (const y of [0.7, 1.5, 2.3]) {
      faceX(x + 0.23, y, -0.5, 0.14, 0.5, SHADE.MetalDark)
    }
    big.box(x + (x < 1.3 ? 0.38 : -0.38), 2.24, -0.5, 0.36, 0.36, 0.5, SHADE.MetalMid, SHADE.Metal)
  }
  big.box(1.3, 2.6, -0.5, 3.02, 0.12, 0.72, SHADE.MetalDark)
  big.box(1.3, 2.72, -0.5, 2.92, 0.46, 0.46, SHADE.Owner)
  big.box(1.3, 3.18, -0.5, 3.08, 0.14, 0.78, SHADE.MetalDark, SHADE.Metal)
  big.quad(
    [-0.16, 2.82, -0.26],
    [2.76, 2.82, -0.26],
    [2.76, 2.96, -0.26],
    [-0.16, 2.96, -0.26],
    SHADE.Accent,
  )
  big.box(1.5, 2.24, -0.5, 0.78, 0.36, 0.8, SHADE.MetalMid, SHADE.Metal)
  big.box(1.5, 1.74, -0.5, 0.4, 0.5, 0.36, SHADE.MetalDark, SHADE.Accent)
  big.box(1.5, 1.34, -0.5, 0.56, 0.4, 0.56, SHADE.OwnerLight)

  // --- the drawn prism -------------------------------------------------------
  // Turned an eighth of a turn out of the base's grid. Nothing else the faction
  // builds is off-axis, so a diamond in plan is the cheapest way there is of
  // being told apart from across the map, and the clamps holding it stay square
  // to everything else, which is what makes the turn read as deliberate.
  const cry = new MeshBuilder()
  cry.box(0, 0.34, 0, 1.2, 0.3, 1.2, SHADE.MetalDark, SHADE.Metal)
  cry.box(0, 0.64, 0, 0.9, 1.4, 0.9, SHADE.OwnerLight)
  cry.box(0, 1.2, 0, 1.04, 0.22, 1.04, SHADE.MetalMid, SHADE.Metal)
  cry.wedge(0, 2.04, 0, 0.9, 0.56, 0.9, 0.34, SHADE.OwnerLight, SHADE.Accent)
  big.merge(cry, 2.15, 0, -2.4, 1, Math.PI / 4)
  big.box(1.37, 0.34, -2.4, 0.4, 0.62, 0.32, SHADE.MetalMid, SHADE.Metal)
  big.box(2.15, 0.34, -1.62, 0.32, 0.62, 0.4, SHADE.MetalMid, SHADE.Metal)

  // Yard clutter: an annealing drum and a crate under a bolted lid.
  big.cylinder(2.3, 0.34, 1.15, 0.36, 0.8, 6, SHADE.MetalDark, SHADE.Metal)
  big.box(2.3, 0.34, 2.1, 0.62, 0.48, 0.62, SHADE.MetalMid, SHADE.MetalDark)

  hull.merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const prismTank: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]

  // Stations, tail to prow: [z, xBelly, xChine, xShoulder, xCrest, yBelly,
  // yChine, yShoulder, yCrest, chineFacet]. The chine is the widest line and it
  // sits low, so the flanks flare out over the tracks and fall back to the deck.
  // The crest carries the vertical story on its own: 0.68 over the capacitor
  // deck, 0.56 across the ring, 0.38 at the point.
  const stations = [
    [-0.9, 0.14, 0.2, 0.17, 0.05, 0.22, 0.34, 0.48, 0.58, 0.025],
    [-0.4, 0.34, 0.48, 0.4, 0.14, 0.13, 0.32, 0.5, 0.68, 0.045],
    [0.06, 0.36, 0.5, 0.43, 0.28, 0.12, 0.31, 0.5, 0.56, 0.045],
    [0.54, 0.3, 0.43, 0.37, 0.15, 0.14, 0.29, 0.42, 0.52, 0.04],
    [0.96, 0.09, 0.15, 0.1, 0.04, 0.2, 0.26, 0.32, 0.38, 0.02],
  ]
  /** One side of a station, bottom to top. */
  const profile = (s: number[], sx: number): number[][] => [
    [sx * s[1], s[5]],
    [sx * s[2], s[6] - s[9]],
    [sx * s[2], s[6] + s[9]],
    [sx * s[3], s[7] - 0.03],
    [sx * (s[3] - 0.025), s[7] + 0.015],
    [sx * s[4], s[8]],
  ]

  const big = new MeshBuilder()
  for (let i = 0; i < stations.length - 1; i++) {
    const a = stations[i]
    const b = stations[i + 1]
    const za = a[0]
    const zb = b[0]
    // The conduit is lit over the middle of the hull only. Lit end to end it was
    // a stripe painted down the side; lit where the hull is widest it reads as
    // something running from the capacitor deck forward into the emitter.
    const live = i === 1 || i === 2
    const bands: Role[] = [
      SHADE.OwnerDark,
      live ? SHADE.Accent : SHADE.MetalDark,
      SHADE.Owner,
      SHADE.MetalMid,
      SHADE.Owner,
    ]
    for (const sx of [1, -1]) {
      const pa = profile(a, sx)
      const pb = profile(b, sx)
      for (let k = 0; k < 5; k++) {
        const q0 = [pa[k][0], pa[k][1], za]
        const q1 = [pa[k + 1][0], pa[k + 1][1], za]
        const q2 = [pb[k + 1][0], pb[k + 1][1], zb]
        const q3 = [pb[k][0], pb[k][1], zb]
        if (sx > 0) big.quad(q0, q1, q2, q3, bands[k])
        else big.quad(q3, q2, q1, q0, bands[k])
      }
    }
    // The crest. Over the engine it is a hand's width of neutral alloy between
    // two steeply sloped deck planes; forward of the ring the same strip has
    // opened out into the foredeck and takes the owner colour with it. No belly
    // quad: it faces straight down, it is culled at every heading the camera has,
    // and twelve triangles buys a spine vent.
    big.quad(
      [-b[4], b[8], zb],
      [b[4], b[8], zb],
      [a[4], a[8], za],
      [-a[4], a[8], za],
      i < 2 ? SHADE.Metal : SHADE.Owner,
    )
  }
  for (const [s, front, shade] of [
    [stations[0], false, SHADE.MetalMid],
    [stations[stations.length - 1], true, SHADE.Owner],
  ] as Array<[number[], boolean, Role]>) {
    const p = profile(s, 1)
    for (let k = 0; k < 5; k++) {
      const q = [
        [p[k][0], p[k][1], s[0]],
        [-p[k][0], p[k][1], s[0]],
        [-p[k + 1][0], p[k + 1][1], s[0]],
        [p[k + 1][0], p[k + 1][1], s[0]],
      ]
      if (front) big.quad(q[1], q[0], q[3], q[2], shade)
      else big.quad(q[0], q[1], q[2], q[3], shade)
    }
  }

  // Vents let across the spine, each cut to the width the spine has where it
  // sits, so the three of them narrow towards the tail with the casting.
  for (const [z, y, w] of [
    [-0.3, 0.614, 0.5],
    [-0.5, 0.62, 0.4],
    [-0.7, 0.58, 0.33],
  ]) {
    big.box(0, y, z, w, 0.06, 0.07, SHADE.MetalDark)
  }
  // A crew blister, off to port. It is the one asymmetry on the vehicle and it
  // sits on the foredeck because that is the only flat the optic does not cover.
  big.wedge(-0.16, 0.48, 0.46, 0.18, 0.12, 0.22, 0.05, SHADE.MetalMid, SHADE.Metal)
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // Drawn at 1.0 and shrunk, the band comes back with eight ribs across half a
  // unit — twice what one drawn at final size would have carried.
  const track = new MeshBuilder()
  for (const sx of [-1, 1]) track.treadBand(sx * 0.38, 0.04, 1.0, 0.2, SHADE.MetalDark)
  r.part(PART.Tread).merge(track, 0, 0, 0, 0.5)

  const gun = new MeshBuilder()
  // A ring sunk into the plateau and a cast cowl over it. No drum, no mantlet,
  // nothing square — the cowl is the mount and the fairing at once.
  gun.cylinder(0, 0.46, 0.1, 0.3, 0.18, 6, SHADE.MetalDark, SHADE.MetalMid)
  gun.wedge(0, 0.6, 0.02, 0.52, 0.18, 0.6, 0.07, SHADE.Owner, SHADE.Owner)
  // The crystal, six-sided because a prism is: a straight-walled course whose
  // faces take the light, then a crown drawing in to a lit point. A single cone
  // was tried first and came back near-black — every side of it faces away.
  gun.cylinder(0, 0.76, 0.12, 0.24, 0.26, 6, SHADE.Accent)
  gun.cylinder(0, 1.02, 0.12, 0.19, 0.2, 6, SHADE.Accent, SHADE.OwnerLight, 0.06)
  // The blade rakes forward off the cowl's nose and stops inside the hull's own
  // prow. Flat and in the trim colour it reads as an emitter; the first pass gave
  // it height and a pale cap and it read as a stubby gun, which is the one thing
  // this vehicle must not look like.
  gun.wedge(0, 0.56, 0.34, 0.32, 0.16, 0.26, 0.06, SHADE.Owner, SHADE.MetalMid)
  gun.box(0, 0.58, 0.6, 0.22, 0.09, 0.26, SHADE.Accent)
  gun.box(0, 0.58, 0.82, 0.11, 0.09, 0.16, SHADE.OwnerLight)
  r.part(PART.Turret).merge(gun, 0, 0, 0, 0.5)
  return r.build()
}

const siegeBattery: Recipe = () => {
  const r = new RecipeBuilder()
  type Shade = (typeof SHADE)[keyof typeof SHADE]

  /**
   * A beam between two arbitrary points. Every primitive here is axis-aligned,
   * so a gun at elevation has to be built by hand or stepped up as a staircase
   * of boxes; a swept box costs the same six quads as one tread of that stair
   * and comes out straight.
   */
  const strut = (
    b: MeshBuilder,
    from: readonly number[],
    to: readonly number[],
    hw: number,
    ht: number,
    shade: Shade,
    capShade: Shade = shade,
    caps = true,
  ): void => {
    const len = Math.hypot(to[0] - from[0], to[1] - from[1], to[2] - from[2]) || 1
    const ux = (to[0] - from[0]) / len
    const uy = (to[1] - from[1]) / len
    const uz = (to[2] - from[2]) / len
    const ry = Math.abs(uy) > 0.9 ? 0 : 1
    const rz = ry ? 0 : 1
    const cl = Math.hypot(ry * uz - rz * uy, rz * ux, ry * ux) || 1
    const sx = (ry * uz - rz * uy) / cl
    const sy = (rz * ux) / cl
    const sz = (-ry * ux) / cl
    const px = uy * sz - uz * sy
    const py = uz * sx - ux * sz
    const pz = ux * sy - uy * sx
    const p = (a: number, t: number, e: number): number[] => {
      const o = e ? to : from
      return [
        o[0] + a * hw * sx + t * ht * px,
        o[1] + a * hw * sy + t * ht * py,
        o[2] + a * hw * sz + t * ht * pz,
      ]
    }
    b.quad(p(-1, 1, 1), p(1, 1, 1), p(1, 1, 0), p(-1, 1, 0), shade)
    b.quad(p(1, -1, 1), p(1, -1, 0), p(1, 1, 0), p(1, 1, 1), shade)
    b.quad(p(-1, -1, 0), p(-1, -1, 1), p(-1, 1, 1), p(-1, 1, 0), shade)
    b.quad(p(-1, -1, 0), p(1, -1, 0), p(1, -1, 1), p(-1, -1, 1), shade)
    if (caps) {
      b.quad(p(-1, -1, 1), p(1, -1, 1), p(1, 1, 1), p(-1, 1, 1), capShade)
      b.quad(p(1, -1, 0), p(-1, -1, 0), p(-1, 1, 0), p(1, 1, 0), capShade)
    }
  }

  // Carriage: a low, wide, deliberately flat platform. Everything else Helios
  // fields is a body with a gun on top; this is a gun with the smallest body
  // that will carry it, and the height goes into the barrel rather than the
  // hull. Modelled at twice size and merged down at the end.
  const big = new MeshBuilder()
  big.box(0, 0.02, -0.02, 0.76, 0.22, 0.98, SHADE.MetalDark)
  big.box(0, 0.18, -0.02, 0.96, 0.34, 1.12, SHADE.Owner)
  big.wedge(0, 0.52, 0.34, 0.90, 0.12, 0.44, 0.10, SHADE.Owner)
  big.box(0, 0.52, -0.36, 0.88, 0.26, 0.36, SHADE.MetalMid, SHADE.Metal)
  for (const z of [-0.44, -0.30]) {
    big.quad(
      [-0.32, 0.781, z + 0.04],
      [0.32, 0.781, z + 0.04],
      [0.32, 0.781, z - 0.04],
      [-0.32, 0.781, z - 0.04],
      SHADE.MetalDark,
    )
  }
  big.box(-0.32, 0.76, -0.42, 0.18, 0.30, 0.18, SHADE.MetalDark, SHADE.Metal)
  // The plinth the gun turns on, and a bolted strake across the nose plate.
  big.box(0, 0.50, -0.06, 0.62, 0.16, 0.58, SHADE.OwnerDark, SHADE.MetalMid)
  big.box(0, 0.16, 0.52, 0.94, 0.14, 0.10, SHADE.MetalDark)
  big.quad(
    [-0.44, 0.34, 0.545],
    [0.44, 0.34, 0.545],
    [0.44, 0.46, 0.545],
    [-0.44, 0.46, 0.545],
    SHADE.Accent,
  )
  // Blade at the nose, recoil spade at the tail: both ends of it are buried.
  big.box(0, 0.02, 0.60, 0.84, 0.24, 0.14, SHADE.MetalDark, SHADE.Metal)
  big.box(0, 0.02, -0.62, 0.68, 0.30, 0.14, SHADE.MetalDark, SHADE.Metal)

  // Four jacks splayed to the corners and planted. The struts are the only
  // diagonals in the chassis and they carry the whole "cannot move" read; the
  // pads reach further out than the tracks do, so the plan view is an X.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      strut(
        big,
        [sx * 0.44, 0.40, sz * 0.34],
        [sx * 0.74, 0.24, sz * 0.66],
        0.13,
        0.1,
        SHADE.Metal,
        SHADE.Metal,
        false,
      )
      big.box(sx * 0.76, 0, sz * 0.68, 0.34, 0.26, 0.34, SHADE.MetalDark, SHADE.Metal)
    }
  }

  const hull = r.part(PART.Hull)
  hull.merge(big, 0, 0, 0, 0.5)

  // Running gear half-buried under armour skirts, so what shows of the track
  // is a finely cleated strip below a slab of plate rather than a bare band.
  const bigTread = new MeshBuilder()
  const band = new MeshBuilder()
  band.treadBand(0, 0, 0.56, 0.16, SHADE.MetalDark)
  for (const sx of [-1, 1]) {
    bigTread.merge(band, sx * 0.52, 0, 0, 2)
    bigTread.box(sx * 0.52, 0.14, 0, 0.38, 0.28, 1.12, SHADE.Owner, SHADE.MetalMid)
  }
  r.part(PART.Tread).merge(bigTread, 0, 0, 0, 0.5)

  // The gun, at fifty degrees. The camera looks down at thirty-five, so a
  // barrel laid at a plausible field elevation comes out flat on screen and
  // the one thing this unit is for reads as a stub: it has to be steep enough
  // that the rise beats the camera's own pitch. The trunnion then sits low and
  // far back, because at this angle every unit of pivot height is a unit of
  // barrel length the height budget will not pay for twice.
  const bigGun = new MeshBuilder()
  bigGun.cylinder(0, 0.62, -0.08, 0.34, 0.14, 6, SHADE.MetalDark, SHADE.MetalMid)
  for (const sx of [-1, 1]) {
    bigGun.box(sx * 0.29, 0.64, -0.2, 0.16, 0.38, 0.5, SHADE.Owner)
    bigGun.box(sx * 0.29, 0.96, -0.06, 0.22, 0.16, 0.22, SHADE.MetalDark, SHADE.Metal)
  }
  bigGun.box(0, 0.66, -0.36, 0.44, 0.3, 0.28, SHADE.MetalMid, SHADE.Metal)
  bigGun.box(0, 0.96, -0.34, 0.38, 0.22, 0.26, SHADE.MetalDark, SHADE.Metal)
  strut(bigGun, [0, 0.6, -0.32], [0, 1.1, 0.09], 0.185, 0.175, SHADE.MetalMid, SHADE.MetalMid, false)
  strut(bigGun, [0, 0.7, -0.26], [0, 1.864, 0.717], 0.13, 0.13, SHADE.Metal)
  strut(bigGun, [0, 1.742, 0.615], [0, 1.9, 0.748], 0.165, 0.16, SHADE.MetalDark, SHADE.Metal)
  r.part(PART.Turret).merge(bigGun, 0, 0, 0, 0.5)

  return r.build()
}

const warden: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]

  /**
   * A slab of armour raked back as it rises: a flat plate lofted between a low
   * front edge and a high rear one, closed on both faces and both flanks. Every
   * primitive here is axis-aligned, and a shield stood dead vertical reads as a
   * fence rather than as armour — the rake is what makes it look like it is
   * leaning into something.
   */
  const armour = (
    b: MeshBuilder,
    cx: number,
    loZ: number,
    loY: number,
    hiZ: number,
    hiY: number,
    loW: number,
    hiW: number,
    t: number,
    face: Role,
    edge: Role,
  ): void => {
    const A = [
      [cx - loW, loY, loZ],
      [cx + loW, loY, loZ],
      [cx + hiW, hiY, hiZ],
      [cx - hiW, hiY, hiZ],
    ]
    const B = A.map((p) => [p[0], p[1], p[2] - t])
    b.quad(A[0], A[1], A[2], A[3], face)
    b.quad(B[3], B[2], B[1], B[0], edge)
    b.quad(A[3], B[3], B[0], A[0], edge)
    b.quad(B[1], B[2], A[2], A[1], edge)
    b.quad(A[3], A[2], B[2], B[3], edge)
  }

  /** A rectangular-section prism between two points. Thighs and shins. */
  const strut = (
    b: MeshBuilder,
    lo: readonly number[],
    hi: readonly number[],
    wLo: readonly number[],
    wHi: readonly number[],
    shade: Role,
  ): void => {
    const ring = (p: readonly number[], w: readonly number[]) => [
      [p[0] - w[0], p[1], p[2] - w[1]],
      [p[0] + w[0], p[1], p[2] - w[1]],
      [p[0] + w[0], p[1], p[2] + w[1]],
      [p[0] - w[0], p[1], p[2] + w[1]],
    ]
    const A = ring(lo, wLo)
    const C = ring(hi, wHi)
    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4
      b.quad(A[i], C[i], C[j], A[j], shade)
    }
  }

  /** A bolt head on an upward face. */
  const bolt = (b: MeshBuilder, x: number, y: number, z: number, s: number): void => {
    b.quad([x - s, y, z + s], [x + s, y, z + s], [x + s, y, z - s], [x - s, y, z - s], SHADE.MetalDark)
  }

  /** A bolt head on a flank, wound for whichever flank it is on. */
  const sideBolt = (b: MeshBuilder, sx: number, x: number, y: number, z: number, s: number): void => {
    const p = [
      [x, y - s, z + s],
      [x, y - s, z - s],
      [x, y + s, z - s],
      [x, y + s, z + s],
    ]
    if (sx > 0) b.quad(p[0], p[1], p[2], p[3], SHADE.MetalDark)
    else b.quad(p[1], p[0], p[3], p[2], SHADE.MetalDark)
  }

  // Hip, knee and sole z for the front pair and the rear pair. Knees stand
  // outboard of hips and soles outboard of knees, so the machine is braced wide
  // rather than propped on four posts under its own belly — the one stance a
  // player can read as "this is not going anywhere".
  const LEGS = [
    [0.3, 0.44, 0.5],
    [-0.3, -0.42, -0.48],
  ]
  const HX = 0.5
  const KX = 0.72
  const FX = 0.76

  // The chassis is a tub with a spaced skirt over each hip, and each skirt has a
  // welded flange bar down it and three bolt heads under that. It is the model's
  // grammar in one detail: a plate, a proud strip, a hard value step across the
  // join. Nothing is drawn under it — the underbody and the hip castings were
  // geometry the fixed camera never once saw.
  const big = new MeshBuilder()
  big.box(0, 0.6, -0.04, 1.14, 0.3, 0.82, SHADE.Owner, SHADE.Owner)
  big.wedge(0, 0.9, -0.04, 1.14, 0.1, 0.82, 0.1, SHADE.Owner, SHADE.Owner)
  for (const sx of [-1, 1]) {
    big.box(sx * 0.62, 0.44, -0.06, 0.14, 0.5, 0.68, SHADE.MetalMid, SHADE.Metal)
    big.box(sx * 0.66, 0.62, -0.06, 0.06, 0.08, 0.68, SHADE.MetalDark)
    for (const z of [-0.24, 0.0, 0.24]) sideBolt(big, sx, sx * 0.692, 0.52, z, 0.045)
    // Exhaust stacks off the back deck. They are the only things that break the
    // roofline behind the shield, and a vertical is the cheapest silhouette there
    // is.
    big.box(sx * 0.26, 1.0, -0.4, 0.18, 0.32, 0.18, SHADE.MetalDark, SHADE.Accent)
  }
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // Four legs: a thigh dropping outward to a knee block, a shin dropping outward
  // again to a planted sole. The limbs are the darkest neutral with a lit collar
  // at the knee and a lit tread plate on the sole, so four legs under a coloured
  // body read as four legs and not as one shadow. The collar is the one place the
  // running gear takes the body's paint, and it puts owner colour down at ankle
  // height where the shield casts none.
  const legs = new MeshBuilder()
  for (const [hz, kz, fz] of LEGS) {
    for (const sx of [-1, 1]) {
      strut(
        legs,
        [sx * KX, 0.34, kz],
        [sx * HX, 0.72, hz],
        [0.13, 0.14],
        [0.17, 0.19],
        SHADE.MetalDark,
      )
      legs.box(sx * KX, 0.24, kz, 0.34, 0.18, 0.34, SHADE.Owner, SHADE.Metal)
      strut(
        legs,
        [sx * FX, 0.09, fz],
        [sx * KX, 0.28, kz],
        [0.16, 0.17],
        [0.12, 0.13],
        SHADE.MetalDark,
      )
      legs.box(sx * FX, 0.0, fz, 0.4, 0.11, 0.44, SHADE.MetalDark, SHADE.MetalMid)
      legs.quad(
        [sx * FX - 0.15, 0.112, fz + 0.17],
        [sx * FX + 0.15, 0.112, fz + 0.17],
        [sx * FX + 0.15, 0.112, fz - 0.17],
        [sx * FX - 0.15, 0.112, fz - 0.17],
        SHADE.Metal,
      )
    }
  }
  r.part(PART.Tread).merge(legs, 0, 0, 0, 0.5)

  // The gun mount carries the shield. A plate bolted to the chassis would armour
  // one bearing forever; hung on the mount it tracks whatever the cannon tracks,
  // which is the whole behaviour of the unit expressed as one moving part.
  const gun = new MeshBuilder()
  gun.cylinder(0, 0.94, -0.04, 0.4, 0.08, 6, SHADE.MetalDark, SHADE.MetalMid)
  gun.box(0, 0.98, -0.08, 0.82, 0.28, 0.46, SHADE.Owner, SHADE.Owner)
  gun.box(-0.22, 1.26, -0.16, 0.26, 0.1, 0.24, SHADE.MetalMid, SHADE.Accent)
  for (const x of [-0.3, 0.3]) {
    for (const z of [-0.24, 0.06]) bolt(gun, x, 1.264, z, 0.05)
  }

  // The shield: wider than the leg stance, raked eighteen degrees, low enough
  // that the legs stay in the silhouette under it, and thick in section. The dark
  // rim down each end is the plate's own edge, and it is what stops the thing
  // reading as a billboard.
  armour(gun, 0, 0.86, 0.56, 0.62, 1.26, 0.9, 0.82, 0.28, SHADE.Owner, SHADE.MetalDark)
  // Weld ribs standing off the face. A recessed panel line would be the truthful
  // way to draw a plate join and it is invisible here; a proud rib catches its
  // own light, which is what reads at this size.
  for (const cx of [-0.7, -0.28, 0.28, 0.7]) {
    armour(gun, cx, 0.92, 0.58, 0.68, 1.24, 0.075, 0.075, 0.1, SHADE.MetalMid, SHADE.Metal)
  }
  // Dozer lip along the bottom, and the capping bar across the top with its bolt
  // row and a sight strip lit under it. The bar is the highest horizontal face on
  // the front half of the machine, so the camera looks straight down onto those
  // bolts — which is the only reason they are worth drawing at all.
  gun.box(0, 0.5, 0.82, 1.72, 0.1, 0.24, SHADE.MetalDark, SHADE.MetalMid)
  gun.box(0, 1.26, 0.6, 1.62, 0.09, 0.28, SHADE.Owner, SHADE.Owner)
  for (let i = 0; i < 7; i++) bolt(gun, -0.6 + i * 0.2, 1.354, 0.6, 0.04)
  gun.quad(
    [-0.68, 1.275, 0.746],
    [0.68, 1.275, 0.746],
    [0.68, 1.34, 0.746],
    [-0.68, 1.34, 0.746],
    SHADE.Accent,
  )
  // A short heavy cannon through the middle of the plate: a near-black mantlet
  // boss sunk into the face, then a tube that steps down twice and stops barely a
  // plate's thickness past its own armour. A long barrel would have made this
  // artillery, and the point is that it holds ground rather than reaching across
  // it. Black against blue is the only value step that survives out here.
  gun.box(0, 0.64, 0.8, 0.48, 0.42, 0.34, SHADE.MetalDark, SHADE.Metal)
  gun.box(0, 0.76, 1.02, 0.26, 0.24, 0.3, SHADE.MetalDark)
  gun.box(0, 0.79, 1.24, 0.19, 0.18, 0.18, SHADE.Metal)
  gun.box(0, 0.76, 1.4, 0.26, 0.24, 0.14, SHADE.MetalDark, SHADE.Accent)
  r.part(PART.Turret).merge(gun, 0, 0, 0, 0.5)
  return r.build()
}

const resonanceVault: Recipe = () => {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  foundation(hull, 2.95, 2.95, 4, 4)

  const big = new MeshBuilder()

  // Two courses of revetment, the lower proud of the upper so the base steps
  // rather than rising as one slab. The deck lands at 1.06, which is 0.53 once
  // this is halved — the height terrain debris stops short of.
  big.box(0, 0.16, 0, 5.4, 0.36, 4.0, SHADE.MetalDark, SHADE.MetalMid)
  big.box(0, 0.52, 0, 5.1, 0.54, 3.8, SHADE.OwnerDark, SHADE.Owner)

  const SIDES = 10
  const AXIS_Y = 1.0
  const AXIS_Z = -0.45
  type Shade = (typeof SHADE)[keyof typeof SHADE]
  const rim = (x: number, k: number, rad: number) => {
    const a = ((k + 0.5) / SIDES) * Math.PI * 2
    return [x, AXIS_Y + Math.sin(a) * rad, AXIS_Z + Math.cos(a) * rad]
  }
  const band = (x0: number, x1: number, rad: number, k: number, shade: Shade) =>
    big.quad(rim(x0, k, rad), rim(x1, k, rad), rim(x1, k + 1, rad), rim(x0, k + 1, rad), shade)

  // The vault: a ten-sided barrel lying the long way, sunk past its springing
  // line and shoved off centre toward the far side, so the deck it leaves over
  // is all on the side the camera can see. Only the arc from the near springing
  // to the crown is ever in frame — the rest of the shell is either inside the
  // revetment or facing away, and is not drawn at all.
  const roof = [
    SHADE.OwnerDark,
    SHADE.OwnerDark,
    SHADE.Owner,
    SHADE.Owner,
    SHADE.OwnerDark,
  ] as const
  for (let i = 0; i < roof.length; i++) band(-2.5, 2.35, 1.25, 9 + i, roof[i])

  // Five saddle straps shrunk over the shell and a heavier flange at the far
  // head. Banding is what turns a tube into a pressure vessel, and only the
  // upper arc of each one is ever in shot.
  for (const sx of [-1.9, -0.95, 0.0, 0.95, 1.85]) {
    for (let k = 9; k <= 12; k++) band(sx - 0.15, sx + 0.15, 1.4, k, SHADE.MetalDark)
  }
  for (let k = 9; k <= 12; k++) band(-2.52, -2.2, 1.48, k, SHADE.Metal)

  // Two relief stacks of unequal weight, set a little off the ridge so the one
  // facet the sun is fully on stays clear of them.
  big.box(-1.4, 2.05, -0.45, 0.5, 0.66, 0.5, SHADE.MetalDark, SHADE.Metal)
  big.box(-1.4, 2.71, -0.45, 0.68, 0.18, 0.68, SHADE.Metal)
  big.box(0.5, 2.12, -0.45, 0.34, 0.42, 0.34, SHADE.MetalDark, SHADE.Metal)
  big.box(0.5, 2.54, -0.45, 0.46, 0.14, 0.46, SHADE.Metal)

  // The headwall: a slab thicker than the shell it caps, standing proud of the
  // revetment and above the crown, with the door plated *onto* its face rather
  // than let into it. Nothing about this building opens.
  big.box(2.52, 0.16, -0.4, 0.5, 2.66, 3.3, SHADE.MetalMid, SHADE.Metal)
  big.box(2.86, 0.16, -0.45, 0.32, 0.42, 2.1, SHADE.MetalDark, SHADE.Metal)
  big.box(2.84, 0.58, -0.45, 0.14, 1.62, 1.7, SHADE.Owner, SHADE.Metal)
  for (const sz of [-1.42, 0.52]) {
    big.box(2.82, 0.16, sz, 0.2, 2.24, 0.3, SHADE.Metal)
    for (const ry of [0.7, 1.72]) big.box(2.94, ry, sz, 0.2, 0.2, 0.2, SHADE.MetalDark)
  }
  big.box(2.82, 2.2, -0.45, 0.2, 0.28, 2.4, SHADE.Metal)
  for (const cz of [-1.35, -0.45, 0.45]) big.box(2.52, 2.82, cz, 0.26, 0.2, 0.26, SHADE.MetalDark)
  big.box(2.8, 2.52, -0.45, 0.16, 0.16, 1.9, SHADE.Accent)
  // Dog bolts across the leaf, two rows of three.
  for (const by of [0.9, 1.82]) {
    for (const bz of [-1.05, -0.45, 0.15]) {
      big.box(2.94, by, bz, 0.2, 0.2, 0.2, SHADE.Metal)
    }
  }

  // A girt across the near wall, and three counterforts standing off it, capped
  // and bolted. These are the silhouette job: a long low mass with blunt teeth
  // down one side is a shape neither the pyramids nor the needles beside it can
  // make. Three rather than four, spaced unevenly, because the gaps are what
  // let the deck behind them show.
  big.box(0, 0.62, 1.98, 5.0, 0.26, 0.18, SHADE.Metal)
  for (const bx of [-1.95, -0.15, 1.7]) {
    big.box(bx, 0.16, 2.1, 0.72, 1.02, 0.8, SHADE.Owner, SHADE.OwnerLight)
    big.box(bx, 1.18, 2.1, 0.84, 0.16, 0.92, SHADE.MetalMid, SHADE.Metal)
    for (const ry of [0.38, 0.9]) big.box(bx, ry, 2.52, 0.22, 0.22, 0.16, SHADE.Metal)
  }

  // What the off-centre vault leaves on the near side: a lit conduit run along
  // the springing, two bolted-down access hatches, and a kerb at the edge.
  big.box(-0.25, 1.06, 0.92, 4.4, 0.1, 0.24, SHADE.Accent)
  big.box(-1.05, 1.06, 1.42, 1.1, 0.14, 0.78, SHADE.MetalDark, SHADE.Metal)
  big.box(0.85, 1.06, 1.42, 0.9, 0.14, 0.78, SHADE.MetalDark, SHADE.Metal)
  big.box(0, 1.06, 1.76, 5.0, 0.2, 0.16, SHADE.MetalDark, SHADE.Metal)

  // The far head is blanked off with a heat sink where the near one has a door.
  big.box(-2.56, 0.52, -0.45, 0.44, 2.1, 2.9, SHADE.MetalDark, SHADE.MetalMid)
  for (const fz of [-1.6, -0.85, -0.1, 0.65]) {
    big.box(-2.56, 2.62, fz, 0.62, 0.34, 0.2, SHADE.Metal)
  }

  hull.merge(big, 0, 0, 0, 0.5)
  return r.build()
}

const arcLancer: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]

  // Stations, tail to prow: [z, xBelly, xChine, xDeck, yBelly, yChine, yDeck].
  // The widest line is the chine at the rear quarter and the hull sheds width
  // continuously from there forward, which is what makes the plan an arrowhead
  // rather than a box with a nose on it. The trailing edge pulls back in so the
  // tail is cropped rather than square.
  const stations = [
    [-0.68, 0.44, 0.56, 0.4, 0.16, 0.3, 0.44],
    [-0.34, 0.62, 0.8, 0.56, 0.12, 0.28, 0.48],
    [0.06, 0.5, 0.66, 0.44, 0.1, 0.26, 0.46],
    [0.44, 0.3, 0.42, 0.24, 0.12, 0.24, 0.36],
    [0.74, 0.08, 0.14, 0.06, 0.18, 0.24, 0.28],
  ]
  /**
   * One side of a station, bottom to top: belly, chine bottom, chine top,
   * shoulder, deck. The shoulder is derived rather than tabulated — it sits
   * three quarters of the way up and four fifths of the way out — so the flank
   * above the chine breaks into a near-vertical strake and a hard chamfer down
   * to the deck. Two planes at eighty degrees to each other take visibly
   * different light off one sun, which is a crease running the whole length of
   * the casting for two quads a segment and no colour change at all.
   */
  const profile = (s: number[], sx: number): number[][] => [
    [sx * s[1], s[4]],
    [sx * s[2], s[5] - 0.045],
    [sx * s[2], s[5] + 0.045],
    [sx * (s[3] + (s[2] - s[3]) * 0.8), s[5] + (s[6] - s[5]) * 0.75],
    [sx * s[3], s[6]],
  ]
  const FLANK: Role[] = [SHADE.OwnerDark, SHADE.MetalMid, SHADE.Owner, SHADE.OwnerLight]
  // Deck lanes as fractions of the deck half-width. The neutral pair are panel
  // seams and the thin lit pair are the feed running forward to the emitter;
  // both cost a quad a segment and both follow the taper, because they are cut
  // from the same station widths the taper is.
  const LANES: Array<[number, number, Role]> = [
    [-1, -0.72, SHADE.Owner],
    [-0.72, -0.66, SHADE.Accent],
    [-0.66, -0.48, SHADE.MetalMid],
    [-0.48, 0.48, SHADE.Owner],
    [0.48, 0.66, SHADE.MetalMid],
    [0.66, 0.72, SHADE.Accent],
    [0.72, 1, SHADE.Owner],
  ]

  const big = new MeshBuilder()
  for (let i = 0; i < stations.length - 1; i++) {
    const a = stations[i]
    const b = stations[i + 1]
    const za = a[0]
    const zb = b[0]
    for (const sx of [1, -1]) {
      const pa = profile(a, sx)
      const pb = profile(b, sx)
      for (let k = 0; k < 4; k++) {
        const q0 = [pa[k][0], pa[k][1], za]
        const q1 = [pa[k + 1][0], pa[k + 1][1], za]
        const q2 = [pb[k + 1][0], pb[k + 1][1], zb]
        const q3 = [pb[k][0], pb[k][1], zb]
        if (sx > 0) big.quad(q0, q1, q2, q3, FLANK[k])
        else big.quad(q3, q2, q1, q0, FLANK[k])
      }
    }
    for (const [f0, f1, shade] of LANES) {
      big.quad(
        [f0 * b[3], b[6], zb],
        [f1 * b[3], b[6], zb],
        [f1 * a[3], a[6], za],
        [f0 * a[3], a[6], za],
        shade,
      )
    }
    big.quad(
      [-a[1], a[4], za],
      [a[1], a[4], za],
      [b[1], b[4], zb],
      [-b[1], b[4], zb],
      SHADE.MetalDark,
    )
  }
  // End caps, closed band by band off the same profile. The tail plate is the
  // radiator bulkhead and goes neutral, which is the one hard value break along
  // the length; the prow is a few centimetres of chisel and stays in colour.
  for (const [s, front, shade] of [
    [stations[0], false, SHADE.MetalMid],
    [stations[stations.length - 1], true, SHADE.Owner],
  ] as Array<[number[], boolean, Role]>) {
    const p = profile(s, 1)
    for (let k = 0; k < 4; k++) {
      const q = [
        [p[k][0], p[k][1], s[0]],
        [-p[k][0], p[k][1], s[0]],
        [-p[k + 1][0], p[k + 1][1], s[0]],
        [p[k + 1][0], p[k + 1][1], s[0]],
      ]
      if (front) big.quad(q[1], q[0], q[3], q[2], shade)
      else big.quad(q[0], q[1], q[2], q[3], shade)
    }
  }

  // The capacitor spine, and the reason this reads at a glance: one continuous
  // blade swept back from the emitter to the tail, drawn as an outline in the
  // z-y plane and given a thickness that thins as it rises. A stack of boxes
  // was tried first and came out a staircase — the whole point of the form is
  // that no two planes on it meet at a right angle.
  //
  // The crest takes the glow rather than the leading edge. A face pointing up
  // and *forward* is the one direction the sun does not reach here, so a lit
  // leading edge renders near-black; the crest is a top face and carries.
  const blade: number[][] = [
    [0.04, 0.4, 0.13],
    [-0.06, 0.78, 0.13],
    [-0.26, 1.04, 0.14],
    [-0.48, 1.16, 0.12],
    [-0.64, 0.92, 0.11],
    [-0.68, 0.4, 0.13],
  ]
  const CREST: Role[] = [
    SHADE.MetalMid,
    SHADE.MetalMid,
    SHADE.Accent,
    SHADE.Accent,
    SHADE.MetalDark,
  ]
  for (const sx of [1, -1]) {
    for (let i = 1; i < blade.length - 1; i++) {
      const v = (k: number) => [sx * blade[k][2], blade[k][1], blade[k][0]]
      if (sx > 0) big.tri(v(0), v(i + 1), v(i), SHADE.OwnerDark)
      else big.tri(v(0), v(i), v(i + 1), SHADE.OwnerDark)
    }
  }
  for (let i = 0; i < blade.length - 1; i++) {
    const p = blade[i]
    const q = blade[i + 1]
    big.quad(
      [p[2], p[1], p[0]],
      [q[2], q[1], q[0]],
      [-q[2], q[1], q[0]],
      [-p[2], p[1], p[0]],
      CREST[i],
    )
  }
  // The root the blade grows out of, swollen where it meets the deck so the
  // two do not simply intersect at a line.
  big.wedge(0, 0.42, -0.3, 0.46, 0.08, 0.76, 0.12, SHADE.Owner, SHADE.Owner)
  // Heat let out either side of the spine's root. Sunk until only their lids
  // stand proud, because the one thing this hull must not have on it is a part
  // that looks bolted to the outside.
  for (const sx of [-1, 1]) {
    big.box(sx * 0.22, 0.43, -0.52, 0.2, 0.06, 0.14, SHADE.MetalDark, SHADE.Accent)
  }
  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // Short strips set well inboard, so the chine flare overhangs them along their
  // whole length and the vehicle reads as riding low rather than as standing on
  // its running gear.
  const track = new MeshBuilder()
  for (const sx of [-1, 1]) {
    track.treadBand(sx * 0.52, -0.24, 0.6, 0.2, SHADE.MetalDark)
  }
  r.part(PART.Tread).merge(track, 0, 0, 0, 0.5)

  // The emitter: a collar sunk into the fore-deck, a faired shoulder, and two
  // tines drawn forward past the prow with the lens hung in the gap between
  // them. There is no muzzle anywhere on it — the thing that says "weapon" is
  // the fork, and the fork is what the beam leaves from.
  const head = new MeshBuilder()
  head.cylinder(0, 0.36, 0.24, 0.32, 0.12, 6, SHADE.MetalDark, SHADE.MetalMid)
  head.wedge(0, 0.46, 0.28, 0.48, 0.16, 0.36, 0.08, SHADE.Owner, SHADE.Owner)
  for (const sx of [-1, 1]) {
    head.wedge(sx * 0.22, 0.44, 0.58, 0.18, 0.14, 0.36, 0.04, SHADE.MetalMid, SHADE.Metal)
    head.box(sx * 0.22, 0.46, 0.8, 0.15, 0.11, 0.12, SHADE.MetalDark, SHADE.Accent)
  }
  head.box(0, 0.44, 0.48, 0.52, 0.1, 0.14, SHADE.MetalMid, SHADE.Metal)
  head.cylinder(0, 0.5, 0.6, 0.16, 0.26, 6, SHADE.Accent, SHADE.OwnerLight)
  r.part(PART.Turret).merge(head, 0, 0, 0, 0.5)
  return r.build()
}

const voidMortar: Recipe = () => {
  const r = new RecipeBuilder()
  type Shade = (typeof SHADE)[keyof typeof SHADE]

  /** Sixty degrees of elevation, leaning back across the tail. */
  const TILT = Math.PI - 1.047
  const AY = Math.sin(TILT)
  const AZ = Math.cos(TILT)
  const BY = 0.78
  const BZ = 0.28

  /**
   * A prism swept along an axis tilted in the y–z plane. `cylinder` only runs up
   * Y and the whole of this vehicle is a tube standing at sixty degrees, so the
   * ring is wound by hand against a right-handed frame — which also means the
   * sides come out with *outward* normals and light properly, where a drum from
   * `cylinder` is shaded from the inside.
   */
  const sleeve = (
    b: MeshBuilder,
    cy: number,
    cz: number,
    len: number,
    rad: number,
    topRad: number,
    sides: number,
    off: number,
    shade: Shade,
    capShade: Shade,
  ): void => {
    const ring: number[][] = []
    const cap: number[][] = []
    for (let i = 0; i < sides; i++) {
      const a = (i / sides) * Math.PI * 2 + off
      const c = Math.cos(a)
      const s = Math.sin(a)
      ring.push([c * rad, cy + s * rad * AZ, cz - s * rad * AY])
      cap.push([c * topRad, cy + AY * len + s * topRad * AZ, cz + AZ * len - s * topRad * AY])
    }
    for (let i = 0; i < sides; i++) {
      const j = (i + 1) % sides
      b.quad(ring[i], ring[j], cap[j], cap[i], shade)
    }
    for (let i = 1; i < sides - 1; i++) b.tri(cap[0], cap[i], cap[i + 1], capShade)
  }

  /** Where a point `s` along the tube's axis sits, for hanging bands on it. */
  const along = (s: number): [number, number] => [BY + AY * s, BZ + AZ * s]

  // Squat, wide and blunt: broader across the tracks than it is long, which no
  // other chassis on the field is, and carrying a tube half the width of the
  // vehicle straight up out of the middle of it.
  const big = new MeshBuilder()
  big.box(0, 0.08, 0, 1.16, 0.38, 0.98, SHADE.Owner, SHADE.Owner)
  // The weld lip where the tub meets the deck, proud all the way round: a step
  // for the light to find on a hull that is otherwise one slab.
  big.box(0, 0.44, 0, 1.22, 0.06, 1.04, SHADE.MetalDark, SHADE.Metal)
  big.box(0, 0.5, 0, 1.06, 0.09, 0.9, SHADE.Owner, SHADE.OwnerLight)

  // Skirts hung over the tracks. Bolt plates are painted onto their outer face
  // rather than built proud of it — at a tenth of a unit across, the step a real
  // stud would cast is worth less than the colour break, and costs five times
  // the triangles.
  for (const sx of [-1, 1]) {
    big.box(sx * 0.63, 0.1, 0, 0.1, 0.32, 0.94, SHADE.OwnerDark, SHADE.Metal)
    for (let i = 0; i < 3; i++) {
      const x = sx * 0.686
      const z = -0.28 + i * 0.28
      const a = 0.075
      big.quad(
        [x, 0.19, z + sx * a],
        [x, 0.19, z - sx * a],
        [x, 0.34, z - sx * a],
        [x, 0.34, z + sx * a],
        SHADE.MetalMid,
      )
    }
  }

  // Bow plate, bolted over the nose rather than blended into it, with a chevron
  // painted across it.
  big.box(0, 0.06, 0.53, 1.04, 0.4, 0.12, SHADE.OwnerDark, SHADE.Metal)
  big.box(0, 0.36, 0.56, 0.5, 0.12, 0.14, SHADE.MetalDark, SHADE.Metal)
  for (const x of [-0.26, 0.26]) {
    big.quad(
      [x - 0.12, 0.12, 0.596],
      [x + 0.12, 0.12, 0.596],
      [x + 0.12, 0.3, 0.596],
      [x - 0.12, 0.3, 0.596],
      SHADE.Accent,
    )
  }

  // One recoil spade across the tail, blade down: the silhouette cue that says a
  // thing plants itself before it fires.
  big.wedge(0, 0, -0.58, 0.96, 0.36, 0.16, -0.06, SHADE.MetalDark, SHADE.MetalMid)

  // The barbette the tube turns on.
  big.box(0, 0.59, 0.02, 0.8, 0.08, 0.8, SHADE.MetalDark, SHADE.MetalMid)
  // Exhaust stack, stowage bin and a hatch, all on the deck the barbette leaves
  // over. Their lit tops are the whole accent budget of the chassis.
  big.box(0.5, 0.59, -0.38, 0.22, 0.36, 0.22, SHADE.MetalDark, SHADE.Accent)
  big.box(-0.44, 0.59, 0.3, 0.3, 0.16, 0.26, SHADE.MetalDark, SHADE.Accent)

  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // Long bands, halved: `treadBand` counts cleats off its own length, so 0.86 of
  // track scaled down comes back with seven ribs where a band drawn at final
  // size would have had three.
  const run = new MeshBuilder()
  run.treadBand(-0.61, 0, 0.86, 0.3, SHADE.MetalDark)
  run.treadBand(0.61, 0, 0.86, 0.3, SHADE.MetalDark)
  r.part(PART.Tread).merge(run, 0, 0, 0, 0.5)

  const gun = new MeshBuilder()
  gun.cylinder(0, 0.67, 0.02, 0.36, 0.09, 6, SHADE.MetalDark, SHADE.MetalMid)
  // The breech: a slab of owner colour hung off the back of the trunnions to
  // counterweight a tube that is entirely in front of them.
  gun.box(0, 0.76, 0.38, 0.66, 0.26, 0.34, SHADE.Owner, SHADE.Owner)
  gun.box(0, 0.7, 0.54, 0.22, 0.3, 0.14, SHADE.MetalDark, SHADE.Accent)
  for (const sx of [-1, 1]) {
    gun.box(sx * 0.33, 0.68, 0.1, 0.14, 0.34, 0.46, SHADE.MetalMid, SHADE.Metal)
  }
  // A short fat tube, clamped in a square yoke — the whole direction in one part
  // — and collared at the muzzle so the bore reads as a dark hole inside a lit
  // ring. Both bands are a twentieth of a unit thick once this is halved, which
  // is the sort of part that never gets drawn at final scale.
  sleeve(gun, BY, BZ, 0.88, 0.28, 0.25, 8, 0, SHADE.Metal, SHADE.MetalDark)
  {
    const [y, z] = along(0.28)
    sleeve(gun, y, z, 0.14, 0.37, 0.37, 4, Math.PI / 4, SHADE.MetalDark, SHADE.MetalDark)
  }
  {
    const [y, z] = along(0.52)
    sleeve(gun, y, z, 0.07, 0.32, 0.32, 4, Math.PI / 4, SHADE.MetalDark, SHADE.MetalDark)
  }
  {
    const [y, z] = along(0.72)
    sleeve(gun, y, z, 0.13, 0.38, 0.38, 8, 0, SHADE.MetalMid, SHADE.Accent)
  }
  gun.box(-0.4, 0.92, -0.06, 0.1, 0.1, 0.14, SHADE.Accent)
  r.part(PART.Turret).merge(gun, 0, 0, 0, 0.5)

  return r.build()
}

/**
 * A marksman: one figure, kneeling, behind a very long barrel.
 *
 * The whole silhouette job here is separating it from a Rifle Squad at
 * gameplay zoom, and the squad's read is *three* small figures. So this is
 * deliberately one — lower and longer, with a barrel that overhangs the cell.
 * Count and profile do the work; nothing about the surface would survive.
 */
function marksman(): MeshRecipe {
  const r = new RecipeBuilder()
  fieldworks(r)
  const hull = r.part(PART.Hull)

  // Kneeling: legs folded under, torso low and pitched forward. Grown with the
  // rest of the infantry, and kept lower than a standing trooper — the height
  // difference is half of what separates the two at a glance.
  hull.box(-0.04, 0, -0.03, 0.12, 0.07, 0.18, SHADE.OwnerDark)
  hull.box(0.07, 0, 0.03, 0.1, 0.07, 0.13, SHADE.OwnerDark)
  // Neutral torso under a team-coloured shoulder line, the same five bands the
  // riflemen carry — these two are the same army and should be built alike.
  hull.box(0, 0.07, 0, 0.17, 0.11, 0.14, SHADE.Owner)
  // Arms out along the rifle, in the same skin tone the riflemen carry.
  hull.box(-0.1, 0.11, 0.06, 0.045, 0.06, 0.12, SHADE.Accent)
  hull.box(0.1, 0.09, 0.02, 0.045, 0.06, 0.1, SHADE.Accent)
  // Head and helmet: skin under a helmet in the ramp's light step, wider than
  // the skull under it — the same close a rifleman gets, so the two read as the
  // same army from above.
  hull.box(0, 0.18, 0.02, 0.095, 0.07, 0.09, SHADE.Accent)
  hull.box(0, 0.25, 0.01, 0.14, 0.045, 0.126, SHADE.OwnerLight, SHADE.OwnerLight)
  // A pack behind the shoulders, the mass every figure in the roster carries.
  hull.box(0, 0.08, -0.11, 0.1, 0.09, 0.06, SHADE.MetalDark)

  // The rifle, along the unit's forward axis and overhanging the cell. This is
  // the read: nothing else this small has a straight line coming out of it, and
  // it only reads if the line is two pixels rather than one.
  hull.box(0.02, 0.17, 0.28, 0.048, 0.048, 0.44, SHADE.MetalMid)
  hull.box(0.02, 0.17, 0.53, 0.07, 0.06, 0.08, SHADE.Metal)
  // Bipod under the muzzle, so it reads as set up rather than carried.
  hull.box(-0.01, 0, 0.44, 0.025, 0.17, 0.025, SHADE.MetalDark)
  hull.box(0.05, 0, 0.44, 0.025, 0.17, 0.025, SHADE.MetalDark)
  // A spotting scope alongside, which is also what sells the extra vision.
  hull.box(-0.1, 0.16, 0.14, 0.05, 0.05, 0.14, SHADE.Metal)
  return r.build()
}

/**
 * The Ascendancy's marksman. Identical numbers to the Helios one, and
 * deliberately not the identical model.
 *
 * Sharing the recipe was the obvious economy and the critic caught it within a
 * round: the two came out confusable, which for a unit that outranges
 * everything is the worst possible thing to be unsure about mid-fight. The
 * factions have separate shape languages — Helios boxy, horizontal,
 * symmetrical; the Ascendancy angular, vertical, asymmetric — so where the
 * Marksman kneels behind a long horizontal barrel on a bipod, this stands
 * upright behind a vertical spire, offset to one side.
 */
function nullifier(): MeshRecipe {
  const r = new RecipeBuilder()
  fieldworks(r)
  const hull = r.part(PART.Hull)

  // A splayed tripod, wide at the ground and converging upward. This is the
  // half of the silhouette that does the separating: the Drone Swarm is a
  // loose cloud of four small bodies at varying heights, and the first version
  // of this — one narrow standing figure — read as a sparser version of the
  // same thing. A shape that is *widest at the bottom and comes to a point* is
  // the one arrangement in the Ascendancy roster that a cloud never makes.
  for (const [x, z] of [
    [-0.19, 0.14],
    [0.19, 0.14],
    [0, -0.22],
  ]) {
    hull.box(x, 0, z, 0.045, 0.2, 0.045, SHADE.MetalDark)
    hull.box(x, 0.19, z, 0.06, 0.03, 0.06, SHADE.Metal)
  }
  hull.cylinder(0, 0.2, 0, 0.12, 0.1, 6, SHADE.OwnerDark, SHADE.Owner)

  // The lance, vertical and tall enough to dominate. Nothing else this small
  // in the roster is more than twice as tall as it is wide.
  hull.cylinder(0, 0.3, 0, 0.035, 0.46, 6, SHADE.Owner, SHADE.Accent, 0.012)
  hull.cylinder(0, 0.52, 0, 0.08, 0.03, 6, SHADE.MetalDark)
  hull.box(0, 0.78, 0, 0.05, 0.06, 0.05, SHADE.Accent)
  return r.build()
}

/**
 * The IFV chassis. One idea, two shape grammars.
 *
 * Low and open-topped, with a ring mount where a passenger's weapon would go —
 * the silhouette has to say "there is room for somebody in here", because that
 * is the entire unit.
 *
 * It used to be one recipe registered against both `Outrider` and `Vector`, and
 * that made it the one genuinely shared vehicle mesh in the game: two units
 * whose outlines were identical pixel for pixel and differed only in hull tint,
 * which is precisely the complaint a jury raised about the roster as a whole.
 * The argument for sharing was that the *chassis volumes* are the shared idea,
 * and that still holds — so the volumes stay, and what diverges is the two
 * things the factions already diverge on everywhere else: **taper** and
 * **locomotion**. Helios gets a parallel-sided body on wheels running proud of
 * the hull in sponsons, with a square bay rim. The Ascendancy gets the same body
 * with its top face pulled well in, riding two hover lobes, under a faceted
 * hex collar that matches its cone-roofed buildings. The ring-mount fittings
 * above the collar are untouched: they read the passenger, not the faction.
 */
function ifv(ascendancy: boolean): MeshRecipe {
  const r = new RecipeBuilder()
  const hull = r.part(PART.Hull)
  // A low body, wide at the back and cut down at the front — parallel-sided for
  // Helios, strongly tapered for the Ascendancy.
  hull.wedge(0, 0.1, -0.05, 0.44, 0.16, 0.62, ascendancy ? 0.09 : 0.02, SHADE.Owner, SHADE.Owner)
  hull.box(0, 0.09, -0.3, 0.4, 0.12, 0.12, SHADE.MetalDark)
  // The open bay: a rim with a dark interior, which is what says it carries
  // somebody rather than being a small tank. The rim is the mid chassis value
  // against a team-coloured deck — it is the biggest single face on the unit
  // from above, so it is where the part boundary has to be visible, and in the
  // light neutral it was a white square with a hole in it.
  //
  // The Ascendancy's is a hex collar, and it has to stay *narrower* than the
  // tapered deck it stands on — sized to the Helios rim it swallowed the whole
  // top face and the team colour left the unit entirely.
  if (ascendancy) {
    hull.cylinder(0, 0.24, -0.06, 0.15, 0.05, 6, SHADE.MetalMid, SHADE.MetalMid)
    hull.cylinder(0, 0.26, -0.06, 0.1, 0.04, 6, SHADE.MetalDark)
    // A lit blade of a prow rather than the Directorate's blunt lamp block.
    hull.box(0, 0.16, 0.26, 0.1, 0.05, 0.16, SHADE.Accent)
  } else {
    hull.box(0, 0.26, -0.06, 0.34, 0.03, 0.34, SHADE.MetalDark)
    hull.box(0, 0.24, -0.06, 0.4, 0.05, 0.4, SHADE.MetalMid)
    hull.box(0, 0.18, 0.22, 0.26, 0.1, 0.14, SHADE.Accent)
  }

  const tread = r.part(PART.Tread)
  if (ascendancy) {
    // Two hover lobes with a lit rim, the running gear every other Ascendancy
    // vehicle wears. Nothing this faction fields touches the ground.
    for (const z of [-0.24, 0.14]) {
      tread.cylinder(0, 0, z, 0.2, 0.06, 6, SHADE.MetalDark, SHADE.Accent)
      tread.cylinder(0, 0.06, z, 0.16, 0.03, 6, SHADE.Accent)
    }
  } else {
    // Six wheels in sponsons standing clear of the hull, tied together by a
    // near-black sill: the separate lower tier that keeps a low vehicle from
    // reading as one plate under an overhead camera.
    for (const x of [-0.23, 0.23]) {
      tread.box(x, 0.03, 0.02, 0.09, 0.07, 0.5, SHADE.MetalDark)
    }
    for (const z of [-0.2, 0.02, 0.24]) {
      tread.cylinder(-0.26, 0.02, z, 0.09, 0.09, 6, SHADE.MetalDark)
      tread.cylinder(0.26, 0.02, z, 0.09, 0.09, 6, SHADE.MetalDark)
    }
  }

  // The ring mount turns with the turret, so whatever the passenger is firing
  // tracks the target the way every other weapon does.
  const turret = r.part(PART.Turret)
  turret.cylinder(0, 0.29, -0.06, 0.13, 0.07, ascendancy ? 6 : 8, SHADE.Metal, SHADE.MetalDark)

  // What sits on the ring depends on who is riding — the renderer shows
  // exactly one of these, which is how a glance at the vehicle answers the
  // only question the unit poses: what did the player put in it?
  //
  // Empty: the chassis stub, deliberately unimpressive.
  const stub = r.part(PART.MountStub)
  stub.box(0, 0.32, 0.14, 0.05, 0.05, 0.3, SHADE.MetalDark)

  // A rifleman or anything else with a gun: a twin mount with a shield plate,
  // the classic open-top silhouette.
  const gun = r.part(PART.MountGun)
  gun.box(0, 0.33, 0.02, 0.14, 0.09, 0.12, SHADE.MetalDark)
  gun.box(-0.045, 0.35, 0.2, 0.03, 0.03, 0.26, SHADE.Metal)
  gun.box(0.045, 0.35, 0.2, 0.03, 0.03, 0.26, SHADE.Metal)
  gun.box(0, 0.36, -0.04, 0.2, 0.1, 0.03, SHADE.Owner)

  // A marksman: one long, thin, unmistakable barrel with a scope block. The
  // length is the signal — it out-reaches everything, and looks like it.
  const long = r.part(PART.MountLong)
  long.box(0, 0.34, 0.04, 0.07, 0.07, 0.1, SHADE.MetalDark)
  long.box(0, 0.35, 0.32, 0.025, 0.025, 0.5, SHADE.Metal)
  long.box(0, 0.4, 0.06, 0.03, 0.04, 0.08, SHADE.Accent)

  // The engineer: a crane arm folded over the bay, tipped with the welding
  // glow. Reads as "tool, not weapon" from across the map, which is the whole
  // point — this one heals.
  const crane = r.part(PART.MountCrane)
  crane.box(0, 0.36, -0.02, 0.06, 0.14, 0.06, SHADE.Metal)
  crane.box(0, 0.45, 0.1, 0.045, 0.045, 0.32, SHADE.MetalDark)
  crane.box(0, 0.4, 0.26, 0.04, 0.1, 0.04, SHADE.Metal)
  crane.box(0, 0.34, 0.26, 0.05, 0.03, 0.05, SHADE.Accent)
  return r.build()
}

const colossus: Recipe = () => {
  const r = new RecipeBuilder()
  type Role = (typeof SHADE)[keyof typeof SHADE]

  /** A painted detail on a forward face: one quad standing a hair off the plate. */
  const faceZ = (
    b: MeshBuilder,
    cx: number,
    y0: number,
    cz: number,
    w: number,
    h: number,
    shade: Role,
  ): void => {
    b.quad(
      [cx - w / 2, y0, cz],
      [cx + w / 2, y0, cz],
      [cx + w / 2, y0 + h, cz],
      [cx - w / 2, y0 + h, cz],
      shade,
    )
  }

  /** The same on a flank. `sx` picks which flank, so a mirrored pair both face out. */
  const faceX = (
    b: MeshBuilder,
    cx: number,
    y0: number,
    cz: number,
    d: number,
    h: number,
    sx: number,
    shade: Role,
  ): void => {
    const a = [cx, y0, cz + (sx * d) / 2]
    const c = [cx, y0, cz - (sx * d) / 2]
    b.quad(a, c, [c[0], y0 + h, c[2]], [a[0], y0 + h, a[2]], shade)
  }

  /** The same on a deck, which on this camera is the face that gets seen most. */
  const faceY = (
    b: MeshBuilder,
    cx: number,
    y: number,
    cz: number,
    w: number,
    d: number,
    shade: Role,
  ): void => {
    b.quad(
      [cx - w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz + d / 2],
      [cx + w / 2, y, cz - d / 2],
      [cx - w / 2, y, cz - d / 2],
      shade,
    )
  }

  /**
   * Hazard blocking: alternating accent and near-black across a strip. Painted
   * warning bands are the cheapest thing on a real machine and the loudest thing
   * on this one, and a row of them along the front lip of the shoulder deck puts
   * a hard beat across the widest line the model has.
   */
  const hazard = (
    b: MeshBuilder,
    cx: number,
    y: number,
    cz: number,
    w: number,
    d: number,
    n: number,
  ): void => {
    const step = w / n
    for (let i = 0; i < n; i++) {
      faceY(
        b,
        cx - w / 2 + (i + 0.5) * step,
        y,
        cz,
        step * 0.8,
        d,
        i % 2 === 0 ? SHADE.Accent : SHADE.MetalDark,
      )
    }
  }

  // -- Legs -----------------------------------------------------------------
  // Two columns, stance wider than the yoke they carry and taller than the body
  // they carry it with: ground to hip is more than half the machine, which is
  // what keeps this a walker rather than a hull with legs drawn under it. Four
  // slabs each, narrowing going up — broad sole, tall shin, short thigh — so it
  // reads planted rather than stilted.
  for (const sx of [-1, 1]) {
    const x = sx * 0.64
    const legs = new MeshBuilder()

    // Sole, with six grouser teeth cut down its outer edge. A tooth is 0.045
    // high at final scale and is the finest repeat on the model.
    legs.box(x, 0, -0.02, 0.54, 0.16, 1.0, SHADE.MetalDark, SHADE.MetalMid)
    for (let i = 0; i < 6; i++) {
      faceX(legs, x + sx * 0.282, 0.03, -0.42 + i * 0.16, 0.1, 0.09, sx, SHADE.MetalMid)
    }
    legs.box(x, 0.16, 0.32, 0.5, 0.08, 0.32, SHADE.MetalMid, SHADE.Metal)

    // Shin: the tallest single plate on the machine, in owner colour, because
    // the legs are half of what the eye gets of this thing head-on. A bolted
    // guard stands proud of its face so the tall plate is two pieces, not one.
    legs.box(x, 0.24, -0.02, 0.5, 0.76, 0.46, SHADE.Owner)
    legs.box(x, 0.36, 0.235, 0.4, 0.54, 0.06, SHADE.MetalMid)
    for (const bx of [-0.15, 0.15]) {
      for (const by of [0.42, 0.8]) {
        faceZ(legs, x + bx, by, 0.272, 0.09, 0.09, SHADE.MetalDark)
      }
    }

    // Knee: a chamfered dark block between shin and thigh, wider than both, so
    // the joint reads as a joint and not as the corner where two boxes meet.
    legs.wedge(x, 0.94, -0.02, 0.58, 0.16, 0.54, 0.06, SHADE.MetalDark, SHADE.Metal)

    // Thigh, set a little aft of the shin so the machine leans into its work.
    legs.box(x, 1.06, -0.07, 0.44, 0.34, 0.48, SHADE.Owner)
    r.part(sx < 0 ? PART.LegA : PART.LegB).merge(legs, 0, 0, 0, 0.5)
  }

  // -- Body -----------------------------------------------------------------
  const big = new MeshBuilder()

  // Hip beam: one dark box bridging both legs. It is what stops them reading as
  // two machines standing next to each other.
  big.box(0, 1.32, -0.07, 1.42, 0.26, 0.56, SHADE.MetalDark, SHADE.MetalMid)

  // Waist, then chest: two owner-coloured masses, each wider than the one under
  // it, so the body corbels out to the yoke instead of rising as a single slab.
  big.box(0, 1.54, 0.0, 1.42, 0.3, 0.7, SHADE.Owner)
  big.box(0, 1.82, 0.02, 1.56, 0.24, 0.76, SHADE.Owner)

  // Glacis: a near-black plate bolted across the whole front and held off the
  // body by its own thickness, with a louvre grille let into it. Dark rather
  // than mid grey deliberately — the front is the one place the model can afford
  // a hard two-value break, and grey plate on a grey-green body reads as nothing.
  big.box(0, 1.66, 0.45, 1.0, 0.48, 0.1, SHADE.MetalDark, SHADE.MetalMid)
  for (let i = 0; i < 3; i++) {
    faceZ(big, 0, 1.75 + i * 0.09, 0.512, 0.62, 0.05, SHADE.Metal)
  }
  for (const bx of [-0.42, 0.42]) {
    faceZ(big, bx, 1.71, 0.512, 0.09, 0.09, SHADE.Metal)
    faceZ(big, bx, 2.03, 0.512, 0.09, 0.09, SHADE.Metal)
  }

  // Shoulder yoke: the widest plate on the machine and the lid of the box, with
  // the hazard blocking along its front lip.
  big.box(0, 2.02, -0.02, 1.76, 0.2, 0.66, SHADE.Owner)
  hazard(big, 0, 2.222, 0.26, 1.5, 0.1, 9)
  faceZ(big, 0, 2.06, 0.312, 1.66, 0.05, SHADE.MetalDark)
  faceZ(big, 0, 2.15, 0.312, 1.66, 0.05, SHADE.MetalDark)

  // Engine house, cantilevered off the back and standing above the yoke line so
  // the roofline is never one flat edge, with three stacks on its deck.
  big.box(0, 1.66, -0.48, 1.24, 0.6, 0.36, SHADE.MetalDark, SHADE.MetalMid)
  for (const ex of [-0.4, 0, 0.4]) {
    big.box(ex, 2.26, -0.46, 0.18, 0.26, 0.18, SHADE.MetalDark, SHADE.Accent)
  }

  // -- Starboard arm: the incinerator ---------------------------------------
  // A blunt throat on a slab housing, welded into the yoke. No wrist, no elbow.
  big.box(0.94, 1.72, -0.02, 0.44, 0.5, 0.68, SHADE.Owner)
  big.box(0.94, 2.22, -0.02, 0.48, 0.08, 0.72, SHADE.MetalDark, SHADE.MetalMid)
  for (const cz of [-0.24, 0.0, 0.24]) {
    faceY(big, 0.94, 2.302, cz, 0.44, 0.05, SHADE.Metal)
  }
  big.box(0.96, 1.24, -0.02, 0.48, 0.5, 0.76, SHADE.Owner)
  big.box(0.96, 1.12, 0.6, 0.62, 0.62, 0.4, SHADE.MetalDark, SHADE.MetalMid)
  faceZ(big, 0.96, 1.28, 0.802, 0.34, 0.32, SHADE.Accent)

  // -- Port arm: the mortar bank --------------------------------------------
  // Six tubes standing open in a tray. The overhead camera looks straight into
  // them, which is the one thing a fixed camera does better than any other view,
  // and nothing else on the field uses it.
  big.box(-0.94, 1.72, -0.02, 0.44, 0.5, 0.68, SHADE.Owner)
  big.box(-0.96, 1.28, 0.02, 0.5, 0.44, 0.62, SHADE.Owner)
  big.box(-0.94, 2.22, -0.02, 0.54, 0.12, 0.72, SHADE.MetalDark, SHADE.MetalMid)
  for (const tz of [-0.2, 0.12]) {
    for (const tx of [-1.08, -0.94, -0.8]) {
      faceY(big, tx, 2.342, tz, 0.11, 0.11, SHADE.Accent)
    }
  }
  // Rungs up the port shoulder, because a machine this size is climbed on.
  for (const ry of [1.8, 1.92, 2.04]) {
    faceZ(big, -0.94, ry, 0.332, 0.26, 0.05, SHADE.Metal)
  }

  r.part(PART.Hull).merge(big, 0, 0, 0, 0.5)

  // -- Cupola ---------------------------------------------------------------
  // The only thing that turns. Armoured to the same rule as the rest: a plate
  // roof standing proud of a plate box, bolts along the join.
  const head = new MeshBuilder()
  head.box(0, 2.22, -0.1, 0.6, 0.22, 0.5, SHADE.Owner)
  head.box(0, 2.44, -0.1, 0.66, 0.06, 0.56, SHADE.MetalDark, SHADE.Metal)
  for (const bx of [-0.24, 0, 0.24]) {
    faceY(head, bx, 2.502, -0.1, 0.09, 0.42, SHADE.Metal)
  }
  faceZ(head, 0, 2.28, 0.152, 0.44, 0.09, SHADE.Accent)
  // A spotter's pintle gun off the starboard lip, the one asymmetric part.
  head.box(0.22, 2.5, 0.06, 0.08, 0.08, 0.44, SHADE.Metal)
  r.part(PART.Turret).merge(head, 0, 0, 0, 0.5)

  return r.build()
}

// ---------------------------------------------------------------------------

const RECIPES: Record<number, Recipe> = {
  [ID.GuardianTank]: guardianTank,
  [ID.HornetGunship]: hornetGunship,
  [ID.LandingField]: landingField,
  [ID.RegolithHarvester]: regolithHarvester,
  [ID.RifleSquad]: rifleSquad,
  [ID.Engineer]: engineer,
  [ID.ScoutBuggy]: scoutBuggy,
  [ID.Mcv]: mcv,
  [ID.CommandYard]: commandYard,
  [ID.SolarArray]: solarArray,
  [ID.RegolithRefinery]: regolithRefinery,
  [ID.HabitatBlock]: habitatBlock,
  [ID.Foundry]: foundry,
  [ID.CommsMast]: commsMast,
  [ID.GuardianTurret]: guardianTurret,
  [ID.OrbitalMirrorLance]: orbitalMirrorLance,

  [ID.PrismWorks]: prismWorks,
  [ID.PrismTank]: prismTank,
  [ID.SiegeBattery]: siegeBattery,
  [ID.Warden]: warden,

  [ID.ResonanceVault]: resonanceVault,
  [ID.ArcLancer]: arcLancer,
  [ID.VoidMortar]: voidMortar,
  [ID.Colossus]: colossus,
  [ID.Marksman]: marksman,
  [ID.Nullifier]: nullifier,
  [ID.Outrider]: () => ifv(false),
  [ID.Vector]: () => ifv(true),

  [ID.SiegeWalker]: siegeWalker,
  [ID.WraithSkimmer]: wraithSkimmer,
  [ID.LaunchSpire]: launchSpire,
  [ID.NaniteHarvester]: nanoHarvester,
  [ID.DroneSwarm]: droneSwarm,
  [ID.Infiltrator]: infiltrator,
  [ID.Skirmisher]: skirmisher,
  [ID.MobileNexusFrame]: mobileNexusFrame,
  [ID.CoreNexus]: coreNexus,
  [ID.FusionCore]: fusionCore,
  [ID.NaniteRefinery]: naniteRefinery,
  [ID.ReplicationVat]: replicationVat,
  [ID.AssemblyHive]: assemblyHive,
  [ID.SignalSpire]: signalSpire,
  [ID.ArcPylon]: arcPylon,
  [ID.LogicBomb]: logicBomb,
}

const cache = new Map<number, MeshRecipe>()
const legacyCache = new Map<number, MeshRecipe>()

/** The recipe for an entity kind, built once and cached. */
export function recipeFor(kind: number): MeshRecipe | undefined {
  const cached = cache.get(kind)
  if (cached) return cached
  const recipe = blenderRecipe(kind)
  if (recipe) cache.set(kind, recipe)
  return recipe
}

/** Kept for the local before/after studies, never used by the live renderer. */
export function legacyRecipeFor(kind: number): MeshRecipe | undefined {
  const cached = legacyCache.get(kind)
  if (cached) return cached
  const factory = RECIPES[kind]
  if (!factory) return undefined
  const recipe = factory()
  legacyCache.set(kind, recipe)
  return recipe
}

/** Entity kinds with a recipe. The roster test compares this to the registry. */
export function recipeKinds(): number[] {
  return Object.keys(RECIPES)
    .map(Number)
    .sort((a, b) => a - b)
}

export function disposeRecipes(): void {
  for (const recipe of cache.values()) {
    for (const part of recipe.parts) part.geometry.dispose()
  }
  cache.clear()
  for (const recipe of legacyCache.values()) for (const part of recipe.parts) part.geometry.dispose()
  legacyCache.clear()
}
